/*! @interlucent/pixel-stream v0.0.101 | (c) Interlucent */
/**
 * Pixel Stream Protocol — Types, Interfaces, Enums, and Codec
 *
 * Pure data definitions and stateless utilities for the binary message
 * protocol, coordinate translation, and file reception. Zero runtime
 * dependencies.
 */
class FileTemplate {
    constructor() {
        this.mimetype = '';
        this.extension = '';
        this.receiving = false;
        this.chunks = 0;
        this.data = [];
        this.valid = false;
        this.timestampStart = 0;
    }
}
// ToStreamer Message IDs (Client to UE)
var ToStreamerMessageType;
(function (ToStreamerMessageType) {
    // Control Messages (0-49)
    ToStreamerMessageType[ToStreamerMessageType["IFrameRequest"] = 0] = "IFrameRequest";
    ToStreamerMessageType[ToStreamerMessageType["RequestQualityControl"] = 1] = "RequestQualityControl";
    ToStreamerMessageType[ToStreamerMessageType["FpsRequest"] = 2] = "FpsRequest";
    ToStreamerMessageType[ToStreamerMessageType["AverageBitrateRequest"] = 3] = "AverageBitrateRequest";
    ToStreamerMessageType[ToStreamerMessageType["StartStreaming"] = 4] = "StartStreaming";
    ToStreamerMessageType[ToStreamerMessageType["StopStreaming"] = 5] = "StopStreaming";
    ToStreamerMessageType[ToStreamerMessageType["LatencyTest"] = 6] = "LatencyTest";
    ToStreamerMessageType[ToStreamerMessageType["RequestInitialSettings"] = 7] = "RequestInitialSettings";
    ToStreamerMessageType[ToStreamerMessageType["TestEcho"] = 8] = "TestEcho";
    ToStreamerMessageType[ToStreamerMessageType["DataChannelLatencyTest"] = 9] = "DataChannelLatencyTest";
    // Generic Input Messages (50-59)
    ToStreamerMessageType[ToStreamerMessageType["UIInteraction"] = 50] = "UIInteraction";
    ToStreamerMessageType[ToStreamerMessageType["Command"] = 51] = "Command";
    ToStreamerMessageType[ToStreamerMessageType["TextboxEntry"] = 52] = "TextboxEntry";
    // Keyboard Input Messages (60-69)
    ToStreamerMessageType[ToStreamerMessageType["KeyDown"] = 60] = "KeyDown";
    ToStreamerMessageType[ToStreamerMessageType["KeyUp"] = 61] = "KeyUp";
    ToStreamerMessageType[ToStreamerMessageType["KeyPress"] = 62] = "KeyPress";
    // Mouse Input Messages (70-79)
    ToStreamerMessageType[ToStreamerMessageType["MouseEnter"] = 70] = "MouseEnter";
    ToStreamerMessageType[ToStreamerMessageType["MouseLeave"] = 71] = "MouseLeave";
    ToStreamerMessageType[ToStreamerMessageType["MouseDown"] = 72] = "MouseDown";
    ToStreamerMessageType[ToStreamerMessageType["MouseUp"] = 73] = "MouseUp";
    ToStreamerMessageType[ToStreamerMessageType["MouseMove"] = 74] = "MouseMove";
    ToStreamerMessageType[ToStreamerMessageType["MouseWheel"] = 75] = "MouseWheel";
    ToStreamerMessageType[ToStreamerMessageType["MouseDouble"] = 76] = "MouseDouble";
    // Touch Input Messages (80-89)
    ToStreamerMessageType[ToStreamerMessageType["TouchStart"] = 80] = "TouchStart";
    ToStreamerMessageType[ToStreamerMessageType["TouchEnd"] = 81] = "TouchEnd";
    ToStreamerMessageType[ToStreamerMessageType["TouchMove"] = 82] = "TouchMove";
    // Gamepad Input Messages (90-99)
    ToStreamerMessageType[ToStreamerMessageType["GamepadButtonPressed"] = 90] = "GamepadButtonPressed";
    ToStreamerMessageType[ToStreamerMessageType["GamepadButtonReleased"] = 91] = "GamepadButtonReleased";
    ToStreamerMessageType[ToStreamerMessageType["GamepadAnalog"] = 92] = "GamepadAnalog";
    ToStreamerMessageType[ToStreamerMessageType["GamepadConnected"] = 93] = "GamepadConnected";
    ToStreamerMessageType[ToStreamerMessageType["GamepadDisconnected"] = 94] = "GamepadDisconnected";
    // XR Input Messages (100+)
    ToStreamerMessageType[ToStreamerMessageType["XRHMDTransform"] = 100] = "XRHMDTransform";
    ToStreamerMessageType[ToStreamerMessageType["XREyeViews"] = 101] = "XREyeViews";
    ToStreamerMessageType[ToStreamerMessageType["XRControllerTransform"] = 102] = "XRControllerTransform";
    ToStreamerMessageType[ToStreamerMessageType["XRSystem"] = 103] = "XRSystem";
    ToStreamerMessageType[ToStreamerMessageType["XRButtonPressed"] = 104] = "XRButtonPressed";
    ToStreamerMessageType[ToStreamerMessageType["XRButtonReleased"] = 105] = "XRButtonReleased";
    ToStreamerMessageType[ToStreamerMessageType["XRButtonTouched"] = 106] = "XRButtonTouched";
    ToStreamerMessageType[ToStreamerMessageType["XRButtonTouchReleased"] = 107] = "XRButtonTouchReleased";
    ToStreamerMessageType[ToStreamerMessageType["XRAnalog"] = 108] = "XRAnalog";
})(ToStreamerMessageType || (ToStreamerMessageType = {}));
// FromStreamer Message IDs (UE to Client)
var FromStreamerMessageType;
(function (FromStreamerMessageType) {
    FromStreamerMessageType[FromStreamerMessageType["QualityControlOwnership"] = 0] = "QualityControlOwnership";
    FromStreamerMessageType[FromStreamerMessageType["Response"] = 1] = "Response";
    FromStreamerMessageType[FromStreamerMessageType["Command"] = 2] = "Command";
    FromStreamerMessageType[FromStreamerMessageType["FreezeFrame"] = 3] = "FreezeFrame";
    FromStreamerMessageType[FromStreamerMessageType["UnfreezeFrame"] = 4] = "UnfreezeFrame";
    FromStreamerMessageType[FromStreamerMessageType["VideoEncoderAvgQP"] = 5] = "VideoEncoderAvgQP";
    FromStreamerMessageType[FromStreamerMessageType["LatencyTest"] = 6] = "LatencyTest";
    FromStreamerMessageType[FromStreamerMessageType["InitialSettings"] = 7] = "InitialSettings";
    FromStreamerMessageType[FromStreamerMessageType["FileExtension"] = 8] = "FileExtension";
    FromStreamerMessageType[FromStreamerMessageType["FileMimeType"] = 9] = "FileMimeType";
    FromStreamerMessageType[FromStreamerMessageType["FileContents"] = 10] = "FileContents";
    FromStreamerMessageType[FromStreamerMessageType["TestEcho"] = 11] = "TestEcho";
    FromStreamerMessageType[FromStreamerMessageType["InputControlOwnership"] = 12] = "InputControlOwnership";
    FromStreamerMessageType[FromStreamerMessageType["GamepadResponse"] = 13] = "GamepadResponse";
    FromStreamerMessageType[FromStreamerMessageType["DataChannelLatencyTest"] = 14] = "DataChannelLatencyTest";
    FromStreamerMessageType[FromStreamerMessageType["Protocol"] = 255] = "Protocol";
})(FromStreamerMessageType || (FromStreamerMessageType = {}));
// Message structure definitions
const ToStreamerMessageStructures = new Map([
    // Control
    ['IFrameRequest', { id: 0, structure: [] }],
    ['RequestQualityControl', { id: 1, structure: [] }],
    ['FpsRequest', { id: 2, structure: [] }],
    ['AverageBitrateRequest', { id: 3, structure: [] }],
    ['StartStreaming', { id: 4, structure: [] }],
    ['StopStreaming', { id: 5, structure: [] }],
    ['LatencyTest', { id: 6, structure: ['string'] }],
    ['RequestInitialSettings', { id: 7, structure: [] }],
    ['TestEcho', { id: 8, structure: [] }],
    ['DataChannelLatencyTest', { id: 9, structure: [] }],
    // Generic Input
    ['UIInteraction', { id: 50, structure: ['string'] }],
    ['Command', { id: 51, structure: ['string'] }],
    ['TextboxEntry', { id: 52, structure: ['string'] }],
    // Keyboard
    ['KeyDown', { id: 60, structure: ['uint8', 'uint8'] }],
    ['KeyUp', { id: 61, structure: ['uint8'] }],
    ['KeyPress', { id: 62, structure: ['uint16'] }],
    // Mouse
    ['MouseEnter', { id: 70, structure: [] }],
    ['MouseLeave', { id: 71, structure: [] }],
    ['MouseDown', { id: 72, structure: ['uint8', 'uint16', 'uint16'] }],
    ['MouseUp', { id: 73, structure: ['uint8', 'uint16', 'uint16'] }],
    ['MouseMove', { id: 74, structure: ['uint16', 'uint16', 'int16', 'int16'] }],
    ['MouseWheel', { id: 75, structure: ['int16', 'uint16', 'uint16'] }],
    ['MouseDouble', { id: 76, structure: ['uint8', 'uint16', 'uint16'] }],
    // Touch
    ['TouchStart', { id: 80, structure: ['uint8', 'uint16', 'uint16', 'uint8', 'uint8', 'uint8'] }],
    ['TouchEnd', { id: 81, structure: ['uint8', 'uint16', 'uint16', 'uint8', 'uint8', 'uint8'] }],
    ['TouchMove', { id: 82, structure: ['uint8', 'uint16', 'uint16', 'uint8', 'uint8', 'uint8'] }],
    // Gamepad
    ['GamepadButtonPressed', { id: 90, structure: ['uint8', 'uint8', 'uint8'] }],
    ['GamepadButtonReleased', { id: 91, structure: ['uint8', 'uint8', 'uint8'] }],
    ['GamepadAnalog', { id: 92, structure: ['uint8', 'uint8', 'double'] }],
    ['GamepadConnected', { id: 93, structure: [] }],
    ['GamepadDisconnected', { id: 94, structure: ['uint8'] }],
    // XR
    ['XRHMDTransform', { id: 100, structure: Array(16).fill('float') }],
    ['XREyeViews', { id: 101, structure: Array(80).fill('float') }], // 5 matrices * 16 values
    ['XRControllerTransform', { id: 102, structure: [...Array(16).fill('float'), 'uint8'] }],
    ['XRSystem', { id: 103, structure: ['uint8'] }],
    ['XRButtonPressed', { id: 104, structure: ['uint8', 'uint8', 'uint8', 'float'] }],
    ['XRButtonReleased', { id: 105, structure: ['uint8', 'uint8', 'uint8'] }],
    ['XRButtonTouched', { id: 106, structure: ['uint8', 'uint8', 'uint8'] }],
    ['XRButtonTouchReleased', { id: 107, structure: ['uint8', 'uint8', 'uint8'] }],
    ['XRAnalog', { id: 108, structure: ['uint8', 'uint8', 'float'] }]
]);
const FromStreamerMessageNames = new Map([
    [0, 'QualityControlOwnership'],
    [1, 'Response'],
    [2, 'Command'],
    [3, 'FreezeFrame'],
    [4, 'UnfreezeFrame'],
    [5, 'VideoEncoderAvgQP'],
    [6, 'LatencyTest'],
    [7, 'InitialSettings'],
    [8, 'FileExtension'],
    [9, 'FileMimeType'],
    [10, 'FileContents'],
    [11, 'TestEcho'],
    [12, 'InputControlOwnership'],
    [13, 'GamepadResponse'],
    [14, 'DataChannelLatencyTest'],
    [255, 'Protocol']
]);
// ===== BINARY MESSAGE ENCODER =====
/**
 * Encodes messages to binary format for sending over WebRTC data channel
 * All values are little-endian as per UE specification
 */
class MessageEncoder {
    /**
     * Encode a message with typed parameters
     * @param messageType The message type name (e.g. 'MouseDown')
     * @param data Array of values matching the message structure
     * @returns ArrayBuffer ready to send over data channel
     */
    static encode(messageType, data = []) {
        const descriptor = ToStreamerMessageStructures.get(messageType);
        if (!descriptor) {
            console.error(`Unknown message type: ${messageType}`);
            return new ArrayBuffer(0);
        }
        // Calculate total byte length
        let byteLength = 1; // Message ID
        data.forEach((element, idx) => {
            const type = descriptor.structure[idx];
            switch (type) {
                case 'uint8':
                    byteLength += 1;
                    break;
                case 'uint16':
                case 'int16':
                    byteLength += 2;
                    break;
                case 'float':
                    byteLength += 4;
                    break;
                case 'double':
                    byteLength += 8;
                    break;
                case 'string':
                    const str = element;
                    byteLength += 2; // length prefix
                    byteLength += 2 * str.length; // UTF-16 chars
                    break;
            }
        });
        // Create buffer and write data
        const buffer = new ArrayBuffer(byteLength);
        const view = new DataView(buffer);
        let offset = 0;
        // Write message ID
        view.setUint8(offset++, descriptor.id);
        // Write data elements
        data.forEach((element, idx) => {
            const type = descriptor.structure[idx];
            switch (type) {
                case 'uint8':
                    view.setUint8(offset, element);
                    offset += 1;
                    break;
                case 'uint16':
                    view.setUint16(offset, element, true); // little-endian
                    offset += 2;
                    break;
                case 'int16':
                    view.setInt16(offset, element, true); // little-endian
                    offset += 2;
                    break;
                case 'float':
                    view.setFloat32(offset, element, true); // little-endian
                    offset += 4;
                    break;
                case 'double':
                    view.setFloat64(offset, element, true); // little-endian
                    offset += 8;
                    break;
                case 'string':
                    const str = element;
                    view.setUint16(offset, str.length, true);
                    offset += 2;
                    for (let i = 0; i < str.length; i++) {
                        view.setUint16(offset, str.charCodeAt(i), true);
                        offset += 2;
                    }
                    break;
            }
        });
        return buffer;
    }
    /**
     * Decode a message received from UE
     * Handles both legacy (UE 4.27-5.4) and modern (UE 5.5+) message formats.
     * @param data ArrayBuffer from data channel message event
     * @returns Decoded message object with type and payload
     */
    static decode(data) {
        const bytes = new Uint8Array(data);
        if (bytes.length === 0) {
            return { type: 'Unknown', payload: null };
        }
        const messageId = bytes[0];
        const messageType = FromStreamerMessageNames.get(messageId);
        if (!messageType) {
            console.warn(`Unknown FromStreamer message ID: ${messageId}`);
            return { type: 'Unknown', payload: null };
        }
        const view = new DataView(data);
        switch (messageType) {
            case 'Response':
            case 'Command':
            case 'LatencyTest':
            case 'TestEcho':
                // String message - handle both legacy (length-prefixed) and modern (raw UTF-16) formats
                return { type: messageType, payload: this.decodeStringPayload(bytes) };
            case 'QualityControlOwnership':
            case 'InputControlOwnership':
                // Single boolean byte
                if (bytes.length < 2) {
                    return { type: messageType, payload: false };
                }
                return { type: messageType, payload: Boolean(bytes[1]) };
            case 'VideoEncoderAvgQP':
                // Legacy UE sends uint8 (2 bytes total), modern sends float32 (5 bytes total)
                if (bytes.length >= 5) {
                    // Modern format: float32
                    return { type: messageType, payload: view.getFloat32(1, true) };
                }
                else if (bytes.length >= 2) {
                    // Legacy format: uint8 QP value (typically 0-51 for H264)
                    return { type: messageType, payload: bytes[1] };
                }
                return { type: messageType, payload: null };
            case 'FileExtension':
            case 'FileMimeType':
                // String payload
                return { type: messageType, payload: this.decodeStringPayload(bytes) };
            case 'FileContents':
                // Binary data with size header
                if (bytes.length < 5) {
                    return { type: messageType, payload: new Uint8Array(0) };
                }
                const size = view.getInt32(1, true);
                const endOffset = Math.min(5 + size, bytes.length);
                return { type: messageType, payload: bytes.slice(5, endOffset) };
            case 'InitialSettings':
            case 'Protocol':
            case 'GamepadResponse':
            case 'DataChannelLatencyTest':
                // String payload (usually JSON)
                return { type: messageType, payload: this.decodeStringPayload(bytes) };
            case 'FreezeFrame':
                // Legacy: JPEG data follows the message ID
                // Modern: may have different format
                if (bytes.length > 1) {
                    return { type: messageType, payload: bytes.slice(1) };
                }
                return { type: messageType, payload: null };
            case 'UnfreezeFrame':
                // No payload typically
                return { type: messageType, payload: null };
            default:
                return { type: messageType, payload: bytes.slice(1) };
        }
    }
    /**
     * Decode string payload: [messageId, UTF-16 chars...]
     * Skip the message ID byte and UTF-16 decode the rest.
     * Matches Epic PixelStreamingInfrastructure and PureWeb platform-sdk-js.
     */
    static decodeStringPayload(bytes) {
        if (bytes.length < 2) {
            return null;
        }
        const text = new TextDecoder('utf-16').decode(bytes.slice(1));
        try {
            return JSON.parse(text);
        }
        catch {
            return text;
        }
    }
}
// ===== COORDINATE TRANSLATOR =====
/**
 * Stateless coordinate translator for pixel streaming input.
 *
 * Reads the video element's current dimensions on every call so it is
 * always in sync with the stream — no reconfigure() needed.
 *
 * Converts element-relative coordinates to values normalized within the
 * range of a short (0-65535 unsigned, +/-32767 signed), accounting for
 * aspect ratio differences between the CSS box and intrinsic video.
 */
class CoordTranslator {
    constructor(video) {
        this.video = video;
    }
    computeAspectRatio() {
        const pw = this.video.clientWidth;
        const ph = this.video.clientHeight;
        const vw = this.video.videoWidth;
        const vh = this.video.videoHeight;
        const playerAspectRatio = ph / pw;
        const videoAspectRatio = vh / vw;
        const playerIsLarger = playerAspectRatio > videoAspectRatio;
        const ratio = playerIsLarger
            ? playerAspectRatio / videoAspectRatio
            : videoAspectRatio / playerAspectRatio;
        return { pw, ph, playerIsLarger, ratio };
    }
    translateUnsigned(x, y) {
        const { pw, ph, playerIsLarger, ratio } = this.computeAspectRatio();
        const normalizedX = playerIsLarger
            ? x / pw
            : ratio * (x / pw - 0.5) + 0.5;
        const normalizedY = playerIsLarger
            ? ratio * (y / ph - 0.5) + 0.5
            : y / ph;
        if (normalizedX < 0.0 || normalizedX > 1.0 || normalizedY < 0.0 || normalizedY > 1.0) {
            return { inRange: false, x: 65535, y: 65535 };
        }
        return { inRange: true, x: Math.floor(normalizedX * 65536), y: Math.floor(normalizedY * 65536) };
    }
    translateSigned(x, y) {
        const { pw, ph, playerIsLarger, ratio } = this.computeAspectRatio();
        const normalizedX = playerIsLarger
            ? x / (0.5 * pw)
            : (ratio * x) / (0.5 * pw);
        const normalizedY = playerIsLarger
            ? (ratio * y) / (0.5 * ph)
            : y / (0.5 * ph);
        return { x: Math.floor(normalizedX * 32767), y: Math.floor(normalizedY * 32767) };
    }
}

const LOG_LEVEL_ORDER = {
    error: 0,
    warn: 1,
    info: 2,
    debug: 3,
};
const EVENT_NAME = 'interlucent:log';

function createLogger(target, source, module, getThreshold) {
    const log = (level, message, data, opts) => {
        if (LOG_LEVEL_ORDER[level] > LOG_LEVEL_ORDER[getThreshold()])
            return;
        const detail = {
            level,
            source,
            module,
            message,
            format: opts?.format ?? 'text',
            data,
            timestamp: performance.now(),
        };
        target.dispatchEvent(new CustomEvent(EVENT_NAME, { detail, bubbles: true }));
    };
    return {
        error: (msg, data, opts) => log('error', msg, data, opts),
        warn: (msg, data, opts) => log('warn', msg, data, opts),
        info: (msg, data, opts) => log('info', msg, data, opts),
        debug: (msg, data, opts) => log('debug', msg, data, opts),
    };
}

const STORAGE_KEY = 'interlucent:logLevel';
let cached = null;
function readFromStorage() {
    try {
        const v = localStorage.getItem(STORAGE_KEY);
        return v && v in LOG_LEVEL_ORDER ? v : null;
    }
    catch {
        return null;
    }
}
cached = readFromStorage();
/** Update log level -- persists to localStorage, takes effect immediately. */
function setLogLevel(level) {
    cached = level;
    try {
        localStorage.setItem(STORAGE_KEY, level);
    }
    catch { /* sandboxed */ }
}
/** Clear override -- reverts to per-consumer fallback. */
function clearLogLevel() {
    cached = null;
    try {
        localStorage.removeItem(STORAGE_KEY);
    }
    catch { /* sandboxed */ }
}
/**
 * Returns a ThresholdGetter that checks the global cached level first,
 * then falls back to the provided default.
 *
 * Fallback can be a static LogLevel or a dynamic ThresholdGetter
 * (e.g. for pixel-stream's attribute-driven logLevel).
 */
function globalThreshold(fallback = 'info') {
    return () => cached ?? (typeof fallback === 'function' ? fallback() : fallback);
}

/**
 * Global `interlucent` DevTools namespace.
 *
 * Creates (or reuses) `globalThis.interlucent` and exposes helpers
 * for other packages to register properties and versions.
 *
 * Usage:
 *   import { expose, registerVersion } from '@interlucent/global';
 *   expose('setLogLevel', setLogLevel);
 *   registerVersion('pixel-stream', '1.2.3');
 *
 * In DevTools:
 *   interlucent.setLogLevel('debug')
 *   interlucent.versions['pixel-stream']  // '1.2.3'
 */
var _a;
// Initialise the namespace once — this is the only side-effect.
const ns = ((_a = globalThis).interlucent ?? (_a.interlucent = {}));
// Lazily create the read-only `versions` sub-object on first registration.
function ensureVersions() {
    if (!ns.versions) {
        const versions = {};
        Object.defineProperty(ns, 'versions', {
            value: versions,
            writable: false,
            enumerable: true,
            configurable: false,
        });
    }
    return ns.versions;
}
/** Add a property to the global `interlucent` namespace. */
function expose(key, value) {
    ns[key] = value;
}
/**
 * Register a package version under `interlucent.versions[name]`.
 * Each entry is frozen (non-writable, non-configurable) once set.
 */
function registerVersion(name, version) {
    const versions = ensureVersions();
    if (!Object.getOwnPropertyDescriptor(versions, name)) {
        Object.defineProperty(versions, name, {
            value: version,
            writable: false,
            enumerable: true,
            configurable: false,
        });
    }
}

/**
 * Expose logging DevTools helpers on the global `interlucent` namespace.
 *
 * Safe to call multiple times — simply overwrites with the same references.
 *
 * After calling:
 *   interlucent.setLogLevel('debug')
 *   interlucent.clearLogLevel()
 */
function exposeDevTools() {
    expose('setLogLevel', setLogLevel);
    expose('clearLogLevel', clearLogLevel);
}

/**
 * Keyboard Input Handler — delegate for keyboard event processing.
 *
 * Captures keydown/keyup/keypress events on `document`, sends the
 * corresponding binary messages to the streamer, and optionally suppresses
 * browser‑default key actions (F1–F12, Tab).
 */
/**
 * Non-printable keys whose `keypress` event no longer fires in modern browsers.
 * Maps keyCode → charCode so we can send a synthetic KeyPress message.
 */
const NON_PRINTABLE_CHAR_CODES = {
    8: 8, // Backspace → BS
    46: 127, // Delete    → DEL
};
class KeyboardInputHandler {
    constructor(ctx, log) {
        this.ctx = ctx;
        this.log = log;
        this.keyDownHandler = null;
        this.keyUpHandler = null;
        this.keyPressHandler = null;
        this.activeKeys = new Set();
    }
    /** Set up keyboard event listeners on `document`. */
    setup() {
        this.keyDownHandler = (e) => {
            const keyCode = e.keyCode || e.which;
            this.activeKeys.add(keyCode);
            this.ctx.sendToStreamer('KeyDown', [keyCode, e.repeat ? 1 : 0]);
            // Modern browsers don't fire 'keypress' for non-printable keys.
            // UE text widgets rely on OnKeyChar (KeyPress message) for Backspace/Delete,
            // so send a synthetic KeyPress here.
            const charCode = NON_PRINTABLE_CHAR_CODES[keyCode];
            if (charCode !== undefined) {
                this.ctx.sendToStreamer('KeyPress', [charCode]);
            }
            // Suppress browser keys if enabled (F5, F11, Tab, etc.)
            if (this.ctx.config.suppressBrowserKeys && this.isBrowserKey(keyCode)) {
                e.preventDefault();
            }
        };
        this.keyUpHandler = (e) => {
            const keyCode = e.keyCode || e.which;
            this.activeKeys.delete(keyCode);
            this.ctx.sendToStreamer('KeyUp', [keyCode]);
            // Suppress browser keys if enabled
            if (this.ctx.config.suppressBrowserKeys && this.isBrowserKey(keyCode)) {
                e.preventDefault();
            }
        };
        this.keyPressHandler = (e) => {
            const keyCode = e.keyCode || e.which;
            this.ctx.sendToStreamer('KeyPress', [keyCode]);
        };
        document.addEventListener('keydown', this.keyDownHandler);
        document.addEventListener('keyup', this.keyUpHandler);
        document.addEventListener('keypress', this.keyPressHandler);
    }
    /** Remove keyboard event listeners. */
    teardown() {
        if (this.keyDownHandler) {
            document.removeEventListener('keydown', this.keyDownHandler);
        }
        if (this.keyUpHandler) {
            document.removeEventListener('keyup', this.keyUpHandler);
        }
        if (this.keyPressHandler) {
            document.removeEventListener('keypress', this.keyPressHandler);
        }
    }
    /**
     * Send KeyUp for every currently‑pressed key and clear the tracking set.
     * Called by the mouse handler when pointer lock is lost.
     */
    releaseAllKeys() {
        this.activeKeys.forEach(keyCode => {
            this.ctx.sendToStreamer('KeyUp', [keyCode]);
        });
        this.activeKeys.clear();
    }
    /**
     * Check if a key code is a browser key that should be suppressed during
     * streaming: F1–F12, Tab, Backspace (history nav), Delete.
     */
    isBrowserKey(keyCode) {
        // F1-F12: 112-123, Tab: 9, Backspace: 8, Delete: 46
        return (keyCode >= 112 && keyCode <= 123)
            || keyCode === 9
            || keyCode === 8
            || keyCode === 46;
    }
}

/**
 * RafFlusher — coalesces high-frequency input (mouse/touch move) to one flush
 * per animation frame.
 *
 * High-polling-rate mice/trackpads fire `mousemove` at 125–1000 Hz, well above
 * the render rate. Sending each one as its own reliable, ordered data-channel
 * message floods the streamer and builds head-of-line backlog. Instead, input
 * handlers stash the *latest* state and call `schedule()`; the flush callback
 * runs at most once per `requestAnimationFrame`, aligning send rate to the
 * display/render cadence.
 *
 * The loop is self-arming: it only runs while events keep arriving, so there is
 * no idle polling (unlike the gamepad's continuous rAF poll).
 */
class RafFlusher {
    /** @param flush invoked (at most once per frame) to send the pending state. */
    constructor(flush) {
        this.flush = flush;
        this.rafId = null;
        this.dirty = false;
        this.tick = () => {
            this.rafId = null;
            if (this.dirty) {
                this.dirty = false;
                this.flush();
            }
        };
    }
    /** Mark pending state dirty and arm a frame to flush it, if not already armed. */
    schedule() {
        this.dirty = true;
        if (this.rafId !== null)
            return;
        this.rafId = requestAnimationFrame(this.tick);
    }
    /**
     * Flush any pending state synchronously, now. Used before sending a discrete
     * event (mouse down/up, touch start/end) so a queued move can't be reordered
     * behind it on the wire.
     */
    flushNow() {
        if (this.rafId !== null) {
            cancelAnimationFrame(this.rafId);
            this.rafId = null;
        }
        if (this.dirty) {
            this.dirty = false;
            this.flush();
        }
    }
    /** Drop pending state and cancel the frame. Call from handler teardown. */
    cancel() {
        if (this.rafId !== null) {
            cancelAnimationFrame(this.rafId);
            this.rafId = null;
        }
        this.dirty = false;
    }
}

/**
 * Mouse Input Handler — delegate for mouse event processing.
 *
 * Supports two modes:
 *  - **Hovering** (default): mouse events on the video element, absolute coords
 *  - **Pointer-locked**: mouse events on `document`, virtual position with delta
 *    - Default: click to lock, Escape to unlock (FPS-style)
 *    - With `pointerLockRelease`: mousedown to lock, mouseup to unlock (orbit/drag-look style)
 */
class MouseInputHandler {
    constructor(ctx, log, opts) {
        this.ctx = ctx;
        this.log = log;
        this.mouseMoveHandler = null;
        this.virtualMouseX = 0;
        this.virtualMouseY = 0;
        this.normalizedCoord = { inRange: true, x: 0, y: 0 };
        /** Whether a mouse button is currently held — used by pointer-lock-release mode. */
        this.mouseButtonDown = false;
        /** Coalesces mouse-move sends to one per animation frame (see RafFlusher). */
        this.moveFlusher = new RafFlusher(() => this.flushMove());
        /**
         * Pending move state accumulated since the last flush. `coord` is the latest
         * absolute position; `rawDeltaX/Y` are summed raw pixel deltas across the
         * frame so relative-delta consumers (camera look) don't lose motion when
         * intermediate events are dropped.
         */
        this.pendingMove = null;
        this.onPointerUnlocked = opts?.onPointerUnlocked;
    }
    /** Returns the last computed normalized coordinate (for use by other handlers). */
    getNormalizedCoord() {
        return this.normalizedCoord;
    }
    /** Send the accumulated move for this frame as a single MouseMove. */
    flushMove() {
        if (!this.pendingMove)
            return;
        const { coord, rawDeltaX, rawDeltaY } = this.pendingMove;
        const delta = this.ctx.getCoordTranslator().translateSigned(rawDeltaX, rawDeltaY);
        this.ctx.sendToStreamer('MouseMove', [coord.x, coord.y, delta.x, delta.y]);
        this.pendingMove = null;
    }
    /** Set up mouse event listeners. Call from `connectedCallback`. */
    setup() {
        const rect = this.ctx.getVideoElement().getBoundingClientRect();
        this.virtualMouseX = rect.width / 2;
        this.virtualMouseY = rect.height / 2;
        this.setupMouseHandlers();
        if (this.ctx.config.pointerLocked) {
            this.setupPointerLock();
        }
    }
    /** Remove mouse move listener from document. Call from `disconnectedCallback`. */
    teardown() {
        this.moveFlusher.cancel();
        this.pendingMove = null;
        if (this.mouseMoveHandler) {
            document.removeEventListener('mousemove', this.mouseMoveHandler);
        }
        this.exitPointerLock();
    }
    // ===== POINTER LOCK =====
    setupPointerLock() {
        if (!this.ctx.config.pointerLocked)
            return;
        const videoEl = this.ctx.getVideoElement();
        videoEl.requestPointerLock = videoEl.requestPointerLock || videoEl.mozRequestPointerLock;
        document.exitPointerLock = document.exitPointerLock || document.mozExitPointerLock;
        if (this.ctx.config.pointerLockRelease) {
            // RELEASE MODE: lock immediately on mousedown, unlock on mouseup.
            // requestPointerLock() is called directly from the mousedown handler
            // to preserve user activation context (setTimeout loses it).
            // Since the lock is granted asynchronously, mouseup may fire before
            // the lock is acquired — we track button state so the
            // pointerlockchange handler can exit immediately if needed.
            this.ctx.getVideoElement().addEventListener('mousedown', () => {
                this.mouseButtonDown = true;
                if (videoEl.requestPointerLock) {
                    videoEl.requestPointerLock();
                }
            });
            document.addEventListener('mouseup', () => {
                this.mouseButtonDown = false;
                if (this.isPointerLocked()) {
                    document.exitPointerLock();
                }
            });
        }
        else {
            // DEFAULT: lock on click, unlock with Escape
            this.ctx.getVideoElement().addEventListener('click', () => {
                if (videoEl.requestPointerLock) {
                    videoEl.requestPointerLock();
                }
            });
        }
        // Listen for pointer lock state changes
        const handleLockChange = () => {
            const pointerLockElement = document.pointerLockElement || document.mozPointerLockElement;
            if (pointerLockElement === this.ctx.getVideoElement() || pointerLockElement === this.ctx.host) {
                this.log.debug('Pointer locked');
                this.ctx.host.dispatchEvent(new CustomEvent('pointerlocked'));
                // RELEASE MODE: if the button was released while the async
                // lock request was in flight, exit immediately.
                if (this.ctx.config.pointerLockRelease && !this.mouseButtonDown) {
                    document.exitPointerLock();
                }
            }
            else {
                this.log.debug('Pointer unlocked');
                // Release all active keys when pointer is unlocked
                this.onPointerUnlocked?.();
                this.ctx.host.dispatchEvent(new CustomEvent('pointerunlocked'));
            }
        };
        document.addEventListener('pointerlockchange', handleLockChange);
        document.addEventListener('mozpointerlockchange', handleLockChange);
    }
    exitPointerLock() {
        if (document.exitPointerLock && this.isPointerLocked()) {
            document.exitPointerLock();
        }
    }
    isPointerLocked() {
        const pointerLockElement = document.pointerLockElement || document.mozPointerLockElement;
        // In shadow DOM, document.pointerLockElement may return the shadow host
        // instead of the internal video element. Check both.
        return pointerLockElement === this.ctx.getVideoElement()
            || pointerLockElement === this.ctx.host;
    }
    // ===== MOUSE EVENT HANDLERS =====
    setupMouseHandlers() {
        const video = this.ctx.getVideoElement();
        const coordTranslator = this.ctx.getCoordTranslator();
        // Mouse move - either locked or hovering mode.
        //
        // The eager work (virtual-position wrap, latest absolute coord) runs
        // synchronously every event so position stays correct; only the *send*
        // is coalesced to one per animation frame (unless opted out). Raw pixel
        // deltas are accumulated across the frame so relative motion isn't lost.
        this.mouseMoveHandler = (e) => {
            let coord;
            if (this.ctx.config.pointerLocked && this.isPointerLocked()) {
                // LOCKED MODE: Track virtual position using movement delta
                const rect = video.getBoundingClientRect();
                this.virtualMouseX += e.movementX;
                this.virtualMouseY += e.movementY;
                // Wrap around edges
                while (this.virtualMouseX > rect.width)
                    this.virtualMouseX -= rect.width;
                while (this.virtualMouseY > rect.height)
                    this.virtualMouseY -= rect.height;
                while (this.virtualMouseX < 0)
                    this.virtualMouseX += rect.width;
                while (this.virtualMouseY < 0)
                    this.virtualMouseY += rect.height;
                this.normalizedCoord = coordTranslator.translateUnsigned(this.virtualMouseX, this.virtualMouseY);
                coord = this.normalizedCoord;
            }
            else {
                // HOVERING MODE: Use actual mouse position
                coord = coordTranslator.translateUnsigned(e.offsetX, e.offsetY);
            }
            e.preventDefault();
            if (this.ctx.config.noMouseCoalescing) {
                const delta = coordTranslator.translateSigned(e.movementX, e.movementY);
                this.ctx.sendToStreamer('MouseMove', [coord.x, coord.y, delta.x, delta.y]);
                return;
            }
            // Accumulate into the pending frame: latest coord wins, deltas sum.
            if (this.pendingMove) {
                this.pendingMove.coord = coord;
                this.pendingMove.rawDeltaX += e.movementX;
                this.pendingMove.rawDeltaY += e.movementY;
            }
            else {
                this.pendingMove = { coord, rawDeltaX: e.movementX, rawDeltaY: e.movementY };
            }
            this.moveFlusher.schedule();
        };
        // Register based on mode
        if (this.ctx.config.pointerLocked) {
            document.addEventListener('mousemove', this.mouseMoveHandler);
        }
        else {
            video.addEventListener('mousemove', this.mouseMoveHandler);
        }
        // Discrete events flush any pending move first so a coalesced move can't
        // arrive on the wire after the click/wheel that logically followed it.
        video.addEventListener('mousedown', (e) => {
            this.moveFlusher.flushNow();
            const coord = this.ctx.config.pointerLocked && this.isPointerLocked()
                ? this.normalizedCoord
                : coordTranslator.translateUnsigned(e.offsetX, e.offsetY);
            this.ctx.sendToStreamer('MouseDown', [e.button, coord.x, coord.y]);
            e.preventDefault();
        });
        video.addEventListener('mouseup', (e) => {
            this.moveFlusher.flushNow();
            const coord = this.ctx.config.pointerLocked && this.isPointerLocked()
                ? this.normalizedCoord
                : coordTranslator.translateUnsigned(e.offsetX, e.offsetY);
            this.ctx.sendToStreamer('MouseUp', [e.button, coord.x, coord.y]);
            e.preventDefault();
        });
        video.addEventListener('dblclick', (e) => {
            this.moveFlusher.flushNow();
            const coord = this.ctx.config.pointerLocked && this.isPointerLocked()
                ? this.normalizedCoord
                : coordTranslator.translateUnsigned(e.offsetX, e.offsetY);
            this.ctx.sendToStreamer('MouseDouble', [e.button, coord.x, coord.y]);
        });
        video.addEventListener('wheel', (e) => {
            this.moveFlusher.flushNow();
            const coord = this.ctx.config.pointerLocked && this.isPointerLocked()
                ? this.normalizedCoord
                : coordTranslator.translateUnsigned(e.offsetX, e.offsetY);
            const wheelDelta = e.wheelDelta !== undefined ? e.wheelDelta : -e.deltaY;
            this.ctx.sendToStreamer('MouseWheel', [wheelDelta, coord.x, coord.y]);
            e.preventDefault();
        });
        video.addEventListener('contextmenu', (e) => {
            e.preventDefault();
        });
        video.addEventListener('mouseenter', () => {
            this.ctx.sendToStreamer('MouseEnter', []);
        });
        video.addEventListener('mouseleave', () => {
            this.moveFlusher.flushNow();
            this.ctx.sendToStreamer('MouseLeave', []);
        });
    }
}

/**
 * Touch Input Handler — delegate for touch event processing.
 *
 * Supports two modes:
 *  - **Real touch**: multi-touch events sent as TouchStart/Move/End
 *  - **Fake mouse from touch**: first touch converted to Mouse events
 */
class TouchInputHandler {
    constructor(ctx, log) {
        this.ctx = ctx;
        this.log = log;
        this.fingerIds = new Map();
        this.availableFingers = [9, 8, 7, 6, 5, 4, 3, 2, 1, 0];
        this.fakeTouchFinger = null;
        /** Coalesces touch-move sends to one per animation frame (see RafFlusher). */
        this.moveFlusher = new RafFlusher(() => this.flushMoves());
        /** Latest pending TouchMove per active finger (real multi-touch path). */
        this.pendingRealMoves = new Map();
        /** Pending fake-mouse move from touch: latest coord + summed raw pixel delta. */
        this.pendingFakeMove = null;
    }
    /** Set up touch event listeners on the video element. */
    setup() {
        const video = this.ctx.getVideoElement();
        // touchstart/touchend flush any pending move first so a coalesced move
        // can't arrive on the wire after the start/end that logically followed it.
        video.addEventListener('touchstart', (e) => {
            this.moveFlusher.flushNow();
            if (!this.ctx.config.nativeTouch) {
                this.handleFakeTouchStart(e);
            }
            else {
                this.handleRealTouchStart(e);
            }
        });
        video.addEventListener('touchmove', (e) => {
            if (!this.ctx.config.nativeTouch) {
                this.handleFakeTouchMove(e);
            }
            else {
                this.handleRealTouchMove(e);
            }
        });
        video.addEventListener('touchend', (e) => {
            this.moveFlusher.flushNow();
            if (!this.ctx.config.nativeTouch) {
                this.handleFakeTouchEnd(e);
            }
            else {
                this.handleRealTouchEnd(e);
            }
        });
    }
    teardown() {
        // Touch listeners are on video element which is removed with the shadow DOM
        this.moveFlusher.cancel();
        this.pendingRealMoves.clear();
        this.pendingFakeMove = null;
    }
    /**
     * Flush coalesced moves for this frame. Real-touch emits one TouchMove per
     * active finger (latest position wins); fake-touch emits one MouseMove with
     * the latest position and the summed raw delta. The two paths are mutually
     * exclusive (gated by `nativeTouch`), so draining both is safe.
     */
    flushMoves() {
        if (this.pendingRealMoves.size > 0) {
            for (const [finger, { coord, force }] of this.pendingRealMoves) {
                this.ctx.sendToStreamer('TouchMove', [
                    1,
                    coord.x,
                    coord.y,
                    finger,
                    force,
                    coord.inRange ? 1 : 0
                ]);
            }
            this.pendingRealMoves.clear();
        }
        if (this.pendingFakeMove) {
            const { coord, rawDeltaX, rawDeltaY } = this.pendingFakeMove;
            const delta = this.ctx.getCoordTranslator().translateSigned(rawDeltaX, rawDeltaY);
            this.ctx.sendToStreamer('MouseMove', [coord.x, coord.y, delta.x, delta.y]);
            this.pendingFakeMove = null;
        }
    }
    // ===== REAL TOUCH =====
    handleRealTouchStart(e) {
        for (let i = 0; i < e.changedTouches.length; i++) {
            const touch = e.changedTouches[i];
            const finger = this.availableFingers.pop();
            if (finger === undefined) {
                this.log.warn('Exhausted touch identifiers');
                continue;
            }
            this.fingerIds.set(touch.identifier, finger);
            const coord = this.getTouchCoord(touch);
            this.ctx.sendToStreamer('TouchStart', [
                1, // numTouches
                coord.x,
                coord.y,
                finger,
                Math.floor(255 * (touch.force > 0 ? touch.force : 1)),
                coord.inRange ? 1 : 0
            ]);
        }
        e.preventDefault();
    }
    handleRealTouchMove(e) {
        const coalesce = !this.ctx.config.noTouchCoalescing;
        for (let i = 0; i < e.touches.length; i++) {
            const touch = e.touches[i];
            const finger = this.fingerIds.get(touch.identifier);
            if (finger === undefined)
                continue;
            const coord = this.getTouchCoord(touch);
            const force = Math.floor(255 * (touch.force > 0 ? touch.force : 1));
            if (coalesce) {
                // Latest position per finger wins; one TouchMove/finger per frame.
                this.pendingRealMoves.set(finger, { coord, force });
            }
            else {
                this.ctx.sendToStreamer('TouchMove', [
                    1,
                    coord.x,
                    coord.y,
                    finger,
                    force,
                    coord.inRange ? 1 : 0
                ]);
            }
        }
        if (coalesce)
            this.moveFlusher.schedule();
        e.preventDefault();
    }
    handleRealTouchEnd(e) {
        for (let i = 0; i < e.changedTouches.length; i++) {
            const touch = e.changedTouches[i];
            const finger = this.fingerIds.get(touch.identifier);
            if (finger === undefined)
                continue;
            const coord = this.getTouchCoord(touch);
            this.ctx.sendToStreamer('TouchEnd', [
                1,
                coord.x,
                coord.y,
                finger,
                Math.floor(255 * touch.force),
                coord.inRange ? 1 : 0
            ]);
            this.fingerIds.delete(touch.identifier);
            this.availableFingers.push(finger);
            this.availableFingers.sort((a, b) => b - a);
        }
        e.preventDefault();
    }
    // ===== FAKE MOUSE FROM TOUCH =====
    handleFakeTouchStart(e) {
        if (this.fakeTouchFinger === null) {
            const touch = e.changedTouches[0];
            const rect = this.ctx.getVideoElement().getBoundingClientRect();
            const pixelX = touch.clientX - rect.left;
            const pixelY = touch.clientY - rect.top;
            const coord = this.ctx.getCoordTranslator().translateUnsigned(pixelX, pixelY);
            this.fakeTouchFinger = {
                id: touch.identifier,
                x: pixelX,
                y: pixelY
            };
            this.ctx.sendToStreamer('MouseEnter', []);
            this.ctx.sendToStreamer('MouseDown', [0, coord.x, coord.y]);
        }
        e.preventDefault();
    }
    handleFakeTouchMove(e) {
        if (this.fakeTouchFinger === null)
            return;
        for (let i = 0; i < e.touches.length; i++) {
            const touch = e.touches[i];
            if (touch.identifier === this.fakeTouchFinger.id) {
                const rect = this.ctx.getVideoElement().getBoundingClientRect();
                const pixelX = touch.clientX - rect.left;
                const pixelY = touch.clientY - rect.top;
                const deltaPixelX = pixelX - this.fakeTouchFinger.x;
                const deltaPixelY = pixelY - this.fakeTouchFinger.y;
                const coordTranslator = this.ctx.getCoordTranslator();
                const coord = coordTranslator.translateUnsigned(pixelX, pixelY);
                // Advance the finger origin eagerly so accumulated deltas across a
                // coalesced frame sum to the true displacement since last flush.
                this.fakeTouchFinger.x = pixelX;
                this.fakeTouchFinger.y = pixelY;
                if (this.ctx.config.noTouchCoalescing) {
                    const delta = coordTranslator.translateSigned(deltaPixelX, deltaPixelY);
                    this.ctx.sendToStreamer('MouseMove', [coord.x, coord.y, delta.x, delta.y]);
                }
                else {
                    if (this.pendingFakeMove) {
                        this.pendingFakeMove.coord = coord;
                        this.pendingFakeMove.rawDeltaX += deltaPixelX;
                        this.pendingFakeMove.rawDeltaY += deltaPixelY;
                    }
                    else {
                        this.pendingFakeMove = { coord, rawDeltaX: deltaPixelX, rawDeltaY: deltaPixelY };
                    }
                    this.moveFlusher.schedule();
                }
                break;
            }
        }
        e.preventDefault();
    }
    handleFakeTouchEnd(e) {
        if (this.fakeTouchFinger === null)
            return;
        for (let i = 0; i < e.changedTouches.length; i++) {
            const touch = e.changedTouches[i];
            if (touch.identifier === this.fakeTouchFinger.id) {
                const coord = this.getTouchCoord(touch);
                this.ctx.sendToStreamer('MouseUp', [0, coord.x, coord.y]);
                this.ctx.sendToStreamer('MouseLeave', []);
                this.fakeTouchFinger = null;
                break;
            }
        }
        e.preventDefault();
    }
    getTouchCoord(touch) {
        const rect = this.ctx.getVideoElement().getBoundingClientRect();
        const x = touch.clientX - rect.left;
        const y = touch.clientY - rect.top;
        return this.ctx.getCoordTranslator().translateUnsigned(x, y);
    }
}

/**
 * Gamepad Input Handler — delegate for gamepad event processing.
 *
 * Manages connected gamepads, polls button/axis state each animation frame,
 * and sends GamepadConnected / Disconnected / ButtonPressed / ButtonReleased /
 * Analog messages to the streamer.
 */
class GamepadInputHandler {
    constructor(ctx, log) {
        this.ctx = ctx;
        this.log = log;
        this.controllers = [];
        this.animationFrame = null;
        // ===== EVENT HANDLERS =====
        this.handleConnected = (event) => {
            const gamepad = event.gamepad;
            this.controllers[gamepad.index] = {
                currentState: this.deepCopyGamepad(gamepad),
                prevState: this.deepCopyGamepad(gamepad),
                id: gamepad.index
            };
            this.ctx.sendToStreamer('GamepadConnected', [gamepad.index]);
            // Start update loop if not running
            if (this.animationFrame === null) {
                this.pollStatus();
            }
            this.ctx.host.dispatchEvent(new CustomEvent('gamepadconnected', { detail: gamepad }));
        };
        this.handleDisconnected = (event) => {
            const gamepad = event.gamepad;
            const controller = this.controllers[gamepad.index];
            if (controller) {
                this.ctx.sendToStreamer('GamepadDisconnected', [controller.id]);
                delete this.controllers[gamepad.index];
            }
            this.ctx.host.dispatchEvent(new CustomEvent('gamepaddisconnected', { detail: gamepad }));
        };
        // ===== POLLING LOOP =====
        this.pollStatus = () => {
            const gamepads = navigator.getGamepads?.();
            if (!gamepads)
                return;
            // Update current states
            for (let i = 0; i < gamepads.length; i++) {
                if (gamepads[i] && this.controllers[i]) {
                    this.controllers[i].currentState = gamepads[i];
                }
            }
            // Process each controller
            for (const controller of this.controllers) {
                if (!controller)
                    continue;
                const controllerId = controller.id;
                const current = controller.currentState;
                const prev = controller.prevState;
                // Process buttons
                for (let i = 0; i < current.buttons.length; i++) {
                    const currBtn = current.buttons[i];
                    const prevBtn = prev.buttons[i];
                    if (currBtn.pressed) {
                        // Left/Right triggers (indices 6, 7) send analog values
                        if (i === 6) {
                            this.ctx.sendToStreamer('GamepadAnalog', [controllerId, 5, currBtn.value]);
                        }
                        else if (i === 7) {
                            this.ctx.sendToStreamer('GamepadAnalog', [controllerId, 6, currBtn.value]);
                        }
                        else {
                            const isRepeat = prevBtn.pressed ? 1 : 0;
                            this.ctx.sendToStreamer('GamepadButtonPressed', [controllerId, i, isRepeat]);
                        }
                    }
                    else if (!currBtn.pressed && prevBtn.pressed) {
                        if (i === 6) {
                            this.ctx.sendToStreamer('GamepadAnalog', [controllerId, 5, 0]);
                        }
                        else if (i === 7) {
                            this.ctx.sendToStreamer('GamepadAnalog', [controllerId, 6, 0]);
                        }
                        else {
                            this.ctx.sendToStreamer('GamepadButtonReleased', [controllerId, i, 0]);
                        }
                    }
                }
                // Process axes (analog sticks)
                for (let i = 0; i < current.axes.length; i += 2) {
                    const x = parseFloat(current.axes[i].toFixed(4));
                    const y = -parseFloat(current.axes[i + 1].toFixed(4)); // Invert Y axis
                    this.ctx.sendToStreamer('GamepadAnalog', [controllerId, i + 1, x]);
                    this.ctx.sendToStreamer('GamepadAnalog', [controllerId, i + 2, y]);
                }
                controller.prevState = this.deepCopyGamepad(current);
            }
            if (this.controllers.filter(c => c !== undefined).length > 0) {
                this.animationFrame = requestAnimationFrame(this.pollStatus);
            }
        };
    }
    /** Wire up gamepad connect/disconnect listeners and detect already-connected pads. */
    setup() {
        window.addEventListener('gamepadconnected', this.handleConnected);
        window.addEventListener('gamepaddisconnected', this.handleDisconnected);
        // Check for already connected gamepads
        const gamepads = navigator.getGamepads?.();
        if (gamepads) {
            for (const gamepad of gamepads) {
                if (gamepad) {
                    this.handleConnected(new GamepadEvent('gamepadconnected', { gamepad }));
                }
            }
        }
    }
    /** Remove listeners, cancel animation frame, notify disconnection for all pads. */
    teardown() {
        window.removeEventListener('gamepadconnected', this.handleConnected);
        window.removeEventListener('gamepaddisconnected', this.handleDisconnected);
        if (this.animationFrame !== null) {
            cancelAnimationFrame(this.animationFrame);
            this.animationFrame = null;
        }
        for (const controller of this.controllers) {
            if (controller?.id !== undefined) {
                this.ctx.sendToStreamer('GamepadDisconnected', [controller.id]);
            }
        }
        this.controllers = [];
    }
    deepCopyGamepad(gamepad) {
        return deepCopyGamepad(gamepad);
    }
}
/** Deep-clone a Gamepad snapshot (shared with XR input). */
function deepCopyGamepad(gamepad) {
    return JSON.parse(JSON.stringify({
        buttons: gamepad.buttons.map(b => ({
            pressed: b.pressed,
            touched: b.touched,
            value: b.value
        })),
        axes: [...gamepad.axes],
        index: gamepad.index,
        id: gamepad.id,
        connected: gamepad.connected,
        timestamp: gamepad.timestamp,
        mapping: gamepad.mapping
    }));
}

/**
 * XR Input Handler — delegate for WebXR session and controller input.
 *
 * Manages the immersive-vr session lifecycle, HMD/controller/transient-pointer
 * transforms, and controller button/axis state. Does NOT include XR stream
 * detection heuristics (that lives in the host, alongside track management).
 */
class XRInputHandler {
    constructor(ctx, log) {
        this.ctx = ctx;
        this.log = log;
        this.session = null;
        this.refSpace = null;
        this.controllers = [];
        this.transientPointers = new Map();
        this.gl = null;
        this.videoTexture = null;
        this.leftView = null;
        this.rightView = null;
        // ===== FRAME LOOP =====
        this.onFrame = (time, frame) => {
            if (!this.session || !this.refSpace)
                return;
            const pose = frame.getViewerPose(this.refSpace);
            if (pose) {
                // Update views
                for (const view of pose.views) {
                    if (view.eye === 'left') {
                        this.leftView = view;
                    }
                    else if (view.eye === 'right') {
                        this.rightView = view;
                    }
                }
                // Send HMD transform and eye data
                this.sendTransform(pose);
                // Process XR input sources (controllers, hands, gaze)
                this.session.inputSources.forEach((source) => {
                    if (source.targetRayMode === 'transient-pointer') {
                        this.updateTransientPointer(source, frame);
                    }
                    else if (source.targetRayMode === 'tracked-pointer' && source.gamepad) {
                        this.updateController(source, frame);
                    }
                });
            }
            // Request next frame
            this.session.requestAnimationFrame((time, frame) => this.onFrame(time, frame));
        };
    }
    /** No-op setup — XR is triggered via `startSession()`. */
    setup() {
        this.log.info('WebXR ready - call startXRSession() to begin');
    }
    /** End any active XR session. */
    teardown() {
        this.endSession();
    }
    /** Whether an XR session is currently active. */
    isSessionActive() {
        return this.session !== null;
    }
    // ===== SESSION LIFECYCLE =====
    async startSession() {
        if (!navigator.xr) {
            this.log.error('WebXR not supported');
            this.ctx.host.dispatchEvent(new CustomEvent('xr-not-supported'));
            return;
        }
        try {
            const session = await navigator.xr.requestSession('immersive-vr', {
                optionalFeatures: []
            });
            await this.onSessionStarted(session);
        }
        catch (error) {
            this.log.error('Failed to start XR session', error);
            this.ctx.host.dispatchEvent(new CustomEvent('xr-session-failed', { detail: error }));
        }
    }
    endSession() {
        if (this.session) {
            this.session.end();
        }
    }
    static async isSupported() {
        if (!navigator.xr)
            return false;
        return navigator.xr.isSessionSupported('immersive-vr');
    }
    // ===== SESSION SETUP =====
    async onSessionStarted(session) {
        this.session = session;
        this.session.addEventListener('end', () => this.onSessionEnded());
        // Add select event listeners for transient-pointer (gaze/pinch)
        this.session.addEventListener('selectstart', (event) => this.onSelectStart(event));
        this.session.addEventListener('selectend', (event) => this.onSelectEnd(event));
        this.session.addEventListener('select', (event) => this.onSelect(event));
        // Initialize WebGL for XR
        const canvas = document.createElement('canvas');
        this.gl = canvas.getContext('webgl2', { xrCompatible: true });
        // Get reference space
        this.refSpace = await session.requestReferenceSpace('local');
        // Set up render layer
        session.updateRenderState({
            baseLayer: new XRWebGLLayer(session, this.gl)
        });
        // Start animation loop
        session.requestAnimationFrame((time, frame) => this.onFrame(time, frame));
        this.ctx.host.dispatchEvent(new CustomEvent('xr-session-started'));
    }
    // ===== HMD TRANSFORM =====
    sendTransform(pose) {
        const hmdMatrix = pose.transform.matrix;
        // Send HMD transform (4x4 matrix)
        this.ctx.sendToStreamer('XRHMDTransform', [
            hmdMatrix[0], hmdMatrix[4], hmdMatrix[8], hmdMatrix[12],
            hmdMatrix[1], hmdMatrix[5], hmdMatrix[9], hmdMatrix[13],
            hmdMatrix[2], hmdMatrix[6], hmdMatrix[10], hmdMatrix[14],
            hmdMatrix[3], hmdMatrix[7], hmdMatrix[11], hmdMatrix[15]
        ]);
        // Send full eye views if we have both eyes
        if (this.leftView && this.rightView) {
            const leftEyeTrans = this.leftView.transform.matrix;
            const leftEyeProj = this.leftView.projectionMatrix;
            const rightEyeTrans = this.rightView.transform.matrix;
            const rightEyeProj = this.rightView.projectionMatrix;
            this.ctx.sendToStreamer('XREyeViews', [
                // Left eye transform
                leftEyeTrans[0], leftEyeTrans[4], leftEyeTrans[8], leftEyeTrans[12],
                leftEyeTrans[1], leftEyeTrans[5], leftEyeTrans[9], leftEyeTrans[13],
                leftEyeTrans[2], leftEyeTrans[6], leftEyeTrans[10], leftEyeTrans[14],
                leftEyeTrans[3], leftEyeTrans[7], leftEyeTrans[11], leftEyeTrans[15],
                // Left eye projection
                leftEyeProj[0], leftEyeProj[4], leftEyeProj[8], leftEyeProj[12],
                leftEyeProj[1], leftEyeProj[5], leftEyeProj[9], leftEyeProj[13],
                leftEyeProj[2], leftEyeProj[6], leftEyeProj[10], leftEyeProj[14],
                leftEyeProj[3], leftEyeProj[7], leftEyeProj[11], leftEyeProj[15],
                // Right eye transform
                rightEyeTrans[0], rightEyeTrans[4], rightEyeTrans[8], rightEyeTrans[12],
                rightEyeTrans[1], rightEyeTrans[5], rightEyeTrans[9], rightEyeTrans[13],
                rightEyeTrans[2], rightEyeTrans[6], rightEyeTrans[10], rightEyeTrans[14],
                rightEyeTrans[3], rightEyeTrans[7], rightEyeTrans[11], rightEyeTrans[15],
                // Right eye projection
                rightEyeProj[0], rightEyeProj[4], rightEyeProj[8], rightEyeProj[12],
                rightEyeProj[1], rightEyeProj[5], rightEyeProj[9], rightEyeProj[13],
                rightEyeProj[2], rightEyeProj[6], rightEyeProj[10], rightEyeProj[14],
                rightEyeProj[3], rightEyeProj[7], rightEyeProj[11], rightEyeProj[15],
                // HMD transform
                hmdMatrix[0], hmdMatrix[4], hmdMatrix[8], hmdMatrix[12],
                hmdMatrix[1], hmdMatrix[5], hmdMatrix[9], hmdMatrix[13],
                hmdMatrix[2], hmdMatrix[6], hmdMatrix[10], hmdMatrix[14],
                hmdMatrix[3], hmdMatrix[7], hmdMatrix[11], hmdMatrix[15]
            ]);
        }
    }
    // ===== CONTROLLER INPUT =====
    updateController(source, frame) {
        if (!source.gamepad || !this.refSpace || !source.gripSpace)
            return;
        const pose = frame.getPose(source.gripSpace, this.refSpace);
        if (!pose)
            return;
        // Determine handedness: 0 = left, 1 = right, 2 = any
        let handedness = 2;
        if (source.handedness === 'left')
            handedness = 0;
        if (source.handedness === 'right')
            handedness = 1;
        // Send controller transform
        const matrix = pose.transform.matrix;
        this.ctx.sendToStreamer('XRControllerTransform', [
            matrix[0], matrix[4], matrix[8], matrix[12],
            matrix[1], matrix[5], matrix[9], matrix[13],
            matrix[2], matrix[6], matrix[10], matrix[14],
            matrix[3], matrix[7], matrix[11], matrix[15],
            handedness
        ]);
        // Detect XR system type
        let system = 0;
        if (source.profiles.includes('htc-vive')) {
            system = 1;
        }
        else if (source.profiles.includes('oculus-touch')) {
            system = 2;
        }
        this.ctx.sendToStreamer('XRSystem', [system]);
        // Track button/axis state
        if (!this.controllers[handedness]) {
            this.controllers[handedness] = {
                prevState: deepCopyGamepad(source.gamepad),
                currentState: source.gamepad
            };
        }
        const controller = this.controllers[handedness];
        const current = source.gamepad;
        const prev = controller.prevState;
        // Process buttons
        for (let i = 0; i < current.buttons.length; i++) {
            const currBtn = current.buttons[i];
            const prevBtn = prev.buttons[i];
            // Button pressed
            if (currBtn.pressed && !prevBtn.pressed) {
                this.ctx.sendToStreamer('XRButtonPressed', [handedness, i, 0, currBtn.value]);
            }
            else if (!currBtn.pressed && prevBtn.pressed) {
                this.ctx.sendToStreamer('XRButtonReleased', [handedness, i, 0]);
            }
            // Button touched
            if (currBtn.touched && !prevBtn.touched) {
                this.ctx.sendToStreamer('XRButtonTouched', [handedness, i, 0]);
            }
            else if (!currBtn.touched && prevBtn.touched) {
                this.ctx.sendToStreamer('XRButtonTouchReleased', [handedness, i, 0]);
            }
        }
        // Process axes
        for (let i = 0; i < current.axes.length; i++) {
            if (current.axes[i] !== prev.axes[i]) {
                this.ctx.sendToStreamer('XRAnalog', [handedness, i, current.axes[i]]);
            }
        }
        controller.prevState = deepCopyGamepad(current);
        controller.currentState = current;
    }
    // ===== TRANSIENT POINTER (GAZE/PINCH) =====
    updateTransientPointer(source, frame) {
        if (!source.targetRaySpace || !this.refSpace)
            return;
        const pose = frame.getPose(source.targetRaySpace, this.refSpace);
        if (!pose)
            return;
        // Get or create transient pointer state
        const pointerId = Array.from(this.transientPointers.keys()).find(id => this.transientPointers.get(id)?.source === source) ?? this.transientPointers.size;
        let pointer = this.transientPointers.get(pointerId);
        if (!pointer) {
            pointer = { source, isSelecting: false };
            this.transientPointers.set(pointerId, pointer);
        }
        // Determine handedness: 0 = left, 1 = right, 2 = none/any
        let handedness = 2;
        if (source.handedness === 'left')
            handedness = 0;
        if (source.handedness === 'right')
            handedness = 1;
        // Send ray transform (reuse XRControllerTransform message type)
        const matrix = pose.transform.matrix;
        this.ctx.sendToStreamer('XRControllerTransform', [
            matrix[0], matrix[4], matrix[8], matrix[12],
            matrix[1], matrix[5], matrix[9], matrix[13],
            matrix[2], matrix[6], matrix[10], matrix[14],
            matrix[3], matrix[7], matrix[11], matrix[15],
            handedness
        ]);
        // Update state (select events are handled separately via session listeners)
        pointer.source = source;
    }
    // ===== SELECT EVENTS =====
    onSelectStart(event) {
        const source = event.inputSource;
        if (source.targetRayMode !== 'transient-pointer')
            return;
        const pointerId = Array.from(this.transientPointers.keys()).find(id => this.transientPointers.get(id)?.source === source);
        if (pointerId === undefined)
            return;
        const pointer = this.transientPointers.get(pointerId);
        if (!pointer)
            return;
        pointer.isSelecting = true;
        let handedness = 2;
        if (source.handedness === 'left')
            handedness = 0;
        if (source.handedness === 'right')
            handedness = 1;
        this.ctx.sendToStreamer('XRButtonPressed', [handedness, 0, 0, 1.0]);
        this.log.debug('Transient pointer select start', { handedness, pointerId });
    }
    onSelectEnd(event) {
        const source = event.inputSource;
        if (source.targetRayMode !== 'transient-pointer')
            return;
        const pointerId = Array.from(this.transientPointers.keys()).find(id => this.transientPointers.get(id)?.source === source);
        if (pointerId === undefined)
            return;
        const pointer = this.transientPointers.get(pointerId);
        if (!pointer)
            return;
        pointer.isSelecting = false;
        let handedness = 2;
        if (source.handedness === 'left')
            handedness = 0;
        if (source.handedness === 'right')
            handedness = 1;
        this.ctx.sendToStreamer('XRButtonReleased', [handedness, 0, 0]);
        this.log.debug('Transient pointer select end', { handedness, pointerId });
    }
    onSelect(event) {
        const source = event.inputSource;
        if (source.targetRayMode !== 'transient-pointer')
            return;
        this.log.debug('Transient pointer select (click)', {
            handedness: source.handedness,
            targetRayMode: source.targetRayMode
        });
    }
    // ===== SESSION END =====
    onSessionEnded() {
        this.session = null;
        this.refSpace = null;
        this.controllers = [];
        this.transientPointers.clear();
        this.leftView = null;
        this.rightView = null;
        this.ctx.host.dispatchEvent(new CustomEvent('xr-session-ended'));
    }
}

/**
 * File Transfer Handler — delegate for chunked file reception from UE.
 *
 * Receives file extension, MIME type, and content chunks via the data channel,
 * assembles them into a Blob, and dispatches progress / completion events.
 */
class FileTransferHandler {
    constructor(ctx, log) {
        this.ctx = ctx;
        this.log = log;
        this.file = new FileTemplate();
        this.resolvers = [];
    }
    /** Process file extension message from UE. */
    handleExtension(data) {
        const view = new Uint8Array(data);
        // Reset file if we got a file message and we are not "receiving" it yet
        if (!this.file.receiving) {
            this.file.mimetype = '';
            this.file.extension = '';
            this.file.receiving = true;
            this.file.valid = false;
            this.file.chunks = 0;
            this.file.data = [];
            this.file.timestampStart = new Date().getTime();
            this.log.debug('Received first chunk of file');
        }
        const extensionAsString = new TextDecoder('utf-16').decode(view.slice(1));
        this.log.debug('File extension', extensionAsString);
        this.file.extension = extensionAsString;
    }
    /** Process file MIME type message from UE. */
    handleMimeType(data) {
        const view = new Uint8Array(data);
        // Reset file if we got a file message and we are not "receiving" it yet
        if (!this.file.receiving) {
            this.file.mimetype = '';
            this.file.extension = '';
            this.file.receiving = true;
            this.file.valid = false;
            this.file.chunks = 0;
            this.file.data = [];
            this.file.timestampStart = new Date().getTime();
            this.log.debug('Received first chunk of file');
        }
        const mimeAsString = new TextDecoder('utf-16').decode(view.slice(1));
        this.log.debug('File mime type', mimeAsString);
        this.file.mimetype = mimeAsString;
    }
    /** Process file contents chunk from UE. */
    handleContents(data) {
        const view = new Uint8Array(data);
        // If we haven't received the initial setup instructions, return
        if (!this.file.receiving)
            return;
        const typeSize = 1;
        const intSize = 4;
        const maxMessageSize = 16 * 1024;
        const headerSize = typeSize + intSize;
        const maxPayloadSize = maxMessageSize - headerSize;
        // Calculate total number of chunks from the total file size
        this.file.chunks = Math.ceil(new DataView(view.slice(typeSize, headerSize).buffer).getInt32(0, true) / maxPayloadSize);
        // Get the file part of the payload
        const fileBytes = view.slice(headerSize);
        // Append to existing data that holds the file
        this.file.data.push(fileBytes);
        this.log.debug(`Received file chunk: ${this.file.data.length}/${this.file.chunks}`);
        // Emit progress event
        const bytesReceived = this.file.data.length * maxPayloadSize;
        const totalBytes = this.file.chunks * maxPayloadSize;
        const percentage = Math.round((this.file.data.length / this.file.chunks) * 100);
        this.ctx.host.dispatchEvent(new CustomEvent('file-progress', {
            detail: {
                receivedChunks: this.file.data.length,
                totalChunks: this.file.chunks,
                percentage,
                bytesReceived,
                totalBytes,
                extension: this.file.extension,
                mimeType: this.file.mimetype
            }
        }));
        if (this.file.data.length === this.file.chunks) {
            this.file.receiving = false;
            this.file.valid = true;
            this.log.info('Received complete file');
            const transferDuration = new Date().getTime() - this.file.timestampStart;
            const transferBitrate = Math.round((this.file.chunks * maxMessageSize) / transferDuration);
            this.log.info(`Average transfer bitrate: ${transferBitrate}kb/s over ${transferDuration / 1000} seconds`);
            // Create file object
            const blob = new Blob(this.file.data, { type: this.file.mimetype });
            const receivedFile = {
                blob,
                extension: this.file.extension,
                mimeType: this.file.mimetype,
                chunks: this.file.chunks
            };
            // Emit event for listeners
            this.ctx.host.dispatchEvent(new CustomEvent('file-received', {
                detail: receivedFile
            }));
            // Resolve all waiting promises
            this.resolvers.forEach(resolve => resolve(receivedFile));
            this.resolvers = [];
            // Reset for next file (keep valid flag until getLastReceivedFile is called)
        }
        else if (this.file.data.length > this.file.chunks) {
            this.file.receiving = false;
            this.log.error(`Received bigger file than advertised: ${this.file.data.length}/${this.file.chunks}`);
        }
    }
    /**
     * Get the last received file (if valid), or null.
     */
    getLastReceivedFile() {
        if (this.file.valid) {
            return {
                blob: new Blob(this.file.data, { type: this.file.mimetype }),
                extension: this.file.extension,
                mimeType: this.file.mimetype,
                chunks: this.file.chunks
            };
        }
        return null;
    }
    /**
     * Returns a promise that resolves when the next complete file is received.
     */
    waitForFile() {
        return new Promise((resolve) => {
            this.resolvers.push(resolve);
        });
    }
}

/**
 * Metrics Manager — delegate for performance metrics and session statistics.
 *
 * Provides performance summary logging, WebRTC stats collection/display,
 * and timing milestone reporting to the session DO.
 */
class MetricsManager {
    constructor(ctx, log) {
        this.ctx = ctx;
        this.log = log;
        /** Timestamp when the session started (for duration calculation). */
        this.sessionStartTime = null;
        /** Encode-pipeline latency breakdown from LatencyTest messages. */
        this.latencyBreakdown = {
            current: null,
            browserReceiptTimeMs: null,
            history: [],
            deltas: null,
        };
        /** State for delta-based stat calculations (cross-browser). */
        this._prevBytesReceived = 0;
        this._prevFramesDecoded = 0;
        this._prevStatsTimestamp = 0;
    }
    /** Reset performance metrics for a new connection. */
    resetPerformanceMetrics() {
        const m = this.ctx.performanceMetrics;
        m.sessionStarted = null;
        m.tokenExchangeStart = null;
        m.tokenExchangeComplete = null;
        m.peerConnectionCreated = null;
        m.sessionWsConnectStart = null;
        m.sessionWsConnectComplete = null;
        m.sessionReady = null;
        m.jobRequested = null;
        m.subscribeStart = performance.now();
        m.offerReceived = null;
        m.answerSent = null;
        m.iceConnected = null;
        m.firstTrack = null;
        m.firstFrame = null;
        m.connectionComplete = null;
    }
    /** Get a copy of the current performance metrics. */
    getPerformanceMetrics() {
        return { ...this.ctx.performanceMetrics };
    }
    /** Log performance summary as a table. */
    logPerformanceSummary() {
        const m = this.ctx.performanceMetrics;
        // Use sessionStarted as baseline if available, otherwise subscribeStart (legacy)
        const baseline = m.sessionStarted || m.subscribeStart;
        if (!baseline)
            return;
        const fmt = (ts) => ts != null ? `${(ts - baseline).toFixed(2)}ms` : '';
        const dur = (a, b) => (a != null && b != null) ? `${(b - a).toFixed(2)}ms` : '';
        const rows = {};
        if (m.tokenExchangeStart)
            rows['Token exchange start'] = { Offset: fmt(m.tokenExchangeStart), Duration: '' };
        if (m.tokenExchangeComplete)
            rows['Token exchange complete'] = { Offset: fmt(m.tokenExchangeComplete), Duration: dur(m.tokenExchangeStart, m.tokenExchangeComplete) };
        if (m.peerConnectionCreated)
            rows['Peer connection created'] = { Offset: fmt(m.peerConnectionCreated), Duration: '' };
        if (m.sessionWsConnectStart)
            rows['WebSocket connect start'] = { Offset: fmt(m.sessionWsConnectStart), Duration: '' };
        if (m.sessionWsConnectComplete)
            rows['WebSocket connected'] = { Offset: fmt(m.sessionWsConnectComplete), Duration: dur(m.sessionWsConnectStart, m.sessionWsConnectComplete) };
        if (m.sessionReady)
            rows['Session ready'] = { Offset: fmt(m.sessionReady), Duration: '' };
        if (m.jobRequested)
            rows['Job requested'] = { Offset: fmt(m.jobRequested), Duration: '' };
        if (m.offerReceived)
            rows['Offer received'] = { Offset: fmt(m.offerReceived), Duration: '' };
        if (m.answerSent)
            rows['Answer sent'] = { Offset: fmt(m.answerSent), Duration: dur(m.offerReceived, m.answerSent) };
        if (m.iceConnected)
            rows['ICE connected'] = { Offset: fmt(m.iceConnected), Duration: '' };
        if (m.firstTrack)
            rows['First media track'] = { Offset: fmt(m.firstTrack), Duration: '' };
        if (m.firstFrame)
            rows['First video frame'] = { Offset: fmt(m.firstFrame), Duration: '' };
        if (m.connectionComplete)
            rows['Connection complete'] = { Offset: fmt(m.connectionComplete), Duration: '' };
        // Total
        const total = m.firstFrame ?? m.connectionComplete;
        if (total)
            rows['TOTAL'] = { Offset: fmt(total), Duration: dur(baseline, total) };
        this.log.info('Performance Summary', rows, { format: 'table' });
        // Dispatch event with metrics
        this.ctx.host.dispatchEvent(new CustomEvent('performance-metrics', {
            detail: { ...m }
        }));
    }
    // ===== LATENCY PIPELINE =====
    /** Process a LatencyTest JSON payload from UE and compute stage deltas. */
    handleLatencyTestResult(payload) {
        const timings = {
            TestStartTimeMs: payload.TestStartTimeMs ?? 0,
            ReceiptTimeMs: payload.ReceiptTimeMs ?? 0,
            PreCaptureTimeMs: payload.PreCaptureTimeMs ?? 0,
            PostCaptureTimeMs: payload.PostCaptureTimeMs ?? 0,
            PreEncodeTimeMs: payload.PreEncodeTimeMs ?? 0,
            PostEncodeTimeMs: payload.PostEncodeTimeMs ?? 0,
            TransmissionTimeMs: payload.TransmissionTimeMs ?? 0,
        };
        const browserReceiptTimeMs = Date.now();
        this.latencyBreakdown.current = timings;
        this.latencyBreakdown.browserReceiptTimeMs = browserReceiptTimeMs;
        // Ring buffer
        this.latencyBreakdown.history.push(timings);
        if (this.latencyBreakdown.history.length > MetricsManager.LATENCY_HISTORY_SIZE) {
            this.latencyBreakdown.history.shift();
        }
        // Compute stage deltas
        this.latencyBreakdown.deltas = {
            captureMs: timings.PostCaptureTimeMs - timings.PreCaptureTimeMs,
            encodeMs: timings.PostEncodeTimeMs - timings.PreEncodeTimeMs,
            transmitMs: timings.TransmissionTimeMs - timings.PostEncodeTimeMs,
            networkMs: browserReceiptTimeMs - timings.TransmissionTimeMs,
            totalMs: browserReceiptTimeMs - timings.TestStartTimeMs,
        };
        this.ctx.host.dispatchEvent(new CustomEvent('latency-test-result', {
            detail: { ...this.latencyBreakdown }
        }));
        this.log.debug('Latency breakdown', this.latencyBreakdown.deltas);
    }
    /** Get the latest latency breakdown. */
    getLatencyBreakdown() {
        return { ...this.latencyBreakdown };
    }
    // ===== SESSION STATISTICS =====
    /** Collect comprehensive session statistics from WebRTC. */
    async collectAndDisplaySessionStats() {
        const pc = this.ctx.getPeerConnection();
        if (!pc) {
            return null;
        }
        try {
            const stats = await pc.getStats();
            const sessionStats = this.parseWebRTCStats(stats);
            this.displaySessionStats(sessionStats);
            return sessionStats;
        }
        catch (e) {
            this.log.error('Failed to collect session stats', e);
            return null;
        }
    }
    parseWebRTCStats(statsReport) {
        const stats = {
            duration: this.sessionStartTime ? (Date.now() - this.sessionStartTime) / 1000 : 0,
            video: {
                codec: null,
                mimeType: null,
                bytesReceived: 0,
                packetsReceived: 0,
                packetsLost: 0,
                framesReceived: 0,
                framesDecoded: 0,
                framesDropped: 0,
                frameRate: 0,
                bitrate: 0,
                resolution: { width: 0, height: 0 },
                jitter: 0,
                pliCount: 0,
                nackCount: 0
            },
            audio: {
                codec: null,
                mimeType: null,
                bytesReceived: 0,
                packetsReceived: 0,
                packetsLost: 0,
                bitrate: 0,
                jitter: 0
            },
            connection: {
                iceLocalCandidateType: null,
                iceRemoteCandidateType: null,
                turnUsed: false,
                rtt: 0,
                availableOutgoingBitrate: 0,
                protocol: null,
                localAddress: null,
                remoteAddress: null
            }
        };
        statsReport.forEach((report) => {
            if (report.type === 'inbound-rtp') {
                const isVideo = report.mediaType === 'video' || report.kind === 'video';
                const isAudio = report.mediaType === 'audio' || report.kind === 'audio';
                if (isVideo) {
                    stats.video.bytesReceived = report.bytesReceived || 0;
                    stats.video.packetsReceived = report.packetsReceived || 0;
                    stats.video.packetsLost = report.packetsLost || 0;
                    stats.video.framesReceived = report.framesReceived || 0;
                    stats.video.framesDecoded = report.framesDecoded || 0;
                    stats.video.framesDropped = report.framesDropped || 0;
                    stats.video.jitter = report.jitter || 0;
                    stats.video.pliCount = report.pliCount || 0;
                    stats.video.nackCount = report.nackCount || 0;
                    // Frame rate: Chrome has framesPerSecond, Firefox has framerateMean
                    if (report.framesPerSecond) {
                        stats.video.frameRate = report.framesPerSecond;
                    }
                    else if (report.framerateMean) {
                        stats.video.frameRate = report.framerateMean;
                    }
                    // Resolution: some browsers put it on inbound-rtp directly
                    if (report.frameWidth && report.frameHeight) {
                        stats.video.resolution.width = report.frameWidth;
                        stats.video.resolution.height = report.frameHeight;
                    }
                    // Bitrate: Firefox/Safari have bitrateMean, Chrome needs delta calc
                    const now = Date.now();
                    const elapsed = (now - this._prevStatsTimestamp) / 1000;
                    if (report.bitrateMean) {
                        stats.video.bitrate = report.bitrateMean;
                    }
                    else if (elapsed > 0 && this._prevBytesReceived > 0) {
                        const bytesDelta = stats.video.bytesReceived - this._prevBytesReceived;
                        stats.video.bitrate = (bytesDelta * 8) / elapsed;
                    }
                    this._prevBytesReceived = stats.video.bytesReceived;
                    this._prevStatsTimestamp = now;
                }
                else if (isAudio) {
                    stats.audio.bytesReceived = report.bytesReceived || 0;
                    stats.audio.packetsReceived = report.packetsReceived || 0;
                    stats.audio.packetsLost = report.packetsLost || 0;
                    stats.audio.jitter = report.jitter || 0;
                    // Bitrate: Firefox/Safari have bitrateMean, Chrome needs duration-based calc
                    if (report.bitrateMean) {
                        stats.audio.bitrate = report.bitrateMean;
                    }
                    else if (stats.duration > 0) {
                        stats.audio.bitrate = (stats.audio.bytesReceived * 8) / stats.duration;
                    }
                }
            }
            if (report.type === 'codec') {
                const mimeType = report.mimeType || '';
                if (mimeType.includes('video')) {
                    stats.video.codec = report.mimeType;
                    stats.video.mimeType = report.mimeType;
                }
                else if (mimeType.includes('audio')) {
                    stats.audio.codec = report.mimeType;
                    stats.audio.mimeType = report.mimeType;
                }
            }
            if (report.type === 'track' && report.kind === 'video') {
                // Resolution fallback (Chrome/Safari put it on track report)
                if (!stats.video.resolution.width && report.frameWidth) {
                    stats.video.resolution.width = report.frameWidth || 0;
                    stats.video.resolution.height = report.frameHeight || 0;
                }
                // Frame rate fallback: delta-based from framesDecoded (Chrome/Safari)
                if (!stats.video.frameRate && report.framesDecoded && report.remoteSource) {
                    const now = Date.now();
                    const elapsed = (now - this._prevStatsTimestamp) / 1000;
                    if (elapsed > 0 && this._prevFramesDecoded > 0) {
                        stats.video.frameRate = (report.framesDecoded - this._prevFramesDecoded) / elapsed;
                    }
                    this._prevFramesDecoded = report.framesDecoded;
                }
            }
            if (report.type === 'candidate-pair' && report.state === 'succeeded') {
                stats.connection.rtt = report.currentRoundTripTime ? report.currentRoundTripTime * 1000 : 0;
                stats.connection.availableOutgoingBitrate = report.availableOutgoingBitrate || 0;
                const localCandidateId = report.localCandidateId;
                const remoteCandidateId = report.remoteCandidateId;
                statsReport.forEach((r) => {
                    if (r.id === localCandidateId && r.type === 'local-candidate') {
                        stats.connection.iceLocalCandidateType = r.candidateType;
                        stats.connection.protocol = r.protocol;
                        stats.connection.localAddress = `${r.address || r.ip}:${r.port}`;
                    }
                    if (r.id === remoteCandidateId && r.type === 'remote-candidate') {
                        stats.connection.iceRemoteCandidateType = r.candidateType;
                        stats.connection.remoteAddress = `${r.address || r.ip}:${r.port}`;
                        if (r.candidateType === 'relay') {
                            stats.connection.turnUsed = true;
                        }
                    }
                });
            }
        });
        return stats;
    }
    displaySessionStats(stats) {
        const pct = (lost, recv) => recv > 0 ? `${((lost / (recv + lost)) * 100).toFixed(2)}%` : '0%';
        const rows = {
            'Duration': { Value: `${stats.duration.toFixed(2)}s` },
            'Video Codec': { Value: stats.video.codec || 'N/A' },
            'Video Resolution': { Value: `${stats.video.resolution.width}x${stats.video.resolution.height}` },
            'Video Bytes': { Value: this.formatBytes(stats.video.bytesReceived) },
            'Video Packets': { Value: `${stats.video.packetsReceived.toLocaleString()} recv, ${stats.video.packetsLost.toLocaleString()} lost (${pct(stats.video.packetsLost, stats.video.packetsReceived)})` },
            'Video Frames': { Value: `${stats.video.framesReceived.toLocaleString()} recv, ${stats.video.framesDecoded.toLocaleString()} decoded, ${stats.video.framesDropped.toLocaleString()} dropped` },
            'Video Rate': { Value: `${stats.video.frameRate.toFixed(2)} fps, ${(stats.video.bitrate / 1000).toFixed(2)} kbps, jitter ${(stats.video.jitter * 1000).toFixed(2)}ms` },
            'Video Recovery': { Value: `${stats.video.pliCount.toLocaleString()} PLI, ${stats.video.nackCount.toLocaleString()} NACK` },
            'Audio Codec': { Value: stats.audio.codec || 'N/A' },
            'Audio Bytes': { Value: this.formatBytes(stats.audio.bytesReceived) },
            'Audio Packets': { Value: `${stats.audio.packetsReceived.toLocaleString()} recv, ${stats.audio.packetsLost.toLocaleString()} lost (${pct(stats.audio.packetsLost, stats.audio.packetsReceived)})` },
            'Audio Rate': { Value: `${(stats.audio.bitrate / 1000).toFixed(2)} kbps, jitter ${(stats.audio.jitter * 1000).toFixed(2)}ms` },
            'ICE Candidates': { Value: `local: ${stats.connection.iceLocalCandidateType || 'N/A'}, remote: ${stats.connection.iceRemoteCandidateType || 'N/A'}` },
            'TURN': { Value: stats.connection.turnUsed ? 'YES (relay)' : 'NO (direct)' },
            'Protocol': { Value: stats.connection.protocol || 'N/A' },
            'RTT': { Value: `${stats.connection.rtt.toFixed(2)}ms` },
            'Bandwidth': { Value: `${(stats.connection.availableOutgoingBitrate / 1000).toFixed(2)} kbps` },
        };
        if (stats.connection.localAddress)
            rows['Local Address'] = { Value: stats.connection.localAddress };
        if (stats.connection.remoteAddress)
            rows['Remote Address'] = { Value: stats.connection.remoteAddress };
        this.log.info('Session Statistics', rows, { format: 'table' });
        this.ctx.host.dispatchEvent(new CustomEvent('session-stats', {
            detail: stats
        }));
    }
    formatBytes(bytes) {
        if (bytes === 0)
            return '0 B';
        const k = 1024;
        const sizes = ['B', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return `${(bytes / Math.pow(k, i)).toFixed(2)} ${sizes[i]}`;
    }
    // ===== TIMING REPORTS =====
    /**
     * Report a timing milestone to the SessionDO for analytics.
     * Computes duration and optional breakdown from performanceMetrics.
     */
    reportTiming(milestone) {
        const m = this.ctx.performanceMetrics;
        const baseline = m.sessionStarted;
        if (!baseline)
            return;
        let durationMs;
        if (milestone === 'session-ready' && m.sessionReady) {
            durationMs = m.sessionReady - baseline;
        }
        else if (milestone === 'first-frame' && m.firstFrame) {
            durationMs = m.firstFrame - baseline;
        }
        else {
            return;
        }
        const breakdown = {};
        if (m.tokenExchangeStart && m.tokenExchangeComplete) {
            breakdown.token_exchange_ms = m.tokenExchangeComplete - m.tokenExchangeStart;
        }
        if (m.sessionWsConnectStart && m.sessionWsConnectComplete) {
            breakdown.ws_connect_ms = m.sessionWsConnectComplete - m.sessionWsConnectStart;
        }
        if (m.sessionReady) {
            breakdown.session_ready_ms = m.sessionReady - baseline;
        }
        if (m.jobRequested) {
            breakdown.job_requested_ms = m.jobRequested - baseline;
        }
        if (m.offerReceived) {
            breakdown.offer_received_ms = m.offerReceived - baseline;
        }
        if (m.answerSent) {
            breakdown.answer_sent_ms = m.answerSent - baseline;
        }
        if (m.iceConnected) {
            breakdown.ice_connected_ms = m.iceConnected - baseline;
        }
        if (m.firstTrack) {
            breakdown.first_track_ms = m.firstTrack - baseline;
        }
        if (m.firstFrame) {
            breakdown.first_frame_ms = m.firstFrame - baseline;
        }
        if (m.jobRequested && m.firstFrame) {
            breakdown.job_to_first_frame_ms = m.firstFrame - m.jobRequested;
        }
        this.ctx.sendSessionMessage({
            type: 'report-timing',
            milestone,
            duration_ms: Math.round(durationMs),
            breakdown,
        });
    }
}
MetricsManager.LATENCY_HISTORY_SIZE = 60;

/**
 * WebRTC Manager — Delegate for all WebRTC-related operations.
 *
 * Manages PeerConnection lifecycle, ICE handling, SDP processing, track handling,
 * data channel operations, microphone management, and WebRTC resilience.
 *
 * Follows the delegate pattern: receives a PixelStreamContext for host-mediated
 * operations and a Logger for structured logging.
 */
/** Preset parameters for stream-quality tiers. */
const STREAM_QUALITY_PRESETS = {
    'low-latency': { minBitrate: 5000, startBitrate: 10000, maxBitrate: 100000, conferenceFlag: true, audioPtime: 10, jitterBufferMs: 0 },
    'balanced': { minBitrate: 3000, startBitrate: 8000, maxBitrate: 100000, conferenceFlag: true, audioPtime: null, jitterBufferMs: 30 },
    'quality': { minBitrate: 1000, startBitrate: 5000, maxBitrate: 150000, conferenceFlag: false, audioPtime: null, jitterBufferMs: 50 },
};
class WebRTCManager {
    constructor(ctx, log) {
        this.ctx = ctx;
        this.log = log;
        // ===== OWNED STATE =====
        this.peerConnection = null;
        this.dataChannel = null;
        this.microphoneStream = null;
        this._useMic = false;
        this._forceMonoAudio = false;
        this.videoTracks = new Map();
        this.activeVideoTrackId = null;
        // Pipeline warming
        this._placeholderStream = null;
        // Codec verification (Android)
        this._codecVerified = false;
        // ICE candidate batching
        this.iceCandidateBatch = [];
        this.iceBatchTimer = null;
        // ICE error tracking
        this.turnErrorCount = 0;
        this.stunErrorCount = 0;
        // WebRTC resilience state
        this._pcGeneration = 0;
        this._disconnectGraceTimer = null;
        this._disconnectPollInterval = null;
        this._recoveryInFlight = false;
        this._lastRecoveryStartTime = 0;
        this._recoveryTimestamps = [];
        this._recoveryWatchdogTimer = null;
        this._rtpBytesAtDisconnect = 0;
        this._lastKnownTurnUsed = false;
        this._lastIceConnectionState = null;
        // ICE restart state
        this._iceRestartAttempts = 0;
        this._iceRestartTimer = null;
        this._iceRestartInProgress = false;
        // TURN health check state
        this._turnHealthy = null;
        this._lastTurnHealthCheck = 0;
        // Encoded Transform — passthrough worker that exposes sendKeyFrameRequest()
        this._keyframeWorker = null;
        // Create audio element for separate audio streams
        this.audioElement = document.createElement('audio');
        this.audioElement.autoplay = true;
        this.audioElement.controls = false;
        this.audioElement.muted = this.ctx.config.mute;
        this.audioElement.style.display = 'none';
    }
    // ===== GETTERS FOR HOST/CONTEXT =====
    getPeerConnection() {
        return this.peerConnection;
    }
    getDataChannel() {
        return this.dataChannel;
    }
    getAudioElement() {
        return this.audioElement;
    }
    /**
     * Unlock audio playback on iOS Safari.
     * Must be called from a user gesture (click/tap/keypress).
     * On iOS, audio.load() + audio.play() within a gesture handler
     * "unlocks" the element so subsequent srcObject assignments produce sound.
     * No-op on platforms that don't restrict autoplay.
     */
    unlockAudio() {
        const audio = this.audioElement;
        // Only needed if we haven't received an audio stream yet,
        // or if a previous autoplay attempt was blocked.
        if (!audio.srcObject) {
            audio.load();
        }
        audio.play().catch(() => {
            // Expected on non-iOS or when no src — safe to ignore
        });
    }
    getVideoTracks() {
        return this.videoTracks;
    }
    getActiveVideoTrackId() {
        return this.activeVideoTrackId;
    }
    /** Current peer connection generation (stale signaling guard). */
    getPcGeneration() {
        return this._pcGeneration;
    }
    /**
     * Reset all resilience state. Called from terminateSession when tearing
     * down a session cleanly.
     */
    resetResilienceState() {
        this._cancelDisconnectGrace();
        this._cancelIceRestart();
        this._cancelRecoveryWatchdog();
        this._recoveryInFlight = false;
        this._recoveryTimestamps = [];
        this._lastRecoveryStartTime = 0;
        this._pcGeneration = 0;
        this._lastIceConnectionState = null;
        this._rtpBytesAtDisconnect = 0;
        this._lastKnownTurnUsed = false;
        // ICE restart state
        this._iceRestartAttempts = 0;
        // TURN health check state
        this._turnHealthy = null;
        this._lastTurnHealthCheck = 0;
    }
    /**
     * Called when the streaming worker has left the session.
     *
     * The gateway sends `participant-left` immediately when the worker's WebSocket
     * drops; use this signal to tear down the PeerConnection right away instead of
     * waiting 15-30 seconds for ICE failure detection.  The session WebSocket stays
     * open so the browser-agent can receive a new worker when one joins.
     */
    onWorkerLeft() {
        this.log.info('Worker left — immediately closing PeerConnection');
        // If the ICE recovery ladder was mid-flight, signaling has vetoed
        // it: no point spending further 8s/16s restart budget when the
        // worker is demonstrably gone. Surface a typed session-error so
        // the overlay can transition to a terminal state (amber ring)
        // rather than the user watching a frozen last frame while ICE
        // cycles through a doomed restart.
        const wasRecovering = this._iceRestartInProgress ||
            this._iceRestartAttempts > 0 ||
            this._disconnectGraceTimer !== null ||
            this._recoveryInFlight;
        if (wasRecovering) {
            this.ctx.host.dispatchEvent(new CustomEvent('session-error', {
                detail: {
                    error: 'Streamer left during media recovery',
                    code: 'worker_left',
                    phase: 'webrtc-recovery',
                },
            }));
            this.ctx.setStatusFailed('worker_left');
        }
        // Cancel all pending recovery / grace state
        this._cancelDisconnectGrace();
        this._cancelIceRestart();
        this._cancelRecoveryWatchdog();
        this._iceRestartInProgress = false;
        this._iceRestartAttempts = 0;
        this._recoveryInFlight = false;
        // Bump generation to invalidate any in-flight ICE / signaling callbacks
        this._pcGeneration++;
        // Nullify event handlers before closing to prevent stale late-firing callbacks
        if (this.peerConnection) {
            this.peerConnection.onconnectionstatechange = null;
            this.peerConnection.oniceconnectionstatechange = null;
            this.peerConnection.onicecandidate = null;
            this.peerConnection.ontrack = null;
            this.peerConnection.onicecandidateerror = null;
            this.peerConnection.ondatachannel = null;
            this.peerConnection.onicegatheringstatechange = null;
        }
        // Close peer connection (transitions stream state to 'idle')
        this.closePeerConnection();
        // Clear video track state
        this.videoTracks.forEach(({ track }) => track.stop());
        this.videoTracks.clear();
        this.activeVideoTrackId = null;
        // Clear video element
        const videoElement = this.ctx.getVideoElement();
        if (videoElement.srcObject) {
            const oldStream = videoElement.srcObject;
            oldStream.getTracks().forEach(t => t.stop());
            videoElement.srcObject = null;
        }
        if (this._placeholderStream) {
            this._placeholderStream.getTracks().forEach(t => t.stop());
            this._placeholderStream = null;
        }
    }
    // ===== WEBRTC RESILIENCE METHODS =====
    /**
     * Snapshot current RTP bytes received.
     * Also opportunistically detects TURN usage from nominated candidate pair.
     */
    async _snapshotRtpBytes() {
        if (!this.peerConnection)
            return 0;
        try {
            const stats = await this.peerConnection.getStats();
            let totalBytes = 0;
            stats.forEach((report) => {
                if (report.type === 'inbound-rtp') {
                    totalBytes += report.bytesReceived || 0;
                }
                // Opportunistically detect TURN usage from nominated candidate pair
                if (report.type === 'candidate-pair' && report.state === 'succeeded' && report.nominated) {
                    const remoteCandidateId = report.remoteCandidateId;
                    stats.forEach((r) => {
                        if (r.id === remoteCandidateId && r.type === 'remote-candidate') {
                            this._lastKnownTurnUsed = r.candidateType === 'relay';
                        }
                    });
                }
            });
            return totalBytes;
        }
        catch {
            return 0;
        }
    }
    /**
     * Check if RTP bytes are still flowing since the disconnect snapshot.
     */
    async _isRtpFlowing() {
        const currentBytes = await this._snapshotRtpBytes();
        return currentBytes > this._rtpBytesAtDisconnect;
    }
    /**
     * Inspect the nominated candidate pair to determine whether TURN (relay)
     * is being used. Dispatches a `transport-selected` event with the full
     * candidate details and updates `_lastKnownTurnUsed`.
     *
     * Called once when ICE reaches `connected` or `completed`.
     */
    async _detectSelectedTransport(gen) {
        const pc = this.peerConnection;
        if (!pc)
            return;
        try {
            const stats = await pc.getStats();
            // Stale guard: generation may have changed during the async getStats
            if (this._pcGeneration !== gen)
                return;
            let localCandidate = null;
            let remoteCandidate = null;
            let pair = null;
            stats.forEach((report) => {
                if (report.type === 'candidate-pair' && report.state === 'succeeded' && report.nominated) {
                    pair = report;
                }
            });
            if (!pair)
                return;
            stats.forEach((report) => {
                if (report.id === pair.localCandidateId && report.type === 'local-candidate') {
                    localCandidate = report;
                }
                if (report.id === pair.remoteCandidateId && report.type === 'remote-candidate') {
                    remoteCandidate = report;
                }
            });
            const turnUsed = remoteCandidate?.candidateType === 'relay' || localCandidate?.candidateType === 'relay';
            this._lastKnownTurnUsed = turnUsed;
            const detail = {
                turnUsed,
                protocol: localCandidate?.protocol || null,
                localCandidateType: localCandidate?.candidateType || null,
                remoteCandidateType: remoteCandidate?.candidateType || null,
                localAddress: localCandidate ? `${localCandidate.address || localCandidate.ip}:${localCandidate.port}` : null,
                remoteAddress: remoteCandidate ? `${remoteCandidate.address || remoteCandidate.ip}:${remoteCandidate.port}` : null,
                rtt: pair.currentRoundTripTime ? pair.currentRoundTripTime * 1000 : null,
                generation: gen,
            };
            this.log.info(`Transport selected: ${turnUsed ? 'TURN (relay)' : 'direct'}, ` +
                `local=${detail.localCandidateType}/${detail.protocol}, remote=${detail.remoteCandidateType}`);
            this.ctx.host.dispatchEvent(new CustomEvent('transport-selected', { detail }));
        }
        catch (e) {
            this.log.debug('Failed to detect selected transport', e);
        }
    }
    /**
     * Check whether media recovery is permitted right now.
     * Enforces: session WS alive, reconnect mode enabled, single-flight,
     * minimum interval, sliding window limit, ICE servers available.
     */
    _canStartRecovery() {
        const sessionWs = this.ctx.getSessionWs();
        const hasSessionWs = sessionWs && sessionWs.readyState === WebSocket.OPEN;
        if (!hasSessionWs) {
            return { allowed: false, reason: 'session-ws-not-open' };
        }
        const reconnectMode = this.ctx.config.reconnectMode;
        const recoveryEnabled = reconnectMode === 'recover' || reconnectMode === 'always';
        if (!recoveryEnabled) {
            return { allowed: false, reason: `reconnect-mode-${reconnectMode}` };
        }
        if (this._recoveryInFlight) {
            return { allowed: false, reason: 'recovery-already-in-flight' };
        }
        const now = Date.now();
        const elapsed = now - this._lastRecoveryStartTime;
        if (this._lastRecoveryStartTime > 0 && elapsed < WebRTCManager.RECOVERY_MIN_INTERVAL_MS) {
            return { allowed: false, reason: `min-interval-not-elapsed (${elapsed}ms < ${WebRTCManager.RECOVERY_MIN_INTERVAL_MS}ms)` };
        }
        // Prune old timestamps outside the sliding window
        this._recoveryTimestamps = this._recoveryTimestamps.filter(t => now - t < WebRTCManager.RECOVERY_WINDOW_MS);
        if (this._recoveryTimestamps.length >= WebRTCManager.MAX_RECOVERY_IN_WINDOW) {
            return { allowed: false, reason: `max-recoveries-in-window (${this._recoveryTimestamps.length}/${WebRTCManager.MAX_RECOVERY_IN_WINDOW} in ${WebRTCManager.RECOVERY_WINDOW_MS}ms)` };
        }
        const iceServers = this.ctx.getIceServers();
        if (!iceServers || iceServers.length === 0) {
            return { allowed: false, reason: 'no-ice-servers' };
        }
        return { allowed: true };
    }
    /**
     * Cancel any pending disconnect grace timer and RTP polling.
     */
    _cancelDisconnectGrace() {
        if (this._disconnectGraceTimer) {
            clearTimeout(this._disconnectGraceTimer);
            this._disconnectGraceTimer = null;
        }
        if (this._disconnectPollInterval) {
            clearInterval(this._disconnectPollInterval);
            this._disconnectPollInterval = null;
        }
    }
    /**
     * Start the hard recovery watchdog. Fired when `_executeMediaRecovery`
     * transitions stream state to 'recovering'. If the SDK does not get back
     * to 'streaming' for this generation within RECOVERY_WATCHDOG_MS, surface
     * a terminal failure. Covers the `have-local-offer` stall that occurs
     * when the new PeerConnection sends an offer to a worker that has been
     * lingered out — the answer never arrives, `iceConnectionState` never
     * transitions to `failed` (ICE doesn't start checking without a remote
     * description), and no other failsafe would otherwise trip.
     */
    _startRecoveryWatchdog(gen) {
        this._cancelRecoveryWatchdog();
        this._recoveryWatchdogTimer = setTimeout(() => {
            this._recoveryWatchdogTimer = null;
            // Stale guard: another recovery superseded this one.
            if (this._pcGeneration !== gen)
                return;
            this.log.error(`Recovery watchdog expired after ${WebRTCManager.RECOVERY_WATCHDOG_MS}ms (gen=${gen})`);
            this.ctx.setStreamState('failed', {
                reason: 'recovery-watchdog-expired',
                generation: gen,
                turnUsed: this._lastKnownTurnUsed,
                watchdogMs: WebRTCManager.RECOVERY_WATCHDOG_MS,
            });
            const exhaustedEvent = new CustomEvent('media-recovery-exhausted', {
                cancelable: true,
                detail: {
                    reason: 'recovery-watchdog-expired',
                    generation: gen,
                    turnUsed: this._lastKnownTurnUsed,
                    watchdogMs: WebRTCManager.RECOVERY_WATCHDOG_MS,
                },
            });
            this.ctx.host.dispatchEvent(exhaustedEvent);
            // Match the sliding-window exhaustion default: leave the session
            // so worker-agents aren't held hostage. Consumers can preventDefault
            // to manage the failure themselves.
            this._recoveryInFlight = false;
            if (!exhaustedEvent.defaultPrevented) {
                this.log.warn('Recovery watchdog expired — leaving session');
                this.ctx.leaveSession('recovery-watchdog-expired');
            }
        }, WebRTCManager.RECOVERY_WATCHDOG_MS);
    }
    /**
     * Cancel any pending recovery watchdog. Idempotent.
     */
    _cancelRecoveryWatchdog() {
        if (this._recoveryWatchdogTimer) {
            clearTimeout(this._recoveryWatchdogTimer);
            this._recoveryWatchdogTimer = null;
        }
    }
    /**
     * Cancel any pending ICE restart timer.
     */
    _cancelIceRestart() {
        if (this._iceRestartTimer) {
            clearTimeout(this._iceRestartTimer);
            this._iceRestartTimer = null;
        }
        this._iceRestartInProgress = false;
    }
    /**
     * Attempt ICE restart as a lightweight recovery mechanism.
     * Creates a new offer with iceRestart: true to re-establish connectivity
     * without tearing down the entire PeerConnection.
     *
     * Returns true if ICE restart was initiated, false if not possible.
     */
    async _attemptIceRestart(reason) {
        const pc = this.peerConnection;
        if (!pc || !pc.remoteDescription) {
            this.log.warn('Cannot ICE restart: no peer connection or remote description');
            return false;
        }
        // ICE restart only re-negotiates ICE on the existing PC — it does NOT
        // recreate the SCTP data channel. If the DC has already closed (e.g.
        // SCTP timed out during a brief network drop), ICE restart can
        // "succeed" at the transport layer and bring the SDK back to
        // `streaming`, but the channel will stay closed and the user lands in
        // a half-recovered state: video plays, sendUIInteraction silently
        // no-ops, no `data-channel-open` event fires. Escalate directly to
        // full recovery so subscribeToStreamer can build a fresh DC.
        // (Reported by Attensi 2026-05-13; reproduced in 07-resilience-
        // failsafes ice-restart-dc-loss scenario.)
        //
        // Only escalate when the DC is *closing* or *closed* — those mean a
        // previously-open channel went dead and cannot recover via ICE
        // restart. Do NOT escalate on `connecting` — that's a healthy state
        // during initial negotiation. The original Attensi initial-connect
        // ICE flap (ice → disconnected → connection-state failed within
        // ~10ms) lands here with the DC still in `connecting`; the legacy
        // path of "try ICE restart on initial flap" is the right call.
        if (this.dataChannel &&
            (this.dataChannel.readyState === 'closing' || this.dataChannel.readyState === 'closed')) {
            const dcState = this.dataChannel.readyState;
            this.log.info(`Skipping ICE restart: data channel is ${dcState}, ICE restart cannot revive SCTP — escalating to full recovery (reason=${reason})`);
            this._executeMediaRecovery(`dc-${dcState}:${reason}`);
            return false;
        }
        if (this._iceRestartInProgress) {
            this.log.debug('ICE restart already in progress');
            return false;
        }
        if (this._iceRestartAttempts >= WebRTCManager.ICE_RESTART_MAX_ATTEMPTS) {
            this.log.info(`ICE restart exhausted (${this._iceRestartAttempts} attempts), falling back to full recovery`);
            return false;
        }
        // Check if session WS is open (required for signaling)
        const sessionWs = this.ctx.getSessionWs();
        if (!sessionWs || sessionWs.readyState !== WebSocket.OPEN) {
            this.log.warn('Cannot ICE restart: session WebSocket not open');
            return false;
        }
        this._iceRestartInProgress = true;
        this._iceRestartAttempts++;
        const gen = this._pcGeneration;
        this.log.info(`Attempting ICE restart ${this._iceRestartAttempts}/${WebRTCManager.ICE_RESTART_MAX_ATTEMPTS}, reason=${reason}, gen=${gen}`);
        this.ctx.host.dispatchEvent(new CustomEvent('ice-restart-started', {
            detail: { attempt: this._iceRestartAttempts, reason, generation: gen }
        }));
        this.ctx.sendSessionMessage({
            type: 'report-event',
            event_type: 'webrtc.ice_restart_started',
            data: { attempt: this._iceRestartAttempts, reason, generation: gen },
        });
        try {
            // Create offer with ICE restart flag
            const offer = await pc.createOffer({ iceRestart: true });
            // Stale guard
            if (this._pcGeneration !== gen) {
                this._iceRestartInProgress = false;
                return false;
            }
            // Apply SDP munging if needed
            const mungedSdp = this.mungeSDP(offer.sdp);
            const mungedOffer = { ...offer, sdp: mungedSdp };
            await pc.setLocalDescription(mungedOffer);
            // Send the restart offer via session WebSocket
            const peerAgentId = this.ctx.getPeerAgentId();
            this.ctx.sendSessionMessage({
                type: 'webrtc-signaling',
                signal_type: 'offer',
                payload: { sdp: mungedOffer.sdp, type: mungedOffer.type },
                ice_restart: true,
                ...(peerAgentId && { target_agent_id: peerAgentId })
            });
            // Start timeout for ICE restart
            this._iceRestartTimer = setTimeout(() => {
                this._iceRestartTimer = null;
                if (this._pcGeneration !== gen)
                    return;
                this.log.warn(`ICE restart timeout expired (attempt ${this._iceRestartAttempts})`);
                this._iceRestartInProgress = false;
                // If we can retry, do so; otherwise fall back to full recovery
                if (this._iceRestartAttempts < WebRTCManager.ICE_RESTART_MAX_ATTEMPTS) {
                    this._attemptIceRestart(`timeout-retry-${this._iceRestartAttempts}`);
                }
                else {
                    this.log.info('ICE restart failed after max attempts, falling back to full media recovery');
                    this.ctx.host.dispatchEvent(new CustomEvent('ice-restart-failed', {
                        detail: { attempts: this._iceRestartAttempts, reason, generation: gen }
                    }));
                    this.ctx.sendSessionMessage({
                        type: 'report-event',
                        event_type: 'webrtc.ice_restart_failed',
                        data: { attempts: this._iceRestartAttempts, reason, generation: gen },
                    });
                    this._executeMediaRecovery(`ice-restart-exhausted:${reason}`);
                }
            }, WebRTCManager.ICE_RESTART_TIMEOUT_MS);
            return true;
        }
        catch (error) {
            this.log.error('ICE restart failed', error);
            this._iceRestartInProgress = false;
            const errMsg = error instanceof Error ? error.message : String(error);
            this.ctx.host.dispatchEvent(new CustomEvent('ice-restart-error', {
                detail: {
                    error: errMsg,
                    attempt: this._iceRestartAttempts,
                    generation: gen
                }
            }));
            this.ctx.sendSessionMessage({
                type: 'report-event',
                event_type: 'webrtc.ice_restart_error',
                data: { error: errMsg, attempt: this._iceRestartAttempts, generation: gen },
            });
            return false;
        }
    }
    /**
     * Check if TURN servers are reachable by attempting a simple ICE gathering.
     * Creates a temporary peer connection, forces relay-only transport, and
     * checks if any relay candidates are gathered within the timeout.
     *
     * Returns true if at least one TURN server responds, false otherwise.
     * Results are cached for TURN_HEALTH_CHECK_CACHE_MS.
     */
    async _checkTurnHealth() {
        const iceServers = this.ctx.getIceServers();
        const turnServers = iceServers.filter(server => {
            const urls = Array.isArray(server.urls) ? server.urls : [server.urls];
            return urls.some(url => url.startsWith('turn:') || url.startsWith('turns:'));
        });
        if (turnServers.length === 0) {
            this.log.debug('No TURN servers configured, skipping health check');
            return true; // No TURN = nothing to check
        }
        // Check cache
        const now = Date.now();
        if (this._turnHealthy !== null &&
            (now - this._lastTurnHealthCheck) < WebRTCManager.TURN_HEALTH_CHECK_CACHE_MS) {
            this.log.debug(`Using cached TURN health: ${this._turnHealthy}`);
            return this._turnHealthy;
        }
        this.log.info('Probing TURN server health...');
        const startTime = performance.now();
        return new Promise((resolve) => {
            let resolved = false;
            let relayFound = false;
            const probe = new RTCPeerConnection({
                iceServers: turnServers,
                iceTransportPolicy: 'relay' // Force TURN-only
            });
            const cleanup = () => {
                if (!resolved) {
                    resolved = true;
                    probe.close();
                }
            };
            // Timeout handler
            const timeoutId = setTimeout(() => {
                if (!resolved) {
                    this.log.warn(`TURN health check timed out after ${WebRTCManager.TURN_HEALTH_CHECK_TIMEOUT_MS}ms`);
                    this._turnHealthy = false;
                    this._lastTurnHealthCheck = Date.now();
                    cleanup();
                    resolve(false);
                }
            }, WebRTCManager.TURN_HEALTH_CHECK_TIMEOUT_MS);
            probe.onicecandidate = (event) => {
                if (event.candidate) {
                    // Check if it's a relay candidate
                    if (event.candidate.type === 'relay' ||
                        event.candidate.candidate.includes('typ relay')) {
                        if (!relayFound) {
                            relayFound = true;
                            const elapsed = (performance.now() - startTime).toFixed(2);
                            this.log.info(`TURN health check passed in ${elapsed}ms`);
                            this._turnHealthy = true;
                            this._lastTurnHealthCheck = Date.now();
                            clearTimeout(timeoutId);
                            cleanup();
                            resolve(true);
                        }
                    }
                }
                else {
                    // Gathering complete without relay candidate
                    if (!relayFound && !resolved) {
                        this.log.warn('TURN health check failed: no relay candidates gathered');
                        this._turnHealthy = false;
                        this._lastTurnHealthCheck = Date.now();
                        clearTimeout(timeoutId);
                        cleanup();
                        resolve(false);
                    }
                }
            };
            probe.onicecandidateerror = (event) => {
                this.log.debug('TURN probe ICE error', {
                    errorCode: event.errorCode,
                    errorText: event.errorText,
                    url: event.url
                });
            };
            // Add a data channel to trigger ICE gathering
            probe.createDataChannel('turn-probe');
            probe.createOffer()
                .then(offer => probe.setLocalDescription(offer))
                .catch(err => {
                this.log.debug('TURN probe offer failed', err);
                if (!resolved) {
                    this._turnHealthy = false;
                    this._lastTurnHealthCheck = Date.now();
                    clearTimeout(timeoutId);
                    cleanup();
                    resolve(false);
                }
            });
        });
    }
    /**
     * Get the current TURN health status.
     * null = unknown/not checked, true = healthy, false = unhealthy
     */
    getTurnHealthy() {
        return this._turnHealthy;
    }
    /**
     * Enter the disconnect grace period. Polls RTP stats periodically.
     * If media is still flowing, cancels (connection is fine despite state).
     * If media stops flowing or grace expires, triggers recovery.
     */
    async _startDisconnectGrace(reason) {
        // Idempotent: cancel any existing grace period
        this._cancelDisconnectGrace();
        // Snapshot current bytes for flow comparison
        this._rtpBytesAtDisconnect = await this._snapshotRtpBytes();
        const graceMs = this.ctx.config.disconnectGraceMs;
        const gen = this._pcGeneration;
        this.log.info(`Starting disconnect grace (${graceMs}ms). ` +
            `reason=${reason}, gen=${gen}, bytesSnapshot=${this._rtpBytesAtDisconnect}, turnUsed=${this._lastKnownTurnUsed}`);
        this.ctx.setStreamState('interrupted', {
            reason,
            generation: gen,
            graceMs,
            bytesSnapshot: this._rtpBytesAtDisconnect,
            turnUsed: this._lastKnownTurnUsed
        });
        // Polling: check RTP flow periodically during grace
        this._disconnectPollInterval = setInterval(async () => {
            // Stale guard: if generation changed, stop polling
            if (this._pcGeneration !== gen) {
                this._cancelDisconnectGrace();
                return;
            }
            const flowing = await this._isRtpFlowing();
            this.log.debug(`Grace poll: flowing=${flowing}, gen=${gen}`);
            if (flowing) {
                this.log.info('RTP still flowing during grace - cancelling recovery');
                this._cancelDisconnectGrace();
                this.ctx.setStreamState('streaming', { reason: 'grace-resolved', generation: gen });
            }
        }, WebRTCManager.DISCONNECT_POLL_INTERVAL_MS);
        // Grace timeout: if not recovered, trigger recovery
        this._disconnectGraceTimer = setTimeout(async () => {
            if (this._disconnectPollInterval) {
                clearInterval(this._disconnectPollInterval);
                this._disconnectPollInterval = null;
            }
            this._disconnectGraceTimer = null;
            // Stale guard
            if (this._pcGeneration !== gen)
                return;
            // Final flow check
            const flowing = await this._isRtpFlowing();
            if (flowing) {
                this.log.info('RTP flowing at grace expiry - skipping recovery');
                this.ctx.setStreamState('streaming', { reason: 'grace-resolved', generation: gen });
                return;
            }
            // Try ICE restart first before full recovery
            this.log.info('Grace expired, RTP not flowing - attempting ICE restart first');
            const iceRestartStarted = await this._attemptIceRestart(`grace-expired:${reason}`);
            if (!iceRestartStarted) {
                // ICE restart not possible, fall back to full recovery
                this._executeMediaRecovery(`grace-expired:${reason}`);
            }
        }, graceMs);
    }
    /**
     * Execute media recovery: tear down the old PeerConnection, create a new one,
     * and re-subscribe to trigger a fresh offer from the server.
     *
     * Guarded by: single-flight lock, minimum interval, sliding window, session WS state.
     */
    async _executeMediaRecovery(reason) {
        // Cancel any grace timer (recovery supersedes grace)
        this._cancelDisconnectGrace();
        // Check all preconditions
        const check = this._canStartRecovery();
        if (!check.allowed) {
            this.log.info(`Recovery blocked: ${check.reason} (reason=${reason})`);
            // If blocked by sliding window, surface as fatal
            if (check.reason?.startsWith('max-recoveries-in-window')) {
                this.ctx.setStreamState('failed', {
                    reason: 'recovery-exhausted',
                    blockReason: check.reason,
                    recoveryTimestamps: [...this._recoveryTimestamps],
                    turnUsed: this._lastKnownTurnUsed
                });
                const exhaustedEvent = new CustomEvent('media-recovery-exhausted', {
                    cancelable: true,
                    detail: {
                        reason,
                        blockReason: check.reason,
                        recoveryTimestamps: [...this._recoveryTimestamps],
                        turnUsed: this._lastKnownTurnUsed
                    }
                });
                this.ctx.host.dispatchEvent(exhaustedEvent);
                // Default: leave session so worker-agent isn't held hostage.
                // Consumers can call preventDefault() to handle this themselves.
                if (!exhaustedEvent.defaultPrevented) {
                    this.log.warn('Media recovery exhausted - leaving session');
                    this.ctx.leaveSession('media-recovery-exhausted');
                }
            }
            return;
        }
        // Acquire the single-flight lock and set the visible state BEFORE any
        // await. Two reasons:
        //
        // 1. Concurrency: _canStartRecovery is the gate; the lock has to be
        //    visible to any concurrent caller that runs between the gate and
        //    the first await. Otherwise two concurrent triggers (e.g. the
        //    dc-closed bailout in _attemptIceRestart firing
        //    _executeMediaRecovery, then the original caller's .then(if
        //    !started executeMediaRecovery) firing it again) both pass the
        //    gate, both await, both proceed — racing each other's
        //    offer/answer exchange.
        //
        // 2. UX: setStreamState('recovering') has to fire before the TURN
        //    health check await, or the user lingers in `interrupted` past
        //    the 3s silence window and sees the alarming "Network
        //    interruption detected" label even though we've already decided
        //    to recover. The TURN check is informational (it only warns;
        //    it doesn't gate recovery), so let it run after the state is
        //    visible.
        this._recoveryInFlight = true;
        this._lastRecoveryStartTime = Date.now();
        this._recoveryTimestamps.push(this._lastRecoveryStartTime);
        // Cancel any pending ICE restart (full recovery supersedes ICE restart)
        this._cancelIceRestart();
        this._iceRestartAttempts = 0;
        // Bump generation — all stale closures will detect this
        const newGen = ++this._pcGeneration;
        this.log.info(`Starting recovery gen=${newGen}, reason=${reason}, ` +
            `turnUsed=${this._lastKnownTurnUsed}, recoveriesInWindow=${this._recoveryTimestamps.length}`);
        this.ctx.setStreamState('recovering', {
            reason,
            generation: newGen,
            turnUsed: this._lastKnownTurnUsed,
            recoveriesInWindow: this._recoveryTimestamps.length
        });
        // Hard deadline for this recovery — covers the `have-local-offer` stall
        // when the new PC's offer goes to a worker that's already gone.
        this._startRecoveryWatchdog(newGen);
        // TURN health check now runs after the state transition, so the UI
        // shows `recovering` immediately while we probe TURN in parallel.
        if (this._lastKnownTurnUsed) {
            const turnHealthy = await this._checkTurnHealth();
            if (!turnHealthy) {
                this.log.warn('TURN health check failed, recovery may be slow or unsuccessful');
                this.ctx.host.dispatchEvent(new CustomEvent('turn-unhealthy', {
                    detail: { reason, lastKnownTurnUsed: this._lastKnownTurnUsed }
                }));
                // Continue with recovery anyway, but warn
            }
        }
        try {
            // Collect pre-teardown stats for diagnostics
            const sessionStartTime = this.ctx.getMetricsSessionStartTime();
            if (sessionStartTime && this.peerConnection) {
                this.ctx.collectAndDisplaySessionStats();
            }
            // Nullify event handlers BEFORE closing to prevent late callbacks
            if (this.peerConnection) {
                this.peerConnection.onconnectionstatechange = null;
                this.peerConnection.oniceconnectionstatechange = null;
                this.peerConnection.onicecandidate = null;
                this.peerConnection.ontrack = null;
                this.peerConnection.onicecandidateerror = null;
                this.peerConnection.ondatachannel = null;
                this.peerConnection.onicegatheringstatechange = null;
            }
            // Close old PC
            this.log.info(`Closing old PeerConnection (gen=${newGen - 1})`);
            this.closePeerConnection();
            // Clear stale track state so handleTrack treats the next arrival
            // as the first video track and re-attaches srcObject + calls play().
            // closePeerConnection() doesn't touch these, and track.onended may
            // not fire reliably across all browsers when PC.close() is called.
            this.videoTracks.forEach(({ track }) => track.stop());
            this.videoTracks.clear();
            this.activeVideoTrackId = null;
            const videoElement = this.ctx.getVideoElement();
            if (videoElement.srcObject) {
                const oldStream = videoElement.srcObject;
                oldStream.getTracks().forEach(t => t.stop());
                videoElement.srcObject = null;
            }
            if (this._placeholderStream) {
                this._placeholderStream.getTracks().forEach(t => t.stop());
                this._placeholderStream = null;
            }
            // Create new PC with existing ICE servers. Preserve mic intent
            // across recovery — `_useMic` captures both initial-config and
            // runtime-enabled mic, which `this.ctx.config.useMic` alone does
            // not. Without this, a mic enabled via requestMicrophone() would
            // be silently dropped on the recovered PC.
            const iceServers = this.ctx.getIceServers();
            this.log.info(`Creating new PeerConnection (gen=${newGen})`);
            await this.createPeerConnection({
                iceServers,
                useMic: this._useMic || this.ctx.config.useMic,
            });
            // Re-run the full subscribe flow so browser-initiated offer mode
            // re-creates the data channel and sends a fresh offer. A raw
            // update-presence would only cover streamer-initiated mode; in
            // browser mode the server never re-offers, so neither the DC
            // m-line nor any media would get renegotiated.
            const subscribedStreamerId = this.ctx.getSubscribedStreamerId();
            if (subscribedStreamerId) {
                this.log.info(`Re-subscribing to streamer: ${subscribedStreamerId}`);
                await this.ctx.subscribeToStreamer(subscribedStreamerId);
            }
            else {
                this.log.warn('No subscribed streamer ID for recovery');
            }
            this.log.info(`Recovery initiated gen=${newGen}, waiting for server offer`);
        }
        catch (error) {
            this.log.error(`Recovery failed gen=${newGen}`, error);
            this.ctx.host.dispatchEvent(new CustomEvent('media-recovery-error', {
                detail: {
                    error: error instanceof Error ? error.message : String(error),
                    generation: newGen,
                    reason
                }
            }));
        }
        finally {
            this._recoveryInFlight = false;
        }
    }
    // ===== SIMPLE BINARY PROTOCOL (Types 2-7) =====
    /**
     * Send raw binary data over the data channel
     */
    sendRawBinary(buffer) {
        if (this.dataChannel && this.dataChannel.readyState === 'open') {
            // Create a new ArrayBuffer to avoid SharedArrayBuffer type issues
            const arrayBuffer = new ArrayBuffer(buffer.length);
            new Uint8Array(arrayBuffer).set(buffer);
            this.dataChannel.send(arrayBuffer);
        }
    }
    /**
     * Send KeyDown event (Type 2) - Simple protocol
     */
    sendKeyDownSimple(keyCode, repeat = false) {
        const buffer = new Uint8Array([2, keyCode, repeat ? 1 : 0]);
        this.sendRawBinary(buffer);
    }
    /**
     * Send KeyUp event (Type 3) - Simple protocol
     */
    sendKeyUpSimple(keyCode) {
        const buffer = new Uint8Array([3, keyCode, 0]);
        this.sendRawBinary(buffer);
    }
    /**
     * Send MouseDown event (Type 4) - Simple protocol
     * @param x X coordinate (0-65535)
     * @param y Y coordinate (0-65535)
     * @param button Mouse button (0=left, 1=middle, 2=right)
     */
    sendMouseDownSimple(x, y, button = 0) {
        const buffer = new Uint8Array(9);
        buffer[0] = 4; // MouseDown
        buffer[1] = x & 0xFF;
        buffer[2] = (x >> 8) & 0xFF;
        buffer[3] = y & 0xFF;
        buffer[4] = (y >> 8) & 0xFF;
        buffer[5] = button;
        this.sendRawBinary(buffer);
    }
    /**
     * Send MouseUp event (Type 5) - Simple protocol
     * @param x X coordinate (0-65535)
     * @param y Y coordinate (0-65535)
     * @param button Mouse button (0=left, 1=middle, 2=right)
     */
    sendMouseUpSimple(x, y, button = 0) {
        const buffer = new Uint8Array(9);
        buffer[0] = 5; // MouseUp
        buffer[1] = x & 0xFF;
        buffer[2] = (x >> 8) & 0xFF;
        buffer[3] = y & 0xFF;
        buffer[4] = (y >> 8) & 0xFF;
        buffer[5] = button;
        this.sendRawBinary(buffer);
    }
    /**
     * Send MouseMove event (Type 6) - Simple protocol
     * @param x X coordinate (0-65535)
     * @param y Y coordinate (0-65535)
     */
    sendMouseMoveSimple(x, y) {
        const buffer = new Uint8Array(9);
        buffer[0] = 6; // MouseMove
        buffer[1] = x & 0xFF;
        buffer[2] = (x >> 8) & 0xFF;
        buffer[3] = y & 0xFF;
        buffer[4] = (y >> 8) & 0xFF;
        buffer[5] = 0;
        this.sendRawBinary(buffer);
    }
    /**
     * Send MouseWheel event (Type 7) - Simple protocol
     * @param delta Wheel delta (positive = up, negative = down)
     */
    sendMouseWheelSimple(delta) {
        const buffer = new Uint8Array(4);
        buffer[0] = 7; // MouseWheel
        buffer[1] = delta & 0xFF;
        buffer[2] = (delta >> 8) & 0xFF;
        this.sendRawBinary(buffer);
    }
    /**
     * Send message to UE via WebRTC data channel
     * Uses binary encoding for efficient transmission
     */
    sendToStreamer(type, data = []) {
        // Encode message to binary format
        const buffer = MessageEncoder.encode(type, data);
        // Send via data channel if available
        if (this.dataChannel && this.dataChannel.readyState === 'open') {
            this.dataChannel.send(buffer);
        }
        else {
            this.log.warn(`Cannot send ${type}: data channel not open`);
        }
        // Still emit event for external listeners
        this.ctx.host.dispatchEvent(new CustomEvent('streamer-message', {
            detail: { type, data }
        }));
    }
    // ===== PEER CONNECTION LIFECYCLE =====
    /**
     * Create and configure WebRTC peer connection
     */
    async createPeerConnection(config) {
        // Transition to 'starting' unless we're mid-recovery (which stays in 'recovering')
        if (!this._recoveryInFlight) {
            this.ctx.setStreamState('starting', { generation: this._pcGeneration });
        }
        // Warm the video element pipeline as early as possible — the
        // canvas placeholder stream and WebCodecs probe run in parallel
        // with the rest of the peer connection setup below.
        this.warmVideoElement();
        // On Android, verify codec availability in a background probe.
        // Android Chrome can silently omit H264 from the first few SDP offers.
        if (this.isAndroid() && !this._codecVerified) {
            this.verifyCodecAvailability().then(codec => {
                if (codec) {
                    this._codecVerified = true;
                }
                else {
                    this.ctx.host.dispatchEvent(new CustomEvent('codec-verification-failed'));
                }
            });
        }
        const rtcConfig = {
            iceServers: config?.iceServers || [
                { urls: 'stun:stun.cloudflare.com:3478' },
            ],
            // Single BUNDLE group → one ICE component; pool size only needs
            // to cover that plus a small buffer for renegotiation.
            iceCandidatePoolSize: 2,
            iceTransportPolicy: 'all',
            bundlePolicy: 'max-bundle'
        };
        if (config?.forceRelay || this.ctx.config.forceRelay) {
            rtcConfig.iceTransportPolicy = 'relay';
        }
        this.peerConnection = new RTCPeerConnection(rtcConfig);
        // Reset ICE error counters for new connection
        this.turnErrorCount = 0;
        this.stunErrorCount = 0;
        // Track peer connection creation time
        if (!this.ctx.performanceMetrics.peerConnectionCreated) {
            this.ctx.performanceMetrics.peerConnectionCreated = performance.now();
        }
        // Setup event handlers with ICE batching
        this.peerConnection.onicecandidate = (event) => {
            if (event.candidate) {
                // Add to batch
                this.iceCandidateBatch.push({
                    candidate: event.candidate.candidate,
                    sdpMid: event.candidate.sdpMid,
                    sdpMLineIndex: event.candidate.sdpMLineIndex,
                    usernameFragment: event.candidate.usernameFragment
                });
                // Clear existing timer
                if (this.iceBatchTimer) {
                    clearTimeout(this.iceBatchTimer);
                }
                // Set timer to send batch
                this.iceBatchTimer = setTimeout(() => {
                    if (this.iceCandidateBatch.length > 0) {
                        this.log.debug(`Sending ${this.iceCandidateBatch.length} ICE candidates in batch`);
                        const peerAgentId = this.ctx.getPeerAgentId();
                        // Send through session WebSocket if connected, otherwise emit events
                        this.iceCandidateBatch.forEach(candidate => {
                            const sessionWs = this.ctx.getSessionWs();
                            if (sessionWs && sessionWs.readyState === WebSocket.OPEN) {
                                // Send via session WebSocket using agent-protocol format
                                this.ctx.sendSessionMessage({
                                    type: 'webrtc-signaling',
                                    signal_type: 'iceCandidate',
                                    payload: candidate,
                                    ...(peerAgentId && { target_agent_id: peerAgentId })
                                });
                            }
                            else {
                                // Fallback: emit event for external handling
                                this.ctx.host.dispatchEvent(new CustomEvent('ice-candidate', {
                                    detail: candidate
                                }));
                            }
                        });
                        this.iceCandidateBatch = [];
                    }
                }, this.ctx.config.iceBatchDelay);
            }
            else {
                // null candidate means gathering complete - send any remaining immediately
                if (this.iceBatchTimer) {
                    clearTimeout(this.iceBatchTimer);
                }
                if (this.iceCandidateBatch.length > 0) {
                    this.log.debug(`Sending final ${this.iceCandidateBatch.length} ICE candidates`);
                    const peerAgentId = this.ctx.getPeerAgentId();
                    this.iceCandidateBatch.forEach(candidate => {
                        const sessionWs = this.ctx.getSessionWs();
                        if (sessionWs && sessionWs.readyState === WebSocket.OPEN) {
                            // Send via session WebSocket using agent-protocol format
                            this.ctx.sendSessionMessage({
                                type: 'webrtc-signaling',
                                signal_type: 'iceCandidate',
                                payload: candidate,
                                ...(peerAgentId && { target_agent_id: peerAgentId })
                            });
                        }
                        else {
                            // Fallback: emit event for external handling
                            this.ctx.host.dispatchEvent(new CustomEvent('ice-candidate', {
                                detail: candidate
                            }));
                        }
                    });
                    this.iceCandidateBatch = [];
                }
            }
        };
        this.peerConnection.ontrack = (event) => {
            this.handleTrack(event);
        };
        this.peerConnection.onconnectionstatechange = () => {
            const pc = this.peerConnection;
            if (!pc)
                return;
            const state = pc.connectionState;
            const gen = this._pcGeneration;
            this.log.info(`Connection state: ${state} (gen=${gen})`);
            this.ctx.host.dispatchEvent(new CustomEvent('connection-state-change', {
                detail: state
            }));
            if (state === 'connected') {
                // Cancel any grace timer, ICE restart, and recovery watchdog —
                // the connection is up again.
                this._cancelDisconnectGrace();
                this._cancelIceRestart();
                this._cancelRecoveryWatchdog();
                this.ctx.setStreamState('streaming', { reason: 'connected', generation: gen });
                // Emit success event if ICE restart was in progress
                if (this._iceRestartAttempts > 0) {
                    this.ctx.host.dispatchEvent(new CustomEvent('ice-restart-success', {
                        detail: { attempts: this._iceRestartAttempts, generation: gen }
                    }));
                    this.ctx.sendSessionMessage({
                        type: 'report-event',
                        event_type: 'webrtc.ice_restart_success',
                        data: { attempts: this._iceRestartAttempts, via: 'connection', generation: gen },
                    });
                }
                this._iceRestartAttempts = 0;
                if (!this.ctx.performanceMetrics.connectionComplete) {
                    this.ctx.performanceMetrics.connectionComplete = performance.now();
                    if (this.ctx.performanceMetrics.subscribeStart) {
                        const timeToConnection = this.ctx.performanceMetrics.connectionComplete - this.ctx.performanceMetrics.subscribeStart;
                        this.log.info(`Time to connection complete: ${timeToConnection.toFixed(2)}ms`);
                    }
                    // Start session tracking
                    this.ctx.setMetricsSessionStartTime(Date.now());
                    // Report first WebRTC connection to SessionDO (satisfies webrtcNegotiation phase)
                    this.ctx.sendSessionMessage({
                        type: 'report-event',
                        event_type: 'webrtc.connected',
                        data: { generation: this._pcGeneration },
                    });
                }
            }
            else if (state === 'failed') {
                this.log.error(`Connection failed (gen=${gen})`);
                // Collect stats before recovery
                const sessionStartTime = this.ctx.getMetricsSessionStartTime();
                if (sessionStartTime) {
                    this.ctx.collectAndDisplaySessionStats();
                }
                // Surface the interrupted state before kicking off recovery so
                // the UI has a typed media-layer state to render even when
                // connectionState skips 'disconnected' and jumps straight to
                // 'failed'. setStreamState is a no-op if we're already in
                // interrupted/recovering from a prior path (idempotent).
                this.ctx.setStreamState('interrupted', {
                    reason: 'connection-failed',
                    generation: gen,
                    turnUsed: this._lastKnownTurnUsed,
                });
                // Try ICE restart first before full recovery
                this._attemptIceRestart('connection-failed').then(started => {
                    if (!started) {
                        this._executeMediaRecovery('connection-failed');
                    }
                });
            }
            else if (state === 'disconnected') {
                this.log.warn(`Connection disconnected (gen=${gen})`);
                // Start grace period — do NOT immediately recover
                this._startDisconnectGrace('connection-disconnected');
            }
            else if (state === 'closed') {
                this.log.info(`Connection closed (gen=${gen})`);
                this._cancelDisconnectGrace();
                const sessionStartTime = this.ctx.getMetricsSessionStartTime();
                if (sessionStartTime) {
                    this.ctx.collectAndDisplaySessionStats();
                }
            }
        };
        // Handle ICE connection state changes
        this.peerConnection.oniceconnectionstatechange = () => {
            const pc = this.peerConnection;
            if (!pc)
                return;
            const state = pc.iceConnectionState;
            const prevState = this._lastIceConnectionState;
            this._lastIceConnectionState = state;
            const gen = this._pcGeneration;
            this.log.info(`ICE connection state: ${prevState || 'null'} -> ${state} (gen=${gen})`);
            if (state === 'connected' || state === 'completed') {
                // Cancel grace, ICE restart, and recovery watchdog — ICE has recovered.
                this._cancelDisconnectGrace();
                this._cancelIceRestart();
                this._cancelRecoveryWatchdog();
                // Emit success event if ICE restart was in progress
                if (this._iceRestartAttempts > 0) {
                    this.log.info(`ICE restart succeeded after ${this._iceRestartAttempts} attempt(s)`);
                    this.ctx.host.dispatchEvent(new CustomEvent('ice-restart-success', {
                        detail: { attempts: this._iceRestartAttempts, generation: gen }
                    }));
                    this.ctx.sendSessionMessage({
                        type: 'report-event',
                        event_type: 'webrtc.ice_restart_success',
                        data: { attempts: this._iceRestartAttempts, via: 'ice', generation: gen },
                    });
                }
                this._iceRestartAttempts = 0; // Reset on success
                if (!this.ctx.performanceMetrics.iceConnected) {
                    this.ctx.performanceMetrics.iceConnected = performance.now();
                    if (this.ctx.performanceMetrics.subscribeStart) {
                        const timeToIceConnected = this.ctx.performanceMetrics.iceConnected - this.ctx.performanceMetrics.subscribeStart;
                        this.log.info(`Time to ICE connected: ${timeToIceConnected.toFixed(2)}ms`);
                    }
                }
                // Detect the selected transport path (TURN relay vs direct)
                this._detectSelectedTransport(gen);
            }
            else if (state === 'failed') {
                this.log.error(`ICE connection failed (gen=${gen})`);
                // Mirror the connection-state 'failed' handler: surface the
                // interrupted state first so the UI shows a typed media-layer
                // state when ICE skips 'disconnected' and jumps to 'failed'.
                this.ctx.setStreamState('interrupted', {
                    reason: 'ice-failed',
                    generation: gen,
                    turnUsed: this._lastKnownTurnUsed,
                });
                // Try ICE restart first before full recovery
                this._attemptIceRestart('ice-failed').then(started => {
                    if (!started) {
                        this._executeMediaRecovery('ice-failed');
                    }
                });
            }
            else if (state === 'disconnected') {
                this.log.warn(`ICE connection disconnected (gen=${gen})`);
                // Start grace only if not already in grace (connectionState handler may have started it)
                if (!this._disconnectGraceTimer) {
                    this._startDisconnectGrace('ice-disconnected');
                }
            }
            this.ctx.host.dispatchEvent(new CustomEvent('ice-connection-state-change', {
                detail: { prevState, newState: state, generation: gen }
            }));
        };
        // Handle ICE candidate errors
        this.peerConnection.onicecandidateerror = (event) => {
            const errorDetails = {
                errorCode: event.errorCode,
                errorText: event.errorText,
                url: event.url,
                address: event.address,
                port: event.port
            };
            // Track TURN and STUN error counts for diagnostics
            const errorText = event.errorText?.toLowerCase() || '';
            if (errorText.includes('turn') || errorText.includes('relay')) {
                this.turnErrorCount++;
            }
            else if (errorText.includes('stun') || errorText.includes('binding')) {
                this.stunErrorCount++;
            }
            // Only log first few errors of each type to avoid console spam
            const totalErrors = this.turnErrorCount + this.stunErrorCount;
            if (totalErrors <= 3) {
                this.log.error('ICE candidate error', errorDetails);
            }
            else if (totalErrors === 4) {
                this.log.warn('Multiple ICE errors detected, suppressing further error logs');
                this.log.warn(`STUN errors: ${this.stunErrorCount}, TURN errors: ${this.turnErrorCount}`);
            }
            this.ctx.host.dispatchEvent(new CustomEvent('ice-candidate-error', {
                detail: errorDetails
            }));
        };
        this.peerConnection.onicegatheringstatechange = () => {
            this.ctx.host.dispatchEvent(new CustomEvent('ice-gathering-state-change', {
                detail: this.peerConnection.iceGatheringState
            }));
        };
        // Register for data channel if UE creates it (when UE sends offer)
        this.peerConnection.ondatachannel = (event) => {
            this.handleDataChannel(event);
        };
        // Add transceivers for audio/video
        const videoTransceiver = this.peerConnection.addTransceiver('video', { direction: 'recvonly' });
        // Set codec preference order: H264 > AV1 > VP9
        this.setVideoCodecPreferences(videoTransceiver);
        if (config?.forceMonoAudio) {
            this._forceMonoAudio = true;
        }
        // Acquire microphone BEFORE adding audio transceiver so the initial
        // SDP offer/answer already includes sendrecv direction with the real
        // audio track — matching the PureWeb approach that avoids mid-session
        // renegotiation.
        const wantMic = config?.useMic || this.ctx.config.useMic;
        if (wantMic) {
            const micStream = await this.acquireMicrophoneStream();
            if (micStream) {
                const audioTrack = micStream.getAudioTracks()[0];
                this.peerConnection.addTransceiver(audioTrack, { direction: 'sendrecv' });
                this.microphoneStream = micStream;
                this._useMic = true;
                this.ctx.host.dispatchEvent(new CustomEvent('microphone-enabled'));
            }
            else {
                // Mic denied/unavailable — fall back to receive-only
                this.peerConnection.addTransceiver('audio', { direction: 'recvonly' });
            }
        }
        else {
            this.peerConnection.addTransceiver('audio', { direction: 'recvonly' });
        }
    }
    /**
     * Close peer connection
     */
    closePeerConnection() {
        // Transition to 'idle' unless we're mid-recovery (which stays in 'recovering')
        if (!this._recoveryInFlight) {
            this.ctx.setStreamState('idle');
        }
        // Clear pending ICE candidate batch timer
        if (this.iceBatchTimer) {
            clearTimeout(this.iceBatchTimer);
            this.iceBatchTimer = null;
        }
        this.iceCandidateBatch = [];
        if (this._keyframeWorker) {
            this._keyframeWorker.terminate();
            this._keyframeWorker = null;
        }
        if (this.dataChannel) {
            this.dataChannel.onopen = null;
            this.dataChannel.onclose = null;
            this.dataChannel.onmessage = null;
            this.dataChannel.onerror = null;
            this.dataChannel.close();
            this.dataChannel = null;
        }
        // Stop the outbound mic stream so recovery can safely re-acquire via
        // getUserMedia without double-binding the device. `_useMic` is kept
        // as the desire flag so the next createPeerConnection can restore it.
        if (this.microphoneStream) {
            this.microphoneStream.getTracks().forEach(track => track.stop());
            this.microphoneStream = null;
        }
        if (this.peerConnection) {
            // Nullify all handlers to prevent late callbacks during/after close()
            this.peerConnection.onconnectionstatechange = null;
            this.peerConnection.oniceconnectionstatechange = null;
            this.peerConnection.onicecandidate = null;
            this.peerConnection.ontrack = null;
            this.peerConnection.onicecandidateerror = null;
            this.peerConnection.ondatachannel = null;
            this.peerConnection.onicegatheringstatechange = null;
            this.peerConnection.onnegotiationneeded = null;
            this.peerConnection.close();
            this.peerConnection = null;
        }
    }
    /**
     * Pre-create peer connection for faster setup.
     * Skips if connection already exists.
     */
    async preCreatePeerConnection(config) {
        if (this.peerConnection) {
            this.log.debug('Peer connection already exists, skipping pre-creation');
            return;
        }
        this.log.debug('Pre-creating peer connection for faster setup...');
        await this.createPeerConnection(config);
    }
    /**
     * Check if peer connection has been pre-created
     */
    isPeerConnectionReady() {
        return this.peerConnection !== null;
    }
    // ===== PIPELINE WARMING =====
    /**
     * Warm the video decode/render pipeline before the real stream arrives.
     *
     * Assigns a black placeholder MediaStream (from a tiny canvas) to the
     * video element, forcing the browser to:
     *  - allocate the compositor surface / GPU texture
     *  - initialise the MediaStream → video element plumbing
     *  - spin up the decode scheduler
     *
     * Also probes WebCodecs VideoDecoder to pre-load the hardware codec
     * driver (fire-and-forget, best-effort).
     */
    warmVideoElement() {
        const videoElement = this.ctx.getVideoElement();
        // Only warm if no stream is already assigned
        if (videoElement.srcObject)
            return;
        try {
            const canvas = document.createElement('canvas');
            canvas.width = 2;
            canvas.height = 2;
            // Leave the canvas transparent — a fresh canvas is already
            // RGBA(0,0,0,0), which is invisible if the video element is
            // momentarily visible during the pending state.
            canvas.getContext('2d');
            this._placeholderStream = canvas.captureStream(0);
            // Manually push one frame so the video element has something to display
            const vt = this._placeholderStream.getVideoTracks()[0];
            if (vt && 'requestFrame' in vt) {
                vt.requestFrame();
            }
            videoElement.srcObject = this._placeholderStream;
            videoElement.play().catch(() => {
                // Silently ignore — placeholder play failure is non-critical
            });
            this.log.debug('Video pipeline warmed with placeholder stream');
        }
        catch (e) {
            this.log.debug('Failed to warm video pipeline', e);
        }
        this.warmVideoDecoder();
    }
    /**
     * Pre-load the hardware video decoder by briefly configuring a WebCodecs
     * VideoDecoder for each preferred codec.  This triggers the OS to load the
     * codec driver / shared library, benefitting the subsequent WebRTC decode
     * path on platforms where the driver is shared (most desktop & Android).
     */
    warmVideoDecoder() {
        const VD = globalThis.VideoDecoder;
        if (!VD)
            return;
        const codecs = [
            'av01.0.08M.08', // AV1 Main, level 4.0, 8-bit
            'vp09.00.10.08', // VP9 Profile 0, level 1.0, 8-bit
            'avc1.640028', // H264 High, level 4.0
        ];
        for (const codec of codecs) {
            try {
                const decoder = new VD({
                    output: () => { },
                    error: () => { },
                });
                decoder.configure({ codec });
                decoder.close();
                this.log.debug(`Warmed VideoDecoder for ${codec}`);
                return; // One successful probe is enough to load the driver
            }
            catch {
                // Codec not supported on this platform, try next
            }
        }
    }
    // ===== CODEC VERIFICATION (ANDROID) =====
    /**
     * Verify that at least one preferred video codec appears in a probe SDP offer.
     *
     * Android Chrome can silently omit H264 hardware decode from the first few
     * SDP offers while the codec driver is loading. This creates a disposable
     * RTCPeerConnection up to `maxAttempts` times (250ms apart) and checks
     * whether any preferred codec (H264, VP9, AV1) is present in the offer.
     *
     * Returns the first matched codec name, or null if none found.
     */
    async verifyCodecAvailability(maxAttempts = 10) {
        const preferredCodecs = ['H264', 'VP9', 'AV1'];
        for (let attempt = 0; attempt < maxAttempts; attempt++) {
            const probe = new RTCPeerConnection({});
            try {
                probe.addTransceiver('video');
                const offer = await probe.createOffer();
                const sdp = offer.sdp || '';
                for (const codec of preferredCodecs) {
                    if (new RegExp(`rtpmap:\\d+ ${codec}`, 'i').test(sdp)) {
                        this.log.debug(`Codec ${codec} verified in SDP (attempt ${attempt + 1})`);
                        return codec;
                    }
                }
            }
            finally {
                probe.close();
            }
            if (attempt < maxAttempts - 1) {
                await new Promise(r => setTimeout(r, 250));
            }
        }
        this.log.warn(`No preferred codecs found in SDP after ${maxAttempts} attempts`);
        return null;
    }
    isAndroid() {
        return /android/i.test(navigator.userAgent);
    }
    // ===== TRACK HANDLING =====
    /**
     * Handle incoming media tracks (video/audio)
     * Supports multiple video tracks (UE 5.2+ multiple cameras)
     */
    handleTrack(event) {
        const track = event.track;
        // Unity WebRTC sends video tracks without associated streams
        // (event.streams is empty). Create a MediaStream wrapper so the
        // track isn't silently dropped.
        const stream = event.streams.length > 0
            ? event.streams[0]
            : new MediaStream([track]);
        // Track first track received (performance metric)
        if (!this.ctx.performanceMetrics.firstTrack) {
            this.ctx.performanceMetrics.firstTrack = performance.now();
            if (this.ctx.performanceMetrics.subscribeStart) {
                const timeToFirstTrack = this.ctx.performanceMetrics.firstTrack - this.ctx.performanceMetrics.subscribeStart;
                this.log.info(`Time to first track: ${timeToFirstTrack.toFixed(2)}ms`);
            }
        }
        if (track.kind === 'video') {
            // Tune receiver-side buffering based on stream-quality preset.
            // jitterBufferTarget (standard, ms) and playoutDelayHint (Chrome, seconds)
            // trade decode-to-render latency against resilience to network jitter.
            const preset = STREAM_QUALITY_PRESETS[this.ctx.config.streamQuality] ?? STREAM_QUALITY_PRESETS['quality'];
            const jitterMs = preset.jitterBufferMs;
            const receiver = event.receiver;
            if ('jitterBufferTarget' in receiver) {
                receiver.jitterBufferTarget = jitterMs;
                this.log.debug(`Video jitterBufferTarget set to ${jitterMs}ms`);
            }
            if ('playoutDelayHint' in receiver) {
                receiver.playoutDelayHint = jitterMs / 1000;
                this.log.debug(`Video playoutDelayHint set to ${jitterMs / 1000}s`);
            }
            // Attach Encoded Transform for RTCP-level keyframe requests.
            // The passthrough worker adds no processing overhead — frames flow
            // through unchanged — but gives us sendKeyFrameRequest() which
            // triggers a PLI/FIR on the RTP layer (faster than data-channel IFrameRequest).
            this.attachKeyframeTransform(receiver);
            // Store video track (supports multiple cameras)
            const trackId = track.id;
            this.videoTracks.set(trackId, { track, stream });
            // If this is the first video track, make it active
            if (this.videoTracks.size === 1) {
                this.activeVideoTrackId = trackId;
                const videoElement = this.ctx.getVideoElement();
                videoElement.srcObject = stream;
                // Release the placeholder warming stream now that the
                // real stream has replaced it on the video element
                if (this._placeholderStream) {
                    this._placeholderStream.getTracks().forEach(t => t.stop());
                    this._placeholderStream = null;
                }
                // Track first frame rendered + ongoing frame drop monitoring.
                // requestVideoFrameCallback fires when a frame is actually
                // presented to the compositor — more precise than loadeddata.
                if (typeof videoElement.requestVideoFrameCallback === 'function') {
                    let lastPresentedFrames = 0;
                    const onVideoFrame = (now, metadata) => {
                        // First-frame metrics (once)
                        if (!this.ctx.performanceMetrics.firstFrame) {
                            this.ctx.performanceMetrics.firstFrame = now;
                            if (this.ctx.performanceMetrics.sessionStarted) {
                                this.log.info(`SESSION START -> FIRST FRAME: ${(now - this.ctx.performanceMetrics.sessionStarted).toFixed(2)}ms`);
                            }
                            if (this.ctx.performanceMetrics.subscribeStart) {
                                this.log.info(`SUBSCRIBE -> FIRST FRAME: ${(now - this.ctx.performanceMetrics.subscribeStart).toFixed(2)}ms`);
                            }
                            if (this.ctx.performanceMetrics.jobRequested) {
                                this.log.info(`JOB REQUEST -> FIRST FRAME: ${(now - this.ctx.performanceMetrics.jobRequested).toFixed(2)}ms`);
                            }
                            // End-to-end latency from RTCP Sender Report clock sync
                            if (metadata.captureTime) {
                                this.log.info(`End-to-end latency: ${(now - metadata.captureTime).toFixed(1)}ms`);
                            }
                            if (metadata.receiveTime && metadata.presentationTime) {
                                this.log.info(`Local decode latency: ${(metadata.presentationTime - metadata.receiveTime).toFixed(1)}ms`);
                            }
                            this.ctx.logPerformanceSummary();
                            this.ctx.reportTiming('first-frame');
                        }
                        // Frame drop detection (skip first callback — no baseline yet)
                        if (lastPresentedFrames > 0) {
                            const dropped = metadata.presentedFrames - lastPresentedFrames - 1;
                            if (dropped >= 3) {
                                this.log.info(`Dropped ${dropped} frame(s)`);
                                this.ctx.host.dispatchEvent(new CustomEvent('frames-dropped', {
                                    detail: { count: dropped, timestamp: now }
                                }));
                            }
                        }
                        lastPresentedFrames = metadata.presentedFrames;
                        videoElement.requestVideoFrameCallback(onVideoFrame);
                    };
                    videoElement.requestVideoFrameCallback(onVideoFrame);
                }
                else {
                    // Fallback for browsers without rVFC
                    const trackFirstFrame = () => {
                        if (!this.ctx.performanceMetrics.firstFrame) {
                            this.ctx.performanceMetrics.firstFrame = performance.now();
                            if (this.ctx.performanceMetrics.sessionStarted) {
                                this.log.info(`SESSION START -> FIRST FRAME: ${(performance.now() - this.ctx.performanceMetrics.sessionStarted).toFixed(2)}ms`);
                            }
                            if (this.ctx.performanceMetrics.subscribeStart) {
                                this.log.info(`SUBSCRIBE -> FIRST FRAME: ${(performance.now() - this.ctx.performanceMetrics.subscribeStart).toFixed(2)}ms`);
                            }
                            if (this.ctx.performanceMetrics.jobRequested) {
                                this.log.info(`JOB REQUEST -> FIRST FRAME: ${(performance.now() - this.ctx.performanceMetrics.jobRequested).toFixed(2)}ms`);
                            }
                            this.ctx.logPerformanceSummary();
                            this.ctx.reportTiming('first-frame');
                        }
                        videoElement.removeEventListener('loadeddata', trackFirstFrame);
                    };
                    videoElement.addEventListener('loadeddata', trackFirstFrame);
                }
                videoElement.play().catch(e => {
                    this.log.warn('Autoplay failed', e);
                    this.ctx.setAutoplayBlocked(true);
                    this.ctx.setJobState('ready');
                });
            }
            // Emit event with track info
            this.ctx.host.dispatchEvent(new CustomEvent('video-track-received', {
                detail: {
                    track,
                    stream,
                    trackId,
                    label: track.label,
                    isActive: trackId === this.activeVideoTrackId,
                    totalTracks: this.videoTracks.size
                }
            }));
            // Detect XR stream after video tracks are added
            this.ctx.detectXRStream();
            // Handle track ended
            track.onended = () => {
                this.videoTracks.delete(trackId);
                this.ctx.host.dispatchEvent(new CustomEvent('video-track-removed', {
                    detail: { trackId, label: track.label }
                }));
                // If active track ended, switch to another if available
                if (trackId === this.activeVideoTrackId && this.videoTracks.size > 0) {
                    const nextTrackId = this.videoTracks.keys().next().value;
                    this.switchVideoTrack(nextTrackId);
                }
            };
        }
        else if (track.kind === 'audio') {
            // Flip the remote-audio flag regardless of which stream the track
            // lives in, so the host can gate UI (e.g. the built-in unmute
            // button) on whether the pixel stream actually carries audio.
            this.ctx.setHasRemoteAudio(true);
            track.addEventListener('ended', () => {
                this.ctx.setHasRemoteAudio(this._hasLiveRemoteAudioTrack());
            });
            const videoElement = this.ctx.getVideoElement();
            // Check if audio is in the same stream as video
            if (videoElement.srcObject === stream) {
                // Audio and video in same stream, no separate audio element needed
                return;
            }
            // Create separate audio element for audio-only stream
            this.audioElement.srcObject = stream;
            this.audioElement.play().catch(e => {
                this.log.warn('Audio autoplay failed', e);
            });
            this.ctx.host.dispatchEvent(new CustomEvent('audio-track-received', {
                detail: { track, stream }
            }));
        }
    }
    /**
     * Check whether any live remote audio tracks remain attached to the
     * video or audio elements. Used to maintain `hasRemoteAudio` state.
     */
    _hasLiveRemoteAudioTrack() {
        const videoEl = this.ctx.getVideoElement();
        const videoStream = videoEl.srcObject;
        if (videoStream?.getAudioTracks().some(t => t.readyState === 'live')) {
            return true;
        }
        const audioStream = this.audioElement.srcObject;
        if (audioStream?.getAudioTracks().some(t => t.readyState === 'live')) {
            return true;
        }
        return false;
    }
    /**
     * Switch active video track (camera)
     */
    switchVideoTrack(trackId) {
        const trackInfo = this.videoTracks.get(trackId);
        if (!trackInfo) {
            this.log.warn(`Video track ${trackId} not found`);
            return;
        }
        this.activeVideoTrackId = trackId;
        const videoElement = this.ctx.getVideoElement();
        videoElement.srcObject = trackInfo.stream;
        videoElement.play().catch(e => {
            this.log.warn('Failed to play switched video track', e);
        });
        this.ctx.host.dispatchEvent(new CustomEvent('video-track-switched', {
            detail: {
                trackId,
                label: trackInfo.track.label
            }
        }));
    }
    // ===== SDP PROCESSING =====
    /**
     * Set video codec preferences on a transceiver: H265 > H264 > AV1 > VP9.
     * H265 offers ~40-50% better compression vs H264 at equivalent quality
     * (Chrome 136+, Safari 18+ — only advertised when HW decoder present).
     * H264 has the fastest hardware decoder init for lowest time-to-first-frame.
     * Falls back gracefully on browsers that lack setCodecPreferences.
     */
    setVideoCodecPreferences(transceiver) {
        if (typeof transceiver.setCodecPreferences !== 'function') {
            this.log.debug('setCodecPreferences not supported, relying on SDP codec ordering');
            return;
        }
        const capabilities = RTCRtpReceiver.getCapabilities?.('video');
        if (!capabilities) {
            this.log.debug('RTCRtpReceiver.getCapabilities not available');
            return;
        }
        const preferredOrder = ['video/H265', 'video/H264', 'video/AV1', 'video/VP9'];
        const sorted = [...capabilities.codecs].sort((a, b) => {
            const aIdx = preferredOrder.indexOf(a.mimeType);
            const bIdx = preferredOrder.indexOf(b.mimeType);
            const aPrio = aIdx >= 0 ? aIdx : preferredOrder.length;
            const bPrio = bIdx >= 0 ? bIdx : preferredOrder.length;
            if (aPrio !== bPrio)
                return aPrio - bPrio;
            // Within H264, prefer High profile (64xxxx) over Constrained
            // Baseline (42xxxx) — better compression for game content
            // (sharp edges, text overlays, HUD elements)
            if (a.mimeType === 'video/H264' && b.mimeType === 'video/H264') {
                const aHigh = /profile-level-id=64/.test(a.sdpFmtpLine || '') ? 0 : 1;
                const bHigh = /profile-level-id=64/.test(b.sdpFmtpLine || '') ? 0 : 1;
                return aHigh - bHigh;
            }
            return 0;
        });
        try {
            transceiver.setCodecPreferences(sorted);
            const order = sorted
                .filter(c => preferredOrder.includes(c.mimeType))
                .map(c => c.mimeType.replace('video/', ''));
            this.log.info('Video codec preferences set', { order });
        }
        catch (e) {
            this.log.warn('Failed to set codec preferences', e);
        }
    }
    mungeSDP(sdp) {
        const cfg = this.ctx.config;
        const preset = STREAM_QUALITY_PRESETS[cfg.streamQuality] ?? STREAM_QUALITY_PRESETS['quality'];
        const minBitrate = cfg.videoBitrateMin ?? preset.minBitrate;
        const startBitrate = cfg.videoBitrateStart ?? preset.startBitrate;
        const maxBitrate = cfg.videoBitrateMax ?? preset.maxBitrate;
        // Video bitrate constraints — min-bitrate ensures fast encoder ramp-up,
        // start-bitrate sets the initial target, max-bitrate caps the ceiling
        let mungedSDP = sdp.replace(/(a=fmtp:\d+ .*level-asymmetry-allowed=.*)\r\n/gm, `$1;x-google-min-bitrate=${minBitrate};x-google-start-bitrate=${startBitrate};x-google-max-bitrate=${maxBitrate}\r\n`);
        // Conference flag on the video m= section — signals the encoder to
        // prioritise low latency and produce more frequent key frames
        if (preset.conferenceFlag) {
            mungedSDP = mungedSDP.replace(/(m=video[^\r\n]*\r\n)/g, '$1a=x-google-flag:conference\r\n');
        }
        // Strip any explicit bandwidth caps (b=AS / b=TIAS) — let congestion
        // control and our fmtp bitrate hints govern the ceiling instead.
        // Hard caps can prevent the encoder from ramping up quickly.
        mungedSDP = mungedSDP.replace(/b=(AS|TIAS):[^\r\n]*\r\n/g, '');
        // Audio packet time — lower ptime reduces latency at the cost of more
        // packets. null = leave browser default (~20ms).
        if (preset.audioPtime !== null) {
            mungedSDP = mungedSDP.replace(/(m=audio[^\r\n]*\r\n)/g, `$1a=ptime:${preset.audioPtime}\r\na=maxptime:${preset.audioPtime}\r\n`);
        }
        // Configure Opus audio parameters.
        // Target the Opus fmtp line directly rather than relying on `useinbandfec=1`
        // being present — some browser/codec combinations omit it from the base SDP,
        // which would silently skip all audio parameter injection.
        const opusPayloadMatch = mungedSDP.match(/a=rtpmap:(\d+) opus\/48000/);
        if (opusPayloadMatch) {
            const opusPT = opusPayloadMatch[1];
            const fmtpRegex = new RegExp(`(a=fmtp:${opusPT} .+?)(\\r\\n)`);
            let audioParams = 'maxaveragebitrate=510000';
            if (this._useMic) {
                audioParams += ';sprop-maxcapturerate=48000';
            }
            audioParams += this._forceMonoAudio ? ';stereo=0' : ';stereo=1';
            audioParams += ';useinbandfec=1';
            if (fmtpRegex.test(mungedSDP)) {
                // Append our params to the existing fmtp line
                mungedSDP = mungedSDP.replace(fmtpRegex, `$1;${audioParams}$2`);
            }
            else {
                // No fmtp line for Opus — insert one after the rtpmap line
                mungedSDP = mungedSDP.replace(new RegExp(`(a=rtpmap:${opusPT} opus/48000/2\\r\\n)`), `$1a=fmtp:${opusPT} ${audioParams}\r\n`);
            }
        }
        return mungedSDP;
    }
    /**
     * Create WebRTC offer (when browser initiates)
     */
    async createOffer() {
        if (!this.peerConnection) {
            throw new Error('Peer connection not created');
        }
        const offer = await this.peerConnection.createOffer();
        offer.sdp = this.mungeSDP(offer.sdp);
        await this.peerConnection.setLocalDescription(offer);
        return offer;
    }
    /**
     * Handle WebRTC offer from streamer and create answer
     * This is the flow used when the streamer initiates the connection
     */
    async handleOffer(offer) {
        // Track offer received time
        if (!this.ctx.performanceMetrics.offerReceived) {
            this.ctx.performanceMetrics.offerReceived = performance.now();
            if (this.ctx.performanceMetrics.subscribeStart) {
                const timeToOffer = this.ctx.performanceMetrics.offerReceived - this.ctx.performanceMetrics.subscribeStart;
                this.log.info(`Time to receive offer: ${timeToOffer.toFixed(2)}ms`);
            }
        }
        if (!this.peerConnection) {
            this.log.debug('Peer connection not pre-created, creating now...');
            await this.createPeerConnection();
        }
        else {
            this.log.debug('Using pre-created peer connection');
        }
        // Set remote description (offer)
        await this.peerConnection.setRemoteDescription(offer);
        // Reinforce codec preferences on video transceivers before creating
        // the answer — covers cases where the remote offer introduced or
        // renegotiated transceivers after the initial createPeerConnection
        for (const t of this.peerConnection.getTransceivers()) {
            if (t.receiver?.track?.kind === 'video') {
                this.setVideoCodecPreferences(t);
            }
        }
        // Create answer
        const answer = await this.peerConnection.createAnswer();
        answer.sdp = this.mungeSDP(answer.sdp);
        await this.peerConnection.setLocalDescription(answer);
        // Track answer sent time
        this.ctx.performanceMetrics.answerSent = performance.now();
        if (this.ctx.performanceMetrics.subscribeStart) {
            const timeToAnswer = this.ctx.performanceMetrics.answerSent - this.ctx.performanceMetrics.subscribeStart;
            this.log.info(`Time to create answer: ${timeToAnswer.toFixed(2)}ms`);
        }
        this.ctx.host.dispatchEvent(new CustomEvent('answer-created', {
            detail: answer
        }));
        return answer;
    }
    /**
     * Handle WebRTC answer from server (when browser initiated offer)
     */
    async handleAnswer(answer) {
        if (!this.peerConnection) {
            throw new Error('Peer connection not created');
        }
        await this.peerConnection.setRemoteDescription(answer);
    }
    /**
     * Add ICE candidate
     */
    async addIceCandidate(candidate) {
        if (!this.peerConnection) {
            throw new Error('Peer connection not created');
        }
        await this.peerConnection.addIceCandidate(candidate);
    }
    // ===== DATA CHANNEL =====
    /**
     * Create data channel (when browser sends offer)
     */
    createDataChannel(label = 'cirrus') {
        if (!this.peerConnection) {
            throw new Error('Peer connection not created');
        }
        this.dataChannel = this.peerConnection.createDataChannel(label, {
            ordered: true
        });
        this.setupDataChannelHandlers();
    }
    /**
     * Handle data channel created by remote (when UE sends offer)
     */
    handleDataChannel(event) {
        this.dataChannel = event.channel;
        this.setupDataChannelHandlers();
    }
    /**
     * Setup data channel event handlers
     */
    setupDataChannelHandlers() {
        if (!this.dataChannel)
            return;
        this.dataChannel.binaryType = 'arraybuffer';
        this.dataChannel.onopen = () => {
            this.log.info('Data channel opened');
            this.ctx.host.dispatchEvent(new CustomEvent('data-channel-open'));
            // Prime the UE input pipeline BEFORE any UIInteraction messages.
            // UE's pixel streaming plugin (and the PureWeb fork) gates input
            // message processing on having seen RequestQualityControl (to
            // assign this peer as the controlling client) and MouseEnter (to
            // mark the viewport as focused). Without these priming messages,
            // subsequent UIInteraction messages like the PureWeb WindowResize
            // action can be silently dropped by the plugin.
            //
            // This matches the initialization sequence in pureweb/platform-sdk-js
            // InputEmitter.ts (constructor calls EmitMouseEnter + EmitRequestQualityControl)
            // and the Epic PixelStreamingInfrastructure reference client.
            this.sendToStreamer('RequestQualityControl', []);
            this.sendToStreamer('MouseEnter', []);
            // Request initial settings from UE
            this.sendToStreamer('RequestInitialSettings', []);
        };
        this.dataChannel.onclose = () => {
            this.log.info('Data channel closed');
            this.ctx.host.dispatchEvent(new CustomEvent('data-channel-close'));
        };
        this.dataChannel.onerror = (error) => {
            this.log.error('Data channel error', error);
            this.ctx.host.dispatchEvent(new CustomEvent('data-channel-error', {
                detail: error
            }));
        };
        this.dataChannel.onmessage = (event) => {
            this.handleDataChannelMessage(event);
        };
    }
    /**
     * Handle incoming data channel messages from UE
     */
    handleDataChannelMessage(event) {
        const decoded = MessageEncoder.decode(event.data);
        this.log.debug('Received from UE', { type: decoded.type, payload: decoded.payload });
        // Dispatch event for external listeners
        this.ctx.host.dispatchEvent(new CustomEvent('message-from-streamer', {
            detail: decoded
        }));
        // Handle specific message types
        switch (decoded.type) {
            case 'QualityControlOwnership':
                this.ctx.host.dispatchEvent(new CustomEvent('quality-control-ownership', {
                    detail: decoded.payload
                }));
                break;
            case 'InputControlOwnership':
                this.ctx.host.dispatchEvent(new CustomEvent('input-control-ownership', {
                    detail: decoded.payload
                }));
                break;
            case 'Response':
            case 'Command':
                this.ctx.host.dispatchEvent(new CustomEvent('ue-command-response', {
                    detail: decoded.payload
                }));
                break;
            case 'VideoEncoderAvgQP':
                this.ctx.host.dispatchEvent(new CustomEvent('video-encoder-qp', {
                    detail: decoded.payload
                }));
                break;
            case 'LatencyTest':
            case 'DataChannelLatencyTest':
                // Parse encode-pipeline timings before echoing back
                if (decoded.payload && typeof decoded.payload === 'object') {
                    this.ctx.handleLatencyTestResult(decoded.payload);
                }
                // Echo back for round-trip measurement
                this.sendToStreamer('LatencyTest', [JSON.stringify(decoded.payload)]);
                break;
            case 'InitialSettings':
                this.ctx.host.dispatchEvent(new CustomEvent('initial-settings', {
                    detail: decoded.payload
                }));
                break;
            case 'GamepadResponse':
                // Handle gamepad ID assignment from UE
                this.ctx.host.dispatchEvent(new CustomEvent('gamepad-response', {
                    detail: decoded.payload
                }));
                break;
            case 'FileExtension':
                this.ctx.handleFileExtension(event.data);
                break;
            case 'FileMimeType':
                this.ctx.handleFileMimeType(event.data);
                break;
            case 'FileContents':
                this.ctx.handleFileContents(event.data);
                break;
            case 'FreezeFrame':
                // UE is freezing the video stream
                this.ctx.host.dispatchEvent(new CustomEvent('freeze-frame', {
                    detail: decoded.payload
                }));
                break;
            case 'UnfreezeFrame':
                // UE is unfreezing the video stream
                this.ctx.host.dispatchEvent(new CustomEvent('unfreeze-frame', {
                    detail: decoded.payload
                }));
                break;
            case 'TestEcho':
                // Echo test response from UE
                this.ctx.host.dispatchEvent(new CustomEvent('test-echo', {
                    detail: decoded.payload
                }));
                break;
            case 'Protocol':
                // Protocol version negotiation
                this.ctx.host.dispatchEvent(new CustomEvent('protocol', {
                    detail: decoded.payload
                }));
                break;
        }
    }
    /**
     * Request I-frame from encoder via data channel (UE Pixel Streaming protocol).
     * Also fires an RTCP-level PLI if the encoded transform is active.
     */
    requestIFrame() {
        this.sendToStreamer('IFrameRequest', []);
        this.requestKeyFrame();
    }
    /**
     * Request a keyframe via RTCP PLI/FIR (encoded transform layer).
     * This is faster than the data-channel IFrameRequest because it operates
     * at the RTP level and doesn't require the data channel to be open.
     */
    requestKeyFrame() {
        if (this._keyframeWorker) {
            this._keyframeWorker.postMessage({ type: 'keyframe' });
        }
    }
    /**
     * Attach an Encoded Transform to a video receiver for RTCP keyframe requests.
     * Falls back gracefully — if the browser lacks RTCRtpScriptTransform, this is a no-op.
     */
    attachKeyframeTransform(receiver) {
        // Feature-detect RTCRtpScriptTransform (Chrome 125+, Firefox, Safari 18+)
        if (typeof globalThis.RTCRtpScriptTransform !== 'function') {
            this.log.debug('RTCRtpScriptTransform not available, skipping keyframe transform');
            return;
        }
        try {
            // Reuse the worker across tracks — only create once
            if (!this._keyframeWorker) {
                const blob = new Blob([WebRTCManager.KEYFRAME_WORKER_SRC], { type: 'application/javascript' });
                this._keyframeWorker = new Worker(URL.createObjectURL(blob));
            }
            receiver.transform = new globalThis.RTCRtpScriptTransform(this._keyframeWorker);
            this.log.debug('Keyframe encoded transform attached to video receiver');
            // Fire an immediate keyframe request to accelerate first-frame rendering
            this._keyframeWorker.postMessage({ type: 'keyframe' });
        }
        catch (e) {
            this.log.warn('Failed to attach keyframe transform', e);
        }
    }
    // ===== DISCONNECT CLEANUP =====
    /**
     * Disconnect media: stop all tracks, close microphone, close peer connection.
     * Does NOT handle XR or resize observer (that stays on host).
     */
    disconnectMedia() {
        // Cancel any active WebRTC resilience timers
        this._cancelDisconnectGrace();
        this.ctx.setStreamState('idle');
        const videoElement = this.ctx.getVideoElement();
        // Stop and remove video stream
        if (videoElement.srcObject) {
            const stream = videoElement.srcObject;
            stream.getTracks().forEach(track => track.stop());
            videoElement.srcObject = null;
        }
        // Stop all video tracks (multiple cameras)
        this.videoTracks.forEach(({ track }) => track.stop());
        this.videoTracks.clear();
        this.activeVideoTrackId = null;
        // Stop and remove audio stream
        if (this.audioElement.srcObject) {
            const stream = this.audioElement.srcObject;
            stream.getTracks().forEach(track => track.stop());
            this.audioElement.srcObject = null;
        }
        // Disable microphone if active
        if (this.microphoneStream) {
            this.microphoneStream.getTracks().forEach(track => track.stop());
            this.microphoneStream = null;
        }
        // Close peer connection
        this.closePeerConnection();
    }
    // ===== CONNECT METHODS =====
    /**
     * Connect to Pixel Streaming server (browser-initiated offer flow)
     * Creates peer connection and returns offer for signalling
     */
    async connect(config) {
        // Create peer connection (async to allow mic acquisition before offer)
        await this.createPeerConnection(config);
        // Create offer for signalling
        const offer = await this.createOffer();
        this.ctx.host.dispatchEvent(new CustomEvent('offer-created', {
            detail: offer
        }));
        return offer;
    }
    /**
     * Switch to a different streamer. Disconnects from current streamer
     * (if connected) and connects to new one.
     */
    async switchStreamer(config) {
        // Disconnect from current streamer
        this.disconnectMedia();
        // Small delay to ensure cleanup completes
        await new Promise(resolve => setTimeout(resolve, 100));
        // Connect to new streamer
        return await this.connect(config);
    }
    // ===== MICROPHONE =====
    /**
     * Acquire a microphone MediaStream, emitting events on failure.
     * Returns null if mic is unavailable or permission denied.
     */
    async acquireMicrophoneStream() {
        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
            this.log.warn('Microphone unavailable: mediaDevices API not supported');
            this.ctx.host.dispatchEvent(new CustomEvent('microphone-unavailable', {
                detail: { reason: 'api-not-supported' }
            }));
            return null;
        }
        const currentState = await this.ctx.checkPermission('microphone');
        if (currentState === 'denied') {
            this.log.warn('Microphone permission previously denied');
            this.ctx.host.dispatchEvent(new CustomEvent('microphone-permission-denied', {
                detail: { reason: 'previously-denied', state: currentState }
            }));
            return null;
        }
        const audioOptions = {
            autoGainControl: false,
            channelCount: 1,
            echoCancellation: false,
            // @ts-ignore - latency not in standard types yet
            latency: 0,
            noiseSuppression: false,
            sampleRate: 48000,
            sampleSize: 16
        };
        try {
            return await navigator.mediaDevices.getUserMedia({
                video: false,
                audio: audioOptions
            });
        }
        catch (error) {
            const errorName = error?.name || 'UnknownError';
            const errorMessage = error?.message || 'Unknown error';
            if (errorName === 'NotAllowedError' || errorName === 'PermissionDeniedError') {
                this.log.warn('Microphone permission denied by user');
                this.ctx.host.dispatchEvent(new CustomEvent('microphone-permission-denied', {
                    detail: { reason: 'user-denied', error: errorName, message: errorMessage }
                }));
            }
            else if (errorName === 'NotFoundError' || errorName === 'DevicesNotFoundError') {
                this.log.warn('No microphone device found');
                this.ctx.host.dispatchEvent(new CustomEvent('microphone-unavailable', {
                    detail: { reason: 'no-device', error: errorName, message: errorMessage }
                }));
            }
            else if (errorName === 'NotReadableError' || errorName === 'TrackStartError') {
                this.log.warn('Microphone not readable (may be in use by another application)');
                this.ctx.host.dispatchEvent(new CustomEvent('microphone-unavailable', {
                    detail: { reason: 'hardware-error', error: errorName, message: errorMessage }
                }));
            }
            else if (errorName === 'OverconstrainedError') {
                this.log.warn('Microphone constraints cannot be satisfied');
                this.ctx.host.dispatchEvent(new CustomEvent('microphone-error', {
                    detail: { reason: 'overconstrained', error: errorName, message: errorMessage }
                }));
            }
            else {
                this.log.error('Microphone error', error);
                this.ctx.host.dispatchEvent(new CustomEvent('microphone-error', {
                    detail: { reason: 'unknown', error: errorName, message: errorMessage }
                }));
            }
            return null;
        }
    }
    /**
     * Request microphone permission and enable if granted.
     * Does NOT throw on permission denial - instead emits events and returns false.
     *
     * NOTE: Microphone must be enabled before the WebRTC connection is
     * established (via the `use-mic` attribute or config). Mid-session
     * enabling is not supported — the transceiver direction is baked into
     * the initial SDP offer/answer.
     */
    async requestMicrophone() {
        const micStream = await this.acquireMicrophoneStream();
        if (!micStream) {
            return false;
        }
        this.microphoneStream = micStream;
        this._useMic = true;
        this.ctx.host.dispatchEvent(new CustomEvent('microphone-enabled'));
        this.ctx.watchPermission('microphone');
        return true;
    }
    /**
     * Enable microphone and send audio to UE.
     * @deprecated Use requestMicrophone() for better error handling
     */
    async enableMicrophone() {
        const success = await this.requestMicrophone();
        if (!success) {
            throw new Error('Failed to enable microphone');
        }
    }
    /**
     * Disable microphone.
     * Stops the mic tracks and cleans up local state. Since mid-session
     * renegotiation is not supported, use mute/unmute instead to
     * silence the mic without tearing down the transceiver.
     */
    disableMicrophone() {
        if (this.microphoneStream) {
            this.microphoneStream.getTracks().forEach(track => track.stop());
            this.microphoneStream = null;
        }
        this._useMic = false;
        this.ctx.host.dispatchEvent(new CustomEvent('microphone-disabled'));
    }
    /**
     * Mute/unmute microphone without removing track
     */
    setMicrophoneMuted(muted) {
        if (this.microphoneStream) {
            this.microphoneStream.getAudioTracks().forEach(track => {
                track.enabled = !muted;
            });
            this.ctx.host.dispatchEvent(new CustomEvent('microphone-muted-change', {
                detail: muted
            }));
        }
    }
    /**
     * Check if microphone is currently enabled
     */
    get isMicrophoneEnabled() {
        return this._useMic && this.microphoneStream !== null;
    }
    /**
     * Check if microphone is currently muted
     */
    get isMicrophoneMuted() {
        if (!this.microphoneStream)
            return true;
        const tracks = this.microphoneStream.getAudioTracks();
        return tracks.length === 0 || !tracks[0].enabled;
    }
    // ===== PUBLIC SEND HELPERS =====
    /**
     * Send command to UE
     */
    sendCommand(command) {
        this.sendToStreamer('Command', [JSON.stringify(command)]);
    }
    /**
     * Send UI interaction to UE
     */
    sendUIInteraction(descriptor) {
        this.sendToStreamer('UIInteraction', [JSON.stringify(descriptor)]);
    }
}
// ===== WEBRTC RESILIENCE POLICY CONSTANTS =====
// Note: DISCONNECT_GRACE_MS is now configurable via ctx.config.disconnectGraceMs
WebRTCManager.DISCONNECT_POLL_INTERVAL_MS = 3000; // RTP stats poll frequency during grace
WebRTCManager.RECOVERY_MIN_INTERVAL_MS = 10000; // Min gap between recovery attempts
WebRTCManager.RECOVERY_WINDOW_MS = 60000; // Sliding window for thrash detection
WebRTCManager.MAX_RECOVERY_IN_WINDOW = 3; // Max recoveries per window before fatal
WebRTCManager.RECOVERY_WATCHDOG_MS = 30000; // Hard deadline from recovery start to back-to-streaming
// ICE restart constants
WebRTCManager.ICE_RESTART_TIMEOUT_MS = 5000; // Timeout for ICE restart success
WebRTCManager.ICE_RESTART_MAX_ATTEMPTS = 2; // Max ICE restarts before full teardown
// TURN health check constants
WebRTCManager.TURN_HEALTH_CHECK_TIMEOUT_MS = 3000; // Timeout for TURN health check
WebRTCManager.TURN_HEALTH_CHECK_CACHE_MS = 30000; // Cache health result for 30s
// Inline worker source for the encoded transform passthrough.
// The worker pipes frames through unchanged but stores a reference to
// the RTCRtpScriptTransformer so we can call sendKeyFrameRequest().
WebRTCManager.KEYFRAME_WORKER_SRC = `
        let transformer = null;
        self.onrtctransform = (event) => {
            transformer = event.transformer;
            // Passthrough: pipe readable straight to writable (zero-copy)
            transformer.readable.pipeTo(transformer.writable);
        };
        self.onmessage = (event) => {
            if (event.data.type === 'keyframe' && transformer) {
                transformer.sendKeyFrameRequest().catch(() => {});
            }
        };
    `;

/**
 * Session Manager
 *
 * Handles the session WebSocket lifecycle:
 * - Token exchange (admission token → access token)
 * - WebSocket connection, reconnection, keepalive, heartbeat
 * - Message routing and signaling dispatch
 * - Streamer subscription management
 */
/**
 * Peek at a JWT payload without verifying the signature.
 * Returns true if the token's `typ`/`token_type` claim indicates an invite token.
 */
function isInviteToken(jwt) {
    try {
        const payload = JSON.parse(atob(jwt.split('.')[1]));
        const typ = payload['typ'] ?? payload['token_type'];
        return typ === 'inv' || typ === 'invite';
    }
    catch {
        return false;
    }
}
/** Classifies HTTP errors from the token exchange endpoint. */
function classifyTokenError(status, body) {
    if (status === 401) {
        return /expired/i.test(body) ? 'token_expired' : 'unauthorized';
    }
    if (status >= 500)
        return 'server_error';
    return 'request_failed';
}
/** Typed session error with a machine-readable code for consumer classification. */
class SessionError extends Error {
    constructor(message, code, statusCode) {
        super(message);
        this.code = code;
        this.statusCode = statusCode;
        this.name = 'SessionError';
    }
}
class SessionManager {
    constructor(ctx, log) {
        this.ctx = ctx;
        this.log = log;
        // WebSocket state
        this._ws = null;
        this._state = 'idle';
        this._heartbeatInterval = null;
        this._lastHeartbeatAck = 0;
        this._keepaliveInterval = null;
        this._reconnectTimeout = null;
        this._reconnectAttempts = 0;
        this._reconnectTimestamps = [];
        // Single-flight guard for `session-ended`. Multiple cleanup paths converge
        // (server's session-ended message → terminateSession → and a host's
        // own stop() call from a React effect cleanup, e.g. StrictMode's
        // intentional mount/unmount). Each historically dispatched its own
        // session-ended event — the first with populated detail, subsequent
        // ones with null payload because state had already been reset. Hosts
        // shouldn't have to dedupe; the SDK emits exactly once per session.
        // Reset on startSession.
        this._sessionEndedDispatched = false;
        this._startAbort = null;
        // Session identity (populated by token exchange)
        this._accessToken = null;
        this._tokenExpiresAt = null;
        this._sessionId = null;
        this._agentId = null;
        this._iceServers = [];
        // Collaborator flag — set when an invite token is detected during token exchange
        this._isCollaborator = false;
        // Streamer tracking
        this._subscribedStreamerId = null;
        this._availableStreamers = new Map();
    }
    // ===== PUBLIC GETTERS =====
    get ws() { return this._ws; }
    get state() { return this._state; }
    get accessToken() { return this._accessToken; }
    get sessionId() { return this._sessionId; }
    get agentId() { return this._agentId; }
    get iceServers() { return this._iceServers; }
    get subscribedStreamerId() { return this._subscribedStreamerId; }
    get isCollaborator() { return this._isCollaborator; }
    get peerAgentId() { return this._peerAgentId; }
    get availableStreamers() {
        return Array.from(this._availableStreamers.keys());
    }
    // ===== SESSION LIFECYCLE =====
    /**
     * Start a session: exchange admission token, pre-create peer connection,
     * and connect the session WebSocket.
     */
    async startSession() {
        const config = this.ctx.config;
        if (!config.admissionToken) {
            const error = new Error('admission-token is required to start a session');
            this.ctx.host.dispatchEvent(new CustomEvent('session-error', {
                detail: { error: error.message, phase: 'validation' }
            }));
            throw error;
        }
        if (this._state !== 'idle' && this._state !== 'error') {
            this.log.info(`Already in state '${this._state}', ignoring startSession call`);
            return;
        }
        // User-initiated start (including retry after `error`). Clear the
        // reconnect sliding window so prior attempts don't immediately trip
        // the cap on a fresh play() call.
        this._reconnectAttempts = 0;
        this._reconnectTimestamps = [];
        // Re-arm the session-ended single-flight guard so the upcoming
        // session's own terminal event can fire.
        this._sessionEndedDispatched = false;
        // Track session start time
        this.ctx.performanceMetrics.sessionStarted = performance.now();
        this.ctx.setSessionState('connecting');
        // Create AbortController so leaveSession() can cancel in-flight operations
        this._startAbort?.abort();
        this._startAbort = new AbortController();
        const startSignal = this._startAbort.signal;
        try {
            // Step 1: Exchange admission token for access token
            this.log.info('Exchanging admission token for access token...');
            this.ctx.performanceMetrics.tokenExchangeStart = performance.now();
            const tokenResponse = await this.exchangeToken(startSignal);
            this._accessToken = tokenResponse.accessToken;
            this._tokenExpiresAt = tokenResponse.expiresAt;
            this._sessionId = tokenResponse.sessionId;
            this._agentId = tokenResponse.agentId;
            this._iceServers = tokenResponse.iceServers || [];
            this.ctx.performanceMetrics.tokenExchangeComplete = performance.now();
            const tokenDuration = this.ctx.performanceMetrics.tokenExchangeComplete - this.ctx.performanceMetrics.tokenExchangeStart;
            this.log.info(`Token exchange successful in ${tokenDuration.toFixed(2)}ms. Session: ${this._sessionId}, Agent: ${this._agentId}`);
            this.log.debug(`Received ${this._iceServers.length} ICE server(s)`, this._iceServers);
            this.ctx.host.dispatchEvent(new CustomEvent('session-token-exchanged', {
                detail: {
                    sessionId: this._sessionId,
                    agentId: this._agentId,
                    iceServers: this._iceServers
                }
            }));
            // If swift-job-request is enabled, the job was submitted during token exchange.
            if (config.swiftJobRequest && !this._isCollaborator) {
                this.ctx.performanceMetrics.jobRequested = this.ctx.performanceMetrics.tokenExchangeStart;
                this.ctx.setJobState('pending');
            }
            // Collaborators join an existing session — assume a job is already in flight
            // until session-ready confirms whether a worker is present.
            if (this._isCollaborator) {
                this.ctx.setJobState('pending');
            }
            // Step 2: Pre-create peer connection with ICE servers for faster WebRTC setup
            // Awaited because createPeerConnection may acquire the microphone.
            if (this._iceServers.length > 0) {
                await this.ctx.preCreatePeerConnection({ iceServers: this._iceServers });
            }
            // Step 3: Connect WebSocket for signalling (with retry for transient failures)
            this.ctx.performanceMetrics.sessionWsConnectStart = performance.now();
            const maxWsRetries = 3;
            for (let wsAttempt = 0; wsAttempt < maxWsRetries; wsAttempt++) {
                if (startSignal.aborted)
                    throw new Error('Session start aborted');
                try {
                    await this.connectWs();
                    break;
                }
                catch (wsError) {
                    if (startSignal.aborted)
                        throw new Error('Session start aborted');
                    if (wsAttempt < maxWsRetries - 1) {
                        const delay = 500 * (wsAttempt + 1);
                        this.log.warn(`WebSocket connection failed (attempt ${wsAttempt + 1}/${maxWsRetries}), retrying in ${delay}ms...`);
                        await new Promise(r => setTimeout(r, delay));
                    }
                    else {
                        throw wsError;
                    }
                }
            }
        }
        catch (error) {
            this.log.error('Failed to start session:', error);
            const failureReason = error instanceof SessionError ? error.code : 'connection_failed';
            this.ctx.setSessionState('error');
            this.ctx.setStatusFailed(failureReason);
            this.ctx.host.dispatchEvent(new CustomEvent('session-error', {
                detail: {
                    error: error instanceof Error ? error.message : String(error),
                    code: error instanceof SessionError ? error.code : undefined,
                    phase: 'connection'
                }
            }));
            throw error;
        }
    }
    /**
     * Leave the session with an optional reason.
     * Reports disconnect reason to transcript before closing.
     */
    leaveSession(reason) {
        // Abort any in-flight startSession operations (token exchange, WS retries)
        if (this._startAbort) {
            this._startAbort.abort();
            this._startAbort = null;
        }
        if (this._state === 'idle' || !this._ws) {
            this.terminateSession();
            return;
        }
        this.log.info(`Client-initiated disconnect${reason ? `: ${reason}` : ''}`);
        this.sendMessage({
            type: 'report-event',
            event_type: 'client.disconnect',
            data: reason ? { reason } : undefined,
        });
        this.sendMessage({ type: 'leave-session' });
        this.terminateSession(reason);
    }
    /**
     * End the session — close WebSocket and clean up all state.
     * @param reason Optional reason conveyed in the WebSocket close frame.
     */
    terminateSession(reason) {
        this.log.info('Ending session');
        // Dispatch the terminal session-ended event BEFORE wiping identity,
        // so the payload's sessionId/agentId are still populated. The guard
        // makes this a no-op when the server's session-ended message handler
        // has already dispatched. If we're here from a clean shutdown path
        // (e.g. user clicked Leave) with no prior dispatch, this is the
        // one-and-only session-ended event for this session.
        this._dispatchSessionEnded({ reason: reason ?? 'terminated' });
        this._state = 'idle';
        this.ctx.setSessionState('idle');
        this.ctx.updateDebugIndicator();
        // Clear timers
        this.stopKeepalive();
        this.stopHeartbeat();
        if (this._reconnectTimeout) {
            clearTimeout(this._reconnectTimeout);
            this._reconnectTimeout = null;
        }
        // Clear WebRTC resilience state
        // (WebRTCManager owns resilience; host calls webrtc.resetResilienceState())
        this._subscribedStreamerId = null;
        this._availableStreamers.clear();
        // Close WebSocket
        if (this._ws) {
            this._ws.onclose = null; // Prevent reconnection handling
            this._ws.close(1000, reason || 'client-terminated');
            this._ws = null;
        }
        // Abort any in-flight startSession operations
        if (this._startAbort) {
            this._startAbort.abort();
            this._startAbort = null;
        }
        // Reset identity (now safe — session-ended already dispatched with
        // these values).
        this._accessToken = null;
        this._tokenExpiresAt = null;
        this._sessionId = null;
        this._agentId = null;
        this._reconnectAttempts = 0;
        this._reconnectTimestamps = [];
        this.ctx.setJobState('unsubmitted');
        this.ctx.setAutoplayBlocked(false);
    }
    /**
     * Single-flight `session-ended` dispatch. Captures sessionId/agentId
     * from current state, so callers should invoke BEFORE wiping identity
     * if they want those populated. Subsequent calls within the same
     * session are no-ops; the flag re-arms on startSession.
     */
    _dispatchSessionEnded(detail) {
        if (this._sessionEndedDispatched) {
            this.log.debug(`Suppressing duplicate session-ended (reason=${detail.reason})`);
            return;
        }
        this._sessionEndedDispatched = true;
        this.ctx.host.dispatchEvent(new CustomEvent('session-ended', {
            detail: {
                reason: detail.reason,
                sessionId: this._sessionId,
                agentId: this._agentId,
            },
        }));
    }
    // ===== MESSAGE SEND =====
    /**
     * Request a session invite token from the server.
     * Only the session owner (browser-agent) may invoke this.
     * The response arrives as an 'invite-token' event on the host element.
     */
    requestInvite() {
        if (!this._ws || this._ws.readyState !== WebSocket.OPEN) {
            this.log.warn('Cannot request invite — WebSocket not connected');
            return;
        }
        if (this._isCollaborator) {
            this.log.warn('session-collaborator role cannot request invite tokens');
            return;
        }
        this.log.info('Requesting session invite token...');
        this.sendMessage({ type: 'request-invite' });
    }
    /**
     * Send a JSON message through the session WebSocket.
     */
    sendMessage(message) {
        if (!this._ws || this._ws.readyState !== WebSocket.OPEN) {
            this.log.warn('Cannot send message - WebSocket not connected');
            return;
        }
        this._ws.send(JSON.stringify(message));
    }
    // ===== STREAMER SUBSCRIPTION =====
    /**
     * Subscribe to a specific streamer via presence update.
     * If the streamer's offer_mode is "browser", initiates WebRTC offer from browser.
     */
    async subscribeToStreamer(streamerId) {
        this.log.info(`Subscribing to streamer: ${streamerId}`);
        this._subscribedStreamerId = streamerId;
        this.ctx.resetPerformanceMetrics();
        // Resolve the contributor's agent_id from the contribution metadata.
        // This is used as target_agent_id on all outgoing WebRTC signals so that
        // SessionDO routes them directly to the worker rather than broadcasting.
        const streamerInfo = this._availableStreamers.get(streamerId);
        const offerMode = streamerInfo?.metadata?.offer_mode;
        if (streamerInfo?.contributorAgentId) {
            this._peerAgentId = streamerInfo.contributorAgentId;
        }
        this.log.info(`Streamer ${streamerId} offer_mode: ${offerMode || 'not specified (defaulting to streamer)'} peer=${this._peerAgentId ?? 'unknown'}`);
        // Send presence update to notify worker of our subscription
        this.sendMessage({
            type: 'update-presence',
            preferred_streamer_id: streamerId,
        });
        // If offer_mode is "browser", we need to create and send the offer
        if (offerMode === 'browser') {
            this.log.info('Browser-initiated offer mode: creating WebRTC offer');
            try {
                // Ensure peer connection exists (async for mic acquisition)
                if (!this.ctx.getPeerConnection()) {
                    await this.ctx.preCreatePeerConnection({ iceServers: this._iceServers });
                }
                // Create data channel (required for browser-initiated flow)
                this.ctx.createDataChannel('cirrus');
                // Create and send offer — targeted directly to the contributor agent
                const offer = await this.ctx.createOffer();
                this.ctx.setNegotiatingStarted();
                this.sendMessage({
                    type: 'webrtc-signaling',
                    signal_type: 'offer',
                    payload: { sdp: offer.sdp },
                    ...(this._peerAgentId && { target_agent_id: this._peerAgentId })
                });
                this.log.info(`Browser-initiated offer sent to ${this._peerAgentId ?? 'broadcast'}`);
            }
            catch (error) {
                this.log.error('Failed to create browser-initiated offer:', error);
                this.ctx.host.dispatchEvent(new CustomEvent('session-error', {
                    detail: {
                        error: error instanceof Error ? error.message : String(error),
                        phase: 'webrtc-offer-creation'
                    }
                }));
            }
        }
        // If offer_mode is "streamer" or not specified, wait for offer from worker
    }
    /**
     * Evaluate whether to subscribe (or re-subscribe) to a streamer.
     */
    evaluateSubscription() {
        if (!this._ws || this._ws.readyState !== WebSocket.OPEN)
            return;
        if (this._availableStreamers.size === 0)
            return;
        const desiredId = this.ctx.config.streamerId;
        let targetId = null;
        if (!desiredId || desiredId === 'default') {
            targetId = this._availableStreamers.keys().next().value ?? null;
        }
        else {
            if (this._availableStreamers.has(desiredId)) {
                targetId = desiredId;
            }
        }
        if (targetId && targetId !== this._subscribedStreamerId) {
            this.subscribeToStreamer(targetId);
        }
    }
    // ===== TOKEN EXCHANGE =====
    /**
     * Exchange admission token for access token via the token endpoint.
     */
    async exchangeToken(externalSignal) {
        const config = this.ctx.config;
        const tokenUrl = `https://${config.apiEndpoint}/agent/token`;
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 15000);
        // If an external signal is provided, link it to our internal controller
        const onExternalAbort = () => controller.abort();
        externalSignal?.addEventListener('abort', onExternalAbort);
        // Detect invite tokens (session-collaborator flow) by peeking at the JWT payload.
        // Invite tokens are exchanged via `invite_token` rather than `assertion`.
        const inviteToken = isInviteToken(config.admissionToken);
        this._isCollaborator = inviteToken;
        const requestBody = inviteToken
            ? {
                grant_type: 'urn:ietf:params:oauth:grant-type:jwt-bearer',
                invite_token: config.admissionToken,
                include_ice_servers: true,
            }
            : {
                grant_type: 'urn:ietf:params:oauth:grant-type:jwt-bearer',
                assertion: config.admissionToken,
                agent_role: 'browser-agent',
                include_ice_servers: true,
                ...(config.swiftJobRequest && { swift_job_request: true }),
                ...(config.preflightDeploymentCheck && { preflight: true }),
                preferred_streamer_id: config.streamerId || 'default',
                ...(config.lat != null && { lat: config.lat }),
                ...(config.lng != null && { lng: config.lng }),
                ...((config.queueWaitTolerance != null || config.webrtcNegotiationTolerance != null) && {
                    tolerances: {
                        ...(config.queueWaitTolerance != null && { queue_wait_tolerance_seconds: config.queueWaitTolerance }),
                        ...(config.webrtcNegotiationTolerance != null && { webrtc_negotiation_tolerance_seconds: config.webrtcNegotiationTolerance }),
                    },
                }),
                ...(config.appId && {
                    application_request: {
                        app_id: config.appId,
                        ...(config.appVersion && { version_id: config.appVersion }),
                    },
                }),
            };
        let response;
        try {
            response = await fetch(tokenUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                signal: controller.signal,
                body: JSON.stringify(requestBody),
            });
        }
        finally {
            clearTimeout(timeout);
            externalSignal?.removeEventListener('abort', onExternalAbort);
        }
        if (!response.ok) {
            const errorText = await response.text().catch(() => 'Unknown error');
            const code = classifyTokenError(response.status, errorText);
            throw new SessionError(`Token exchange failed (${response.status}): ${errorText}`, code, response.status);
        }
        const data = await response.json();
        if (!data.ice_servers || data.ice_servers.length === 0) {
            this.log.warn('No ICE servers received from gateway');
        }
        return {
            accessToken: data.access_token,
            expiresAt: Date.now() + (data.expires_in * 1000),
            sessionId: data.session_id,
            agentId: data.agent_id,
            iceServers: data.ice_servers || [],
        };
    }
    // ===== WEBSOCKET LIFECYCLE =====
    /**
     * Connect the session WebSocket.
     */
    async connectWs() {
        if (!this._accessToken) {
            throw new Error('No access token available for WebSocket connection');
        }
        this.ctx.setSessionState('authenticating');
        const wsUrl = `wss://${this.ctx.config.apiEndpoint}/ws/session`;
        this.log.info(`Connecting WebSocket to ${this.ctx.config.apiEndpoint}...`);
        return new Promise((resolve, reject) => {
            try {
                this._ws = new WebSocket(wsUrl, [`Bearer.${this._accessToken}`]);
                let settled = false;
                const connectionTimeout = setTimeout(() => {
                    if (this._ws && this._ws.readyState === WebSocket.CONNECTING) {
                        this._ws.close();
                        if (!settled) {
                            settled = true;
                            reject(new Error('WebSocket connection timeout'));
                        }
                    }
                }, 10000);
                this._ws.onopen = () => {
                    clearTimeout(connectionTimeout);
                    settled = true;
                    this.ctx.performanceMetrics.sessionWsConnectComplete = performance.now();
                    if (this.ctx.performanceMetrics.sessionWsConnectStart) {
                        const wsDuration = this.ctx.performanceMetrics.sessionWsConnectComplete - this.ctx.performanceMetrics.sessionWsConnectStart;
                        this.log.info(`WebSocket connected in ${wsDuration.toFixed(2)}ms`);
                    }
                    else {
                        this.log.info('WebSocket connected');
                    }
                    // NOTE: `_reconnectAttempts` is intentionally NOT reset here.
                    // Bare WS open is not proof of a successful session attach —
                    // a server that accepts the handshake then immediately closes
                    // would loop forever otherwise. The counter is reset only on
                    // `session-ready` (see handleMessage). Sliding-window cap in
                    // handleWsClose backstops cases where session-ready never fires.
                    this._state = 'connected';
                    this.ctx.setSessionState('connected');
                    this.startKeepalive();
                    this.startHeartbeat();
                    this.ctx.host.dispatchEvent(new CustomEvent('session-connected', {
                        detail: {
                            sessionId: this._sessionId,
                            agentId: this._agentId
                        }
                    }));
                    resolve();
                };
                this._ws.onmessage = (event) => {
                    this.handleMessage(event.data);
                };
                this._ws.onclose = (event) => {
                    clearTimeout(connectionTimeout);
                    if (!settled) {
                        settled = true;
                        reject(new Error(`WebSocket upgrade failed (code=${event.code})`));
                        return;
                    }
                    this.handleWsClose(event);
                };
                this._ws.onerror = (event) => {
                    this.log.error('WebSocket error:', event);
                };
            }
            catch (error) {
                reject(error);
            }
        });
    }
    // ===== MESSAGE HANDLING =====
    /**
     * Handle incoming WebSocket messages (signalling protocol).
     */
    handleMessage(data) {
        // Filter raw "pong" auto-responses from Cloudflare edge keepalive
        if (data === 'pong')
            return;
        let message;
        try {
            message = JSON.parse(data);
        }
        catch {
            this.log.error('Failed to parse WebSocket message:', data);
            return;
        }
        this.log.debug(`Received message: ${message.type}`);
        switch (message.type) {
            case 'offer':
                this.handleSignallingOffer(message);
                break;
            case 'iceCandidate':
                this.handleSignallingIceCandidate(message);
                break;
            case 'streamer-list':
                this.ctx.host.dispatchEvent(new CustomEvent('streamer-list', {
                    detail: { streamers: message.streamers }
                }));
                break;
            case 'streamer-joined':
                this.ctx.cancelRendezvousTimer();
                this.ctx.host.dispatchEvent(new CustomEvent('streamer-joined', {
                    detail: { streamerId: message.streamerId }
                }));
                break;
            case 'streamer-left':
                this.ctx.host.dispatchEvent(new CustomEvent('streamer-left', {
                    detail: { streamerId: message.streamerId }
                }));
                break;
            case 'pong':
            case 'heartbeat':
                break;
            case 'error': {
                const errMsg = message;
                this.log.error('Server error:', message);
                this.ctx.host.dispatchEvent(new CustomEvent('session-error', {
                    detail: {
                        error: errMsg.message || 'Unknown server error',
                        code: errMsg.code,
                        details: errMsg.details,
                        phase: 'signalling'
                    }
                }));
                // Terminal, session-level failures routed through setStatusFailed
                // so the overlay can surface a specific amber/red state and stop
                // spending queueWait / rendezvous budget. Add codes here as the
                // server grows the typed-failure vocabulary.
                if (errMsg.code === 'no_deployment') {
                    this.ctx.setStatusFailed('no_deployment');
                }
                break;
            }
            case 'webrtc-signaling': {
                const sigMsg = message;
                this.log.info(`WebRTC signaling: ${sigMsg.signal_type} from ${sigMsg.from_agent_id}`);
                switch (sigMsg.signal_type) {
                    case 'offer': {
                        // Guard: if the peer connection is already established, this offer is
                        // intended for a different participant (e.g., a collaborator joining an
                        // in-progress session). The worker broadcasts offers when it can't target
                        // a specific agent. ICE restarts in this codebase are always self-initiated
                        // (browser sends the offer), so the streamer never sends a new offer to
                        // an already-connected peer.
                        const existingPc = this.ctx.getPeerConnection();
                        if (existingPc && existingPc.connectionState === 'connected') {
                            this.log.info(`Ignoring offer from ${sigMsg.from_agent_id} — already connected (offer is for another participant)`);
                            break;
                        }
                        this._peerAgentId = sigMsg.from_agent_id;
                        this.ctx.setNegotiatingStarted();
                        this.handleSignallingOffer({ type: 'offer', sdp: sigMsg.payload.sdp });
                        break;
                    }
                    case 'iceCandidate':
                        this.handleSignallingIceCandidate({
                            type: 'iceCandidate',
                            candidate: sigMsg.payload.candidate,
                            sdpMid: sigMsg.payload.sdpMid,
                            sdpMLineIndex: sigMsg.payload.sdpMLineIndex
                        });
                        break;
                    case 'answer':
                        this._peerAgentId = sigMsg.from_agent_id;
                        this.handleSignallingAnswer({ type: 'answer', sdp: sigMsg.payload.sdp });
                        break;
                }
                break;
            }
            case 'session-ready': {
                const readyMsg = message;
                this.ctx.performanceMetrics.sessionReady = performance.now();
                const participantSummary = readyMsg.participants?.map(p => `${p.agent_id}(${p.agent_role})`).join(', ') || 'none';
                if (this.ctx.performanceMetrics.sessionStarted) {
                    const readyDuration = this.ctx.performanceMetrics.sessionReady - this.ctx.performanceMetrics.sessionStarted;
                    this.log.info(`Session ready in ${readyDuration.toFixed(2)}ms: ${readyMsg.session_id}, participants: ${participantSummary}`);
                }
                else {
                    this.log.info(`Session ready: ${readyMsg.session_id}, participants: ${participantSummary}`);
                }
                this._sessionId = readyMsg.session_id;
                this._agentId = readyMsg.agent_id;
                // Server has confirmed our session is attached. Safe to reset
                // the linear reconnect-attempt counter — this is the application-
                // level success signal that bare ws.onopen used to substitute
                // for (incorrectly: a handshake-then-close server would loop).
                // The sliding-window timestamps are NOT cleared here — they age
                // out naturally over RECONNECT_WINDOW_MS and trip the cap if
                // we're churning even when each cycle reaches session-ready.
                this._reconnectAttempts = 0;
                this.ctx.host.dispatchEvent(new CustomEvent('session-ready', {
                    detail: {
                        sessionId: readyMsg.session_id,
                        agentId: readyMsg.agent_id,
                        projectId: readyMsg.project_id,
                        appId: readyMsg.app_id,
                        versionId: readyMsg.version_id,
                        participants: readyMsg.participants
                    }
                }));
                if (readyMsg.participants?.some(p => p.agent_role === 'worker-agent')) {
                    this.ctx.setJobState('fulfilled');
                }
                this.ctx.reportTiming('session-ready');
                if (this.ctx.config.streamerId && !this._subscribedStreamerId) {
                    this._subscribedStreamerId = this.ctx.config.streamerId === 'default' ? null : this.ctx.config.streamerId;
                }
                break;
            }
            case 'participant-joined': {
                const joinMsg = message;
                this.log.info(`Participant joined: ${joinMsg.agent_id} (${joinMsg.agent_role})`);
                this.ctx.host.dispatchEvent(new CustomEvent('participant-joined', {
                    detail: { agentId: joinMsg.agent_id, agentRole: joinMsg.agent_role, metadata: joinMsg.metadata }
                }));
                if (joinMsg.agent_role === 'worker-agent') {
                    this.ctx.setJobState('fulfilled');
                }
                break;
            }
            case 'participant-left': {
                const leftMsg = message;
                this.log.info(`Participant left: ${leftMsg.agent_id} (${leftMsg.agent_role}), reason: ${leftMsg.reason || 'none'}`);
                if (leftMsg.agent_role === 'worker-agent') {
                    // Clear peer routing if this was our streaming worker
                    if (this._peerAgentId === leftMsg.agent_id) {
                        this._peerAgentId = undefined;
                    }
                    // Revert job state — the worker is gone. WebRTC is torn down
                    // separately via 'contribution-unavailable' for the subscribed
                    // streamer, which arrives before this message and honours any
                    // server-side grace/linger window.
                    this.ctx.setJobState('pending');
                }
                this.ctx.host.dispatchEvent(new CustomEvent('participant-left', {
                    detail: { agentId: leftMsg.agent_id, agentRole: leftMsg.agent_role, reason: leftMsg.reason }
                }));
                break;
            }
            case 'session-ended': {
                const endMsg = message;
                this.log.info(`Session ended by server: ${endMsg.reason || 'unknown reason'}`);
                // Set meta-status before teardown so consumers see failed/ended
                const timeoutReasons = ['queue_wait_timeout', 'rendezvous_timeout', 'webrtc_negotiation_timeout'];
                if (endMsg.reason && timeoutReasons.includes(endMsg.reason)) {
                    this.ctx.setStatusFailed(endMsg.reason);
                }
                else {
                    this.ctx.setStatusEnded();
                }
                // Dispatch via the single-flight helper BEFORE terminateSession
                // so sessionId/agentId in the payload are still populated.
                // terminateSession will be a no-op for dispatch (guard set).
                this._dispatchSessionEnded({ reason: endMsg.reason ?? 'server-terminated' });
                this.terminateSession();
                break;
            }
            case 'heartbeat-ack':
                this._lastHeartbeatAck = Date.now();
                break;
            case 'job-requested': {
                const jobMsg = message;
                this.log.info(`Job requested: ${jobMsg.job_id}`);
                this.ctx.setJobState('pending');
                this.ctx.host.dispatchEvent(new CustomEvent('job-requested', {
                    detail: { jobId: jobMsg.job_id }
                }));
                break;
            }
            case 'job-cancelled': {
                const cancelMsg = message;
                this.log.info(`Job cancelled: success=${cancelMsg.success}, reason=${cancelMsg.reason || 'none'}`);
                if (cancelMsg.success) {
                    this.ctx.setJobState('unsubmitted');
                }
                this.ctx.host.dispatchEvent(new CustomEvent('job-cancelled', {
                    detail: { jobId: cancelMsg.job_id, success: cancelMsg.success, reason: cancelMsg.reason }
                }));
                break;
            }
            case 'invite-token': {
                const inviteMsg = message;
                this.log.info('Received invite token from session');
                this.ctx.host.dispatchEvent(new CustomEvent('invite-token', {
                    detail: { token: inviteMsg.token }
                }));
                break;
            }
            case 'contribution-available': {
                const contrib = message;
                if (contrib.contribution_type === 'pixel-streaming') {
                    this.ctx.setRendezvoused();
                    this._availableStreamers.set(contrib.contribution_id, {
                        contributorAgentId: contrib.contributor_agent_id,
                        metadata: contrib.metadata,
                    });
                    this.ctx.host.dispatchEvent(new CustomEvent('streamer-available', {
                        detail: {
                            streamerId: contrib.contribution_id,
                            contributorAgentId: contrib.contributor_agent_id,
                            metadata: contrib.metadata,
                        }
                    }));
                    this.ctx.host.dispatchEvent(new CustomEvent('streamer-list-changed', {
                        detail: { streamers: this.availableStreamers }
                    }));
                    this.evaluateSubscription();
                }
                break;
            }
            case 'contribution-unavailable': {
                const unavail = message;
                if (this._availableStreamers.has(unavail.contribution_id)) {
                    this._availableStreamers.delete(unavail.contribution_id);
                    this.ctx.host.dispatchEvent(new CustomEvent('streamer-unavailable', {
                        detail: { streamerId: unavail.contribution_id }
                    }));
                    this.ctx.host.dispatchEvent(new CustomEvent('streamer-list-changed', {
                        detail: { streamers: this.availableStreamers }
                    }));
                    if (this._subscribedStreamerId === unavail.contribution_id) {
                        this._subscribedStreamerId = null;
                        // The stream we were watching is gone — tear down WebRTC
                        // immediately rather than waiting 15-30s for ICE failure.
                        this.ctx.onWorkerLeft();
                        this.evaluateSubscription();
                    }
                }
                break;
            }
            case 'presence-changed':
                // Server echoes our own presence update back; no client action needed.
                break;
            default:
                this.log.debug(`Unknown message type: ${message.type}`);
                this.ctx.host.dispatchEvent(new CustomEvent('session-message', {
                    detail: message
                }));
        }
    }
    // ===== SIGNALING HANDLERS =====
    /**
     * Handle SDP offer from streamer — create answer and send back.
     */
    async handleSignallingOffer(message) {
        const gen = this.ctx.getPcGeneration();
        try {
            this.log.info(`Processing SDP offer from streamer (gen=${gen})...`);
            const answer = await this.ctx.handleOffer({ type: 'offer', sdp: message.sdp });
            if (this.ctx.getPcGeneration() !== gen) {
                this.log.warn(`Discarding stale answer (gen=${gen}, current=${this.ctx.getPcGeneration()})`);
                return;
            }
            this.sendMessage({
                type: 'webrtc-signaling',
                signal_type: 'answer',
                payload: { sdp: answer.sdp },
                ...(this._peerAgentId && { target_agent_id: this._peerAgentId })
            });
            this.log.info(`Sent SDP answer (gen=${gen}) target=${this._peerAgentId ?? '*'}`);
            // Tell SessionDO the SDP phase is complete so the 25s SDP timer
            // can hand off to the 90s first-RTP timer. Best-effort; v1
            // servers ignore unknown report-events harmlessly.
            this.sendMessage({
                type: 'report-event',
                event_type: 'webrtc.sdp_applied',
            });
        }
        catch (error) {
            if (this.ctx.getPcGeneration() !== gen) {
                this.log.debug(`Ignoring offer error from stale gen=${gen}`);
                return;
            }
            this.log.error('Failed to handle offer:', error);
            this.ctx.host.dispatchEvent(new CustomEvent('session-error', {
                detail: {
                    error: error instanceof Error ? error.message : String(error),
                    phase: 'webrtc-offer'
                }
            }));
        }
    }
    /**
     * Handle SDP answer from streamer (browser-initiated offer flow).
     */
    async handleSignallingAnswer(message) {
        const gen = this.ctx.getPcGeneration();
        try {
            this.log.info(`Processing SDP answer from streamer (gen=${gen})...`);
            await this.ctx.handleAnswer({ type: 'answer', sdp: message.sdp });
            if (this.ctx.getPcGeneration() !== gen) {
                this.log.warn(`Answer processed but gen changed (gen=${gen}, current=${this.ctx.getPcGeneration()})`);
                return;
            }
            this.log.info(`SDP answer processed successfully (gen=${gen})`);
            // Tell SessionDO the SDP phase is complete. Mirrors the offer-receive path.
            this.sendMessage({
                type: 'report-event',
                event_type: 'webrtc.sdp_applied',
            });
        }
        catch (error) {
            if (this.ctx.getPcGeneration() !== gen) {
                this.log.debug(`Ignoring answer error from stale gen=${gen}`);
                return;
            }
            this.log.error('Failed to handle answer:', error);
            this.ctx.host.dispatchEvent(new CustomEvent('session-error', {
                detail: {
                    error: error instanceof Error ? error.message : String(error),
                    phase: 'webrtc-answer'
                }
            }));
        }
    }
    /**
     * Handle ICE candidate from streamer.
     */
    async handleSignallingIceCandidate(message) {
        const gen = this.ctx.getPcGeneration();
        try {
            await this.ctx.addIceCandidate({
                candidate: message.candidate,
                sdpMid: message.sdpMid,
                sdpMLineIndex: message.sdpMLineIndex
            });
        }
        catch (error) {
            if (this.ctx.getPcGeneration() !== gen) {
                this.log.debug(`Ignoring ICE candidate error from stale gen=${gen}`);
                return;
            }
            this.log.error('Failed to add ICE candidate:', error);
        }
    }
    // ===== RECONNECTION =====
    /**
     * Handle WebSocket close — attempt reconnection based on reconnect-mode.
     */
    handleWsClose(event) {
        const closeReason = this.getCloseCodeDescription(event.code);
        this.log.info(`WebSocket closed: code=${event.code} (${closeReason}), reason="${event.reason || 'none'}"`);
        this.stopKeepalive();
        this.stopHeartbeat();
        if (this._state === 'idle') {
            this.log.info('Not reconnecting - session was intentionally ended');
            return;
        }
        const config = this.ctx.config;
        if (config.reconnectMode === 'none') {
            this.log.info('Reconnect mode is "none" - not attempting reconnection');
            this._state = 'error';
            this.ctx.setSessionState('error');
            this.ctx.setStatusFailed('disconnected');
            this.ctx.host.dispatchEvent(new CustomEvent('session-disconnected', {
                detail: {
                    code: event.code,
                    reason: event.reason || closeReason,
                    wasClean: event.wasClean,
                    willReconnect: false
                }
            }));
            return;
        }
        const wasConnectedOrReconnecting = this._state === 'connected' || this._state === 'reconnecting';
        const hasAttemptsRemaining = config.reconnectAttempts === Infinity ||
            config.reconnectAttempts === -1 ||
            this._reconnectAttempts < config.reconnectAttempts;
        // Prune old timestamps and enforce the sliding-window cap. This is
        // defense in depth on top of `_reconnectAttempts`: even with the
        // counter, a server that consistently reaches session-ready and then
        // closes shortly after could churn forever. The window caps total
        // reconnects across resets.
        const now = Date.now();
        this._reconnectTimestamps = this._reconnectTimestamps.filter(t => now - t < SessionManager.RECONNECT_WINDOW_MS);
        const withinWindow = this._reconnectTimestamps.length < SessionManager.MAX_RECONNECT_IN_WINDOW;
        const canRecover = config.reconnectMode === 'recover' && this._accessToken;
        const canAlwaysReconnect = config.reconnectMode === 'always';
        if (wasConnectedOrReconnecting && hasAttemptsRemaining && withinWindow && (canRecover || canAlwaysReconnect)) {
            this.log.info(`Will attempt reconnection (mode=${config.reconnectMode}, attempts=${this._reconnectAttempts + 1}/${config.reconnectAttempts === Infinity ? '∞' : config.reconnectAttempts}, inWindow=${this._reconnectTimestamps.length}/${SessionManager.MAX_RECONNECT_IN_WINDOW})`);
            this._state = 'reconnecting';
            this.ctx.setSessionState('reconnecting');
            this.scheduleReconnect();
        }
        else {
            const reason = !hasAttemptsRemaining
                ? 'max reconnection attempts reached'
                : !withinWindow
                    ? `max reconnects in ${SessionManager.RECONNECT_WINDOW_MS / 1000}s window (${this._reconnectTimestamps.length}/${SessionManager.MAX_RECONNECT_IN_WINDOW})`
                    : !canRecover && config.reconnectMode === 'recover'
                        ? 'no access token for recovery'
                        : 'session was not in connected state';
            this.log.info(`Not reconnecting: ${reason}`);
            this._state = 'error';
            this.ctx.setSessionState('error');
            const failureCode = !hasAttemptsRemaining || !withinWindow
                ? 'reconnect_exhausted'
                : !canRecover && config.reconnectMode === 'recover'
                    ? 'no_access_token'
                    : 'not_connected';
            this.ctx.setStatusFailed(failureCode);
            this.ctx.host.dispatchEvent(new CustomEvent('session-disconnected', {
                detail: {
                    code: event.code,
                    reason: event.reason || closeReason,
                    wasClean: event.wasClean,
                    willReconnect: false,
                    failureReason: reason
                }
            }));
        }
    }
    getCloseCodeDescription(code) {
        const descriptions = {
            1000: 'Normal closure',
            1001: 'Going away',
            1002: 'Protocol error',
            1003: 'Unsupported data',
            1005: 'No status received',
            1006: 'Abnormal closure',
            1007: 'Invalid frame payload data',
            1008: 'Policy violation',
            1009: 'Message too big',
            1010: 'Mandatory extension',
            1011: 'Internal server error',
            1012: 'Service restart',
            1013: 'Try again later',
            1014: 'Bad gateway',
            1015: 'TLS handshake failure'
        };
        return descriptions[code] || `Unknown (${code})`;
    }
    scheduleReconnect() {
        if (this._reconnectTimeout) {
            clearTimeout(this._reconnectTimeout);
        }
        const config = this.ctx.config;
        let delay;
        if (config.reconnectStrategy === 'periodic') {
            delay = config.reconnectInterval;
        }
        else {
            delay = Math.min(SessionManager.DEFAULT_MIN_RECONNECT_DELAY * Math.pow(SessionManager.DEFAULT_RECONNECT_BACKOFF, this._reconnectAttempts), SessionManager.DEFAULT_MAX_RECONNECT_DELAY);
        }
        const maxAttempts = config.reconnectAttempts === Infinity || config.reconnectAttempts === -1
            ? '∞'
            : config.reconnectAttempts;
        this.log.info(`Scheduling reconnect attempt ${this._reconnectAttempts + 1}/${maxAttempts} in ${delay}ms (strategy=${config.reconnectStrategy})`);
        this.ctx.host.dispatchEvent(new CustomEvent('session-reconnecting', {
            detail: {
                attempt: this._reconnectAttempts + 1,
                maxAttempts: config.reconnectAttempts,
                delay,
                strategy: config.reconnectStrategy
            }
        }));
        this._reconnectTimeout = setTimeout(async () => {
            this._reconnectAttempts++;
            this._reconnectTimestamps.push(Date.now());
            try {
                // Check if the access token has expired and re-exchange if needed
                if (this._tokenExpiresAt && Date.now() >= this._tokenExpiresAt) {
                    this.log.info('Access token expired, re-exchanging before reconnect...');
                    const tokenResponse = await this.exchangeToken();
                    this._accessToken = tokenResponse.accessToken;
                    this._tokenExpiresAt = tokenResponse.expiresAt;
                    this.log.info('Token re-exchange successful');
                }
                this.log.info(`Attempting reconnection ${this._reconnectAttempts}...`);
                await this.connectWs();
            }
            catch (error) {
                this.log.error('Reconnection failed:', error);
                const hasAttemptsRemaining = config.reconnectAttempts === Infinity ||
                    config.reconnectAttempts === -1 ||
                    this._reconnectAttempts < config.reconnectAttempts;
                if (hasAttemptsRemaining) {
                    this._state = 'reconnecting';
                    this.ctx.setSessionState('reconnecting');
                    this.scheduleReconnect();
                }
                else {
                    this.log.info('Max reconnection attempts reached');
                    this._state = 'error';
                    this.ctx.setSessionState('error');
                    this.ctx.setStatusFailed('reconnect_exhausted');
                    this.ctx.host.dispatchEvent(new CustomEvent('session-disconnected', {
                        detail: {
                            code: 1006,
                            reason: 'Max reconnection attempts reached',
                            wasClean: false,
                            willReconnect: false,
                            failureReason: 'max reconnection attempts reached'
                        }
                    }));
                }
            }
        }, delay);
    }
    // ===== KEEPALIVE / HEARTBEAT =====
    startKeepalive() {
        this.stopKeepalive();
        this._keepaliveInterval = setInterval(() => {
            if (this._ws && this._ws.readyState === WebSocket.OPEN) {
                this._ws.send('ping');
            }
        }, SessionManager.SESSION_KEEPALIVE_INTERVAL);
    }
    stopKeepalive() {
        if (this._keepaliveInterval) {
            clearInterval(this._keepaliveInterval);
            this._keepaliveInterval = null;
        }
    }
    startHeartbeat() {
        this.stopHeartbeat();
        this._lastHeartbeatAck = Date.now();
        this._heartbeatInterval = setInterval(() => {
            if (this._ws && this._ws.readyState === WebSocket.OPEN) {
                const sinceLastAck = Date.now() - this._lastHeartbeatAck;
                if (sinceLastAck > SessionManager.SESSION_HEARTBEAT_TIMEOUT) {
                    this.log.warn(`Heartbeat timeout — no ack for ${Math.round(sinceLastAck / 1000)}s, closing stale WebSocket`);
                    this._ws.close(4000, 'heartbeat timeout');
                    return;
                }
                this.sendMessage({ type: 'heartbeat' });
            }
        }, SessionManager.SESSION_HEARTBEAT_INTERVAL);
    }
    stopHeartbeat() {
        if (this._heartbeatInterval) {
            clearInterval(this._heartbeatInterval);
            this._heartbeatInterval = null;
        }
    }
}
// Reconnection defaults
SessionManager.DEFAULT_MIN_RECONNECT_DELAY = 1000;
SessionManager.DEFAULT_MAX_RECONNECT_DELAY = 30000;
SessionManager.DEFAULT_RECONNECT_BACKOFF = 2;
SessionManager.SESSION_HEARTBEAT_INTERVAL = 30000;
SessionManager.SESSION_HEARTBEAT_TIMEOUT = 90000; // 3 missed acks → stale
SessionManager.SESSION_KEEPALIVE_INTERVAL = 25000;
// Sliding-window defense-in-depth: even if `_reconnectAttempts` keeps
// getting reset (e.g. by a server that accepts the WS handshake then
// immediately closes), refuse further reconnects after too many in a
// short window. Mirrors the recovery-side MAX_RECOVERY_IN_WINDOW guard.
SessionManager.RECONNECT_WINDOW_MS = 300000; // 5 minutes
SessionManager.MAX_RECONNECT_IN_WINDOW = 5;

/**
 * Pixel Streaming Web Component
 *
 * A self-contained web component for Pixel Streaming that handles:
 * - Video streaming display
 * - WebRTC peer connection and data channel
 * - Binary message protocol (ToStreamer/FromStreamer)
 * - Audio output and microphone input
 * - Mouse input (hovering and pointer-locked modes)
 * - Touch input (real multi-touch and fake mouse from touch)
 * - Keyboard input
 * - Gamepad support
 * - WebXR support (VR headsets and controllers)
 * - Coordinate transformation for aspect ratio handling
 */
const RESOLUTION_CLAMP_MAP = {
    sxga: { maxWidth: 1280, maxHeight: 1024 },
    hd: { maxWidth: 1366, maxHeight: 768 },
    hdplus: { maxWidth: 1600, maxHeight: 900 },
    fhd: { maxWidth: 1920, maxHeight: 1080 },
    wuxga: { maxWidth: 1920, maxHeight: 1200 },
    qhd: { maxWidth: 2560, maxHeight: 1440 },
    wqhd: { maxWidth: 3440, maxHeight: 1440 },
    uhd: { maxWidth: 3840, maxHeight: 2160 },
};
// ===== MAIN WEB COMPONENT =====
// SSR guard: allow the module to be imported in Node.js (e.g. Next.js SSR)
// without crashing on missing browser globals.
const HTMLElementBase = typeof HTMLElement !== 'undefined'
    ? HTMLElement
    : class {
    };
class PixelStream extends HTMLElementBase {
    /** Instance accessor for the package version. */
    get version() {
        return PixelStream.VERSION;
    }
    /** Current configuration snapshot. */
    get config() {
        return {
            nativeTouch: this._nativeTouch,
            pointerLocked: this._pointerLocked,
            pointerLockRelease: this._pointerLockRelease,
            suppressBrowserKeys: this._suppressBrowserKeys,
            noMouseCoalescing: this._noMouseCoalescing,
            noTouchCoalescing: this._noTouchCoalescing,
            apiEndpoint: this._apiEndpoint,
            admissionToken: this._admissionToken,
            appId: this._appId,
            appVersion: this._appVersion,
            noAutoConnect: this._noAutoConnect,
            mute: this._mute,
            volume: this._volume,
            rendezvousPreference: this._rendezvousPreference,
            lingerPreference: this._lingerPreference,
            leftGracePeriod: this._leftGracePeriod,
            reconnectMode: this._reconnectMode,
            reconnectAttempts: this._reconnectAttempts,
            reconnectStrategy: this._reconnectStrategy,
            reconnectInterval: this._reconnectInterval,
            disconnectGraceMs: this._disconnectGraceMs,
            resizeMode: this._resizeMode,
            dprCap: this._dprCap,
            resolutionClamp: this._resolutionClamp,
            queueWaitTolerance: this._queueWaitTolerance,
            webrtcNegotiationTolerance: this._webrtcNegotiationTolerance,
            debug: this._debug,
            controls: this._controls,
            swiftJobRequest: this._swiftJobRequest,
            preflightDeploymentCheck: this._preflightDeploymentCheck,
            streamerId: this._streamerId,
            lat: this._lat,
            lng: this._lng,
            forceRelay: this._forceRelay,
            useMic: this._useMicIntent || (this.webrtc?.isMicrophoneEnabled ?? false),
            forceMonoAudio: false,
            iceBatchDelay: this._iceBatchDelay,
            streamQuality: this._streamQuality,
            videoBitrateMin: this._videoBitrateMin,
            videoBitrateStart: this._videoBitrateStart,
            videoBitrateMax: this._videoBitrateMax,
            logLevel: this._logLevel,
        };
    }
    static get observedAttributes() {
        return [
            'native-touch',
            'pointer-lock',
            'pointer-lock-release',
            'enable-gamepad',
            'enable-xr',
            'suppress-browser-keys',
            'no-mouse-coalescing',
            'no-touch-coalescing',
            // Admission & session attributes
            'admission-token',
            'app-id',
            'app-version',
            'no-auto-connect',
            'mute',
            'show-unmute',
            'use-mic',
            'volume',
            'rendezvous-preference',
            'linger-preference',
            'left-grace-period',
            // Session management
            'api-endpoint',
            // Reconnection configuration
            'reconnect-mode',
            'reconnect-attempts',
            'reconnect-strategy',
            'reconnect-interval',
            'disconnect-grace-ms',
            // Resize handling
            'resize-mode',
            'dpr-cap',
            'resolution-clamp',
            // Debug mode
            'debug',
            // Job controls
            'controls',
            'swift-job-request',
            // Network
            'force-relay',
            // Tolerances (seconds)
            'queue-wait-tolerance',
            'webrtc-negotiation-tolerance',
            // Streamer selection
            'streamer-id',
            // Geolocation override
            'lat',
            'lng',
            // Stream quality
            'stream-quality',
            'video-bitrate-min',
            'video-bitrate-start',
            'video-bitrate-max',
            // Logging
            'log-level',
            // Idle timer
            'idle-timer',
            'idle-audible-warning',
            'idle-watch-duration',
            'idle-warning-duration',
            'idle-countdown-duration',
            // Session robustness opt-ins
            'preflight-deployment-check',
            'show-requeue',
            // Overlay guidance (optional text)
            'guidance-level',
            'guidance-strings',
        ];
    }
    get nativeTouch() {
        return this._nativeTouch;
    }
    set nativeTouch(value) {
        this._nativeTouch = value;
        this.toggleAttribute('native-touch', value);
    }
    get pointerLock() {
        return this._pointerLocked;
    }
    set pointerLock(value) {
        this._pointerLocked = value;
        this.toggleAttribute('pointer-lock', value);
    }
    get pointerLockRelease() {
        return this._pointerLockRelease;
    }
    set pointerLockRelease(value) {
        this._pointerLockRelease = value;
        this.toggleAttribute('pointer-lock-release', value);
    }
    get suppressBrowserKeys() {
        return this._suppressBrowserKeys;
    }
    set suppressBrowserKeys(value) {
        this._suppressBrowserKeys = value;
        this.toggleAttribute('suppress-browser-keys', value);
    }
    get noMouseCoalescing() {
        return this._noMouseCoalescing;
    }
    set noMouseCoalescing(value) {
        this._noMouseCoalescing = value;
        this.toggleAttribute('no-mouse-coalescing', value);
    }
    get noTouchCoalescing() {
        return this._noTouchCoalescing;
    }
    set noTouchCoalescing(value) {
        this._noTouchCoalescing = value;
        this.toggleAttribute('no-touch-coalescing', value);
    }
    /**
     * Debug mode - shows visual indicators for session connectivity.
     * When enabled, displays a colored border/glow:
     * - Gray: idle/no session
     * - Yellow: connecting/authenticating/reconnecting
     * - Green: connected
     * - Red: error
     */
    get debug() {
        return this._debug;
    }
    set debug(value) {
        this._debug = value;
        this.toggleAttribute('debug', value);
        this._updateDebugIndicator();
    }
    get logLevel() {
        return this._logLevel;
    }
    set logLevel(value) {
        this._logLevel = value;
        this.setAttribute('log-level', value);
    }
    /**
     * ICE candidate batching delay in milliseconds.
     * - 0ms for localhost/LAN (no batching overhead)
     * - 10-20ms for regional networks
     * - 50-100ms for high-latency or mobile networks
     */
    get iceBatchDelay() {
        return this._iceBatchDelay;
    }
    set iceBatchDelay(value) {
        this._iceBatchDelay = Math.max(0, value);
    }
    // ===== ADMISSION & SESSION PROPERTIES =====
    /**
     * Required token for session admission.
     * Setting this triggers an 'admission-token-change' event.
     */
    get admissionToken() {
        return this._admissionToken;
    }
    set admissionToken(value) {
        const oldValue = this._admissionToken;
        this._admissionToken = value;
        if (value !== null) {
            this.setAttribute('admission-token', value);
        }
        else {
            this.removeAttribute('admission-token');
        }
        if (oldValue !== value) {
            this.dispatchEvent(new CustomEvent('admission-token-change', {
                detail: { oldValue, newValue: value }
            }));
        }
    }
    /**
     * Optional application identifier (overrides/supplements admission token)
     */
    get appId() {
        return this._appId;
    }
    set appId(value) {
        const oldValue = this._appId;
        this._appId = value;
        if (value !== null) {
            this.setAttribute('app-id', value);
        }
        else {
            this.removeAttribute('app-id');
        }
        if (oldValue !== value) {
            this.dispatchEvent(new CustomEvent('app-id-change', {
                detail: { oldValue, newValue: value }
            }));
        }
    }
    /**
     * Optional application version refinement
     */
    get appVersion() {
        return this._appVersion;
    }
    set appVersion(value) {
        const oldValue = this._appVersion;
        this._appVersion = value;
        if (value !== null) {
            this.setAttribute('app-version', value);
        }
        else {
            this.removeAttribute('app-version');
        }
        if (oldValue !== value) {
            this.dispatchEvent(new CustomEvent('app-version-change', {
                detail: { oldValue, newValue: value }
            }));
        }
    }
    /**
     * Disable automatic session connection when admission token is set.
     * By default, the component auto-connects. Set this to opt out and
     * manage session lifecycle manually via `startSession()`.
     */
    get noAutoConnect() {
        return this._noAutoConnect;
    }
    set noAutoConnect(value) {
        const oldValue = this._noAutoConnect;
        this._noAutoConnect = value;
        this.toggleAttribute('no-auto-connect', value);
        if (oldValue !== value) {
            this.dispatchEvent(new CustomEvent('no-auto-connect-change', {
                detail: { oldValue, newValue: value }
            }));
        }
    }
    /**
     * Mute audio output
     */
    get mute() {
        return this._mute;
    }
    set mute(value) {
        const oldValue = this._mute;
        this._mute = value;
        this.toggleAttribute('mute', value);
        // Apply mute to video and audio elements
        if (this.video) {
            this.video.muted = value;
        }
        this.webrtc.getAudioElement().muted = value;
        if (oldValue !== value) {
            this.dispatchEvent(new CustomEvent('mute-change', {
                detail: { oldValue, newValue: value, muted: value }
            }));
            this._onMuteChanged();
        }
    }
    /**
     * Shared side effects for any mute transition (setter or attribute).
     * Arms the auto-unmute gesture listener while muted, refreshes the
     * corner unmute button.
     */
    _onMuteChanged() {
        if (this._mute) {
            this._installAutoUnmuteListener();
        }
        else {
            this._removeAutoUnmuteListener();
        }
        this._updateUnmuteControl();
    }
    /**
     * Enable microphone input. When set before connecting, the microphone
     * will be requested during WebRTC negotiation. When toggled while
     * streaming, enables or disables the microphone immediately.
     */
    get useMic() {
        return this._useMicIntent;
    }
    set useMic(value) {
        const oldValue = this._useMicIntent;
        this._useMicIntent = value;
        this.toggleAttribute('use-mic', value);
        // If already streaming, enable/disable mic immediately
        if (this._streamState === 'streaming') {
            if (value) {
                this.requestMicrophone().then(success => {
                    if (!success) {
                        this._useMicIntent = false;
                        this.toggleAttribute('use-mic', false);
                    }
                });
            }
            else {
                this.disableMicrophone();
            }
        }
        if (oldValue !== value) {
            this.dispatchEvent(new CustomEvent('use-mic-change', {
                detail: { oldValue, newValue: value, microphone: value }
            }));
        }
    }
    /**
     * Audio output volume (0.0 silent – 1.0 full).
     * Applies to both the video and dedicated audio elements.
     */
    get volume() {
        return this._volume;
    }
    set volume(value) {
        const clamped = Math.max(0, Math.min(1, Number(value) || 0));
        const oldValue = this._volume;
        this._volume = clamped;
        this.setAttribute('volume', String(clamped));
        if (this.video) {
            this.video.volume = clamped;
        }
        this.webrtc.getAudioElement().volume = clamped;
        if (oldValue !== clamped) {
            this.dispatchEvent(new CustomEvent('volume-change', {
                detail: { oldValue, newValue: clamped }
            }));
        }
    }
    /**
     * Milliseconds to wait for a peer streamer before giving up.
     * If set and no streamer connects within this time, 'rendezvous-timeout' event is fired.
     */
    get rendezvousPreference() {
        return this._rendezvousPreference;
    }
    set rendezvousPreference(value) {
        const oldValue = this._rendezvousPreference;
        this._rendezvousPreference = value;
        if (value !== null) {
            this.setAttribute('rendezvous-preference', value.toString());
        }
        else {
            this.removeAttribute('rendezvous-preference');
        }
        if (oldValue !== value) {
            this.dispatchEvent(new CustomEvent('rendezvous-preference-change', {
                detail: { oldValue, newValue: value }
            }));
        }
    }
    /**
     * Milliseconds to remain in session after all other agents leave.
     * When the last peer leaves, a 'linger-started' event is fired and the timer begins.
     * If no peer reconnects, 'linger-timeout' event is fired.
     */
    get lingerPreference() {
        return this._lingerPreference;
    }
    set lingerPreference(value) {
        const oldValue = this._lingerPreference;
        this._lingerPreference = value;
        if (value !== null) {
            this.setAttribute('linger-preference', value.toString());
        }
        else {
            this.removeAttribute('linger-preference');
        }
        if (oldValue !== value) {
            this.dispatchEvent(new CustomEvent('linger-preference-change', {
                detail: { oldValue, newValue: value }
            }));
        }
    }
    /**
     * Milliseconds grace period before this client appears as left to other agents.
     * This allows for brief disconnections without triggering peer 'left' events.
     */
    get leftGracePeriod() {
        return this._leftGracePeriod;
    }
    set leftGracePeriod(value) {
        const oldValue = this._leftGracePeriod;
        this._leftGracePeriod = value;
        if (value !== null) {
            this.setAttribute('left-grace-period', value.toString());
        }
        else {
            this.removeAttribute('left-grace-period');
        }
        if (oldValue !== value) {
            this.dispatchEvent(new CustomEvent('left-grace-period-change', {
                detail: { oldValue, newValue: value }
            }));
        }
    }
    // ===== SESSION MANAGEMENT PROPERTIES =====
    /**
     * API endpoint for token exchange and WebSocket connections.
     * Defaults to 'api.interlucent.ai'. Use 'api.dev.interlucent.ai' for dev stack.
     */
    get apiEndpoint() {
        return this._apiEndpoint;
    }
    set apiEndpoint(value) {
        const oldValue = this._apiEndpoint;
        this._apiEndpoint = value;
        if (value) {
            this.setAttribute('api-endpoint', value);
        }
        else {
            this.removeAttribute('api-endpoint');
        }
        if (oldValue !== value) {
            this.dispatchEvent(new CustomEvent('api-endpoint-change', {
                detail: { oldValue, newValue: value }
            }));
        }
    }
    /**
     * Reconnection mode: 'none', 'recover', or 'always'.
     * - none: No automatic reconnection (default)
     * - recover: Reconnect to the same session using existing access token
     * - always: Always attempt reconnection (for future use)
     */
    get reconnectMode() {
        return this._reconnectMode;
    }
    set reconnectMode(value) {
        this._reconnectMode = value;
        this.setAttribute('reconnect-mode', value);
    }
    /**
     * Maximum number of reconnection attempts.
     * Use Infinity or -1 for unlimited attempts.
     */
    get reconnectAttempts() {
        return this._reconnectAttempts;
    }
    set reconnectAttempts(value) {
        this._reconnectAttempts = value;
        if (value === Infinity || value === -1) {
            this.removeAttribute('reconnect-attempts');
        }
        else {
            this.setAttribute('reconnect-attempts', String(value));
        }
    }
    /**
     * Reconnection strategy: 'periodic' or 'exponential-backoff'.
     */
    get reconnectStrategy() {
        return this._reconnectStrategy;
    }
    set reconnectStrategy(value) {
        this._reconnectStrategy = value;
        this.setAttribute('reconnect-strategy', value);
    }
    /**
     * Reconnection interval in milliseconds (for periodic strategy).
     */
    get reconnectInterval() {
        return this._reconnectInterval;
    }
    set reconnectInterval(value) {
        this._reconnectInterval = value;
        this.setAttribute('reconnect-interval', String(value));
    }
    /**
     * Grace period in milliseconds before treating a disconnected WebRTC connection as failed.
     * During this period, RTP stats are polled to check if media is still flowing.
     * Default: 9000ms (9 seconds)
     */
    get disconnectGraceMs() {
        return this._disconnectGraceMs;
    }
    set disconnectGraceMs(value) {
        this._disconnectGraceMs = Math.max(0, value);
        this.setAttribute('disconnect-grace-ms', String(this._disconnectGraceMs));
    }
    /**
     * Resize mode - determines what messages are sent when viewport resizes.
     * - 'none': No automatic resize messages sent
     * - 'auto': Auto-detect best format based on UE version/response
     * - 'pureweb': Pureweb plugin format (UIInteraction with action: '1', width, height)
     * - 'pre-5.4': Legacy mode - sends UIInteraction with Console command (r.setres WxH)
     * - '5.4+': Modern mode (default) - sends Command with Resolution.Width/Height
     */
    get resizeMode() {
        return this._resizeMode;
    }
    set resizeMode(value) {
        this._resizeMode = value;
        // Reset detection state when mode changes
        this._detectedResizeMode = null;
        this._resizeDetectionInProgress = false;
        this._initialResizeSent = false;
        this.setAttribute('resize-mode', value);
    }
    /**
     * DPR cap - limits devicePixelRatio used when computing resize dimensions.
     * - 1 (default without resolution-clamp): Send CSS pixels to the game (matches Epic's PixelStreamingInfrastructure).
     *   Games handle their own internal resolution scaling.
     * - Infinity (default when resolution-clamp is set): Send physical device pixels (matches PureWeb SDK behavior).
     *   The resolution-clamp caps the upper bound, preventing retina explosions.
     * - 1.5, 2, etc.: Allow some Retina benefit with a cap.
     *
     * When resolution-clamp is set without an explicit dpr-cap, dpr-cap auto-promotes to Infinity
     * so the clamp actually activates on high-DPI devices. Setting dpr-cap explicitly always wins.
     */
    get dprCap() {
        return this._dprCap;
    }
    set dprCap(value) {
        this._dprCapExplicit = true;
        this._dprCap = Math.max(0.1, value);
        this.setAttribute('dpr-cap', String(this._dprCap));
    }
    /**
     * Resolution clamp — caps device-pixel dimensions sent to the server.
     * Accepts a named preset string (e.g. 'fhd', 'qhd'), a "WIDTHxHEIGHT" string
     * (e.g. '1080x512'), or a custom { maxWidth, maxHeight } object.
     * Set to null to disable clamping.
     */
    get resolutionClamp() {
        return this._resolutionClamp;
    }
    set resolutionClamp(value) {
        if (value === null) {
            this._resolutionClamp = null;
            this.removeAttribute('resolution-clamp');
            if (!this._dprCapExplicit) {
                this._dprCap = 1;
            }
        }
        else if (typeof value === 'string') {
            // Delegates to attributeChangedCallback which handles dpr-cap promotion
            this.setAttribute('resolution-clamp', value);
        }
        else {
            this._resolutionClamp = value;
            if (!this._dprCapExplicit) {
                this._dprCap = Infinity;
            }
        }
    }
    /**
     * Check if the current stream is detected as an XR/stereo stream.
     * Detection is based on:
     * - Multiple video tracks with XR-like labels (left/right/stereo)
     * - Side-by-side stereo aspect ratio (>3.5:1)
     * - Exactly 2 video tracks (stereo pair)
     */
    get isXRStream() {
        return this._isXRStream;
    }
    /**
     * Current session connection state.
     * One of: 'idle', 'connecting', 'authenticating', 'connected', 'reconnecting', 'error'
     */
    get sessionState() {
        return this._sessionState;
    }
    /**
     * Current WebRTC media pipeline state.
     * One of: 'idle', 'starting', 'streaming', 'interrupted', 'recovering', 'failed'
     */
    get streamState() {
        return this._streamState;
    }
    /**
     * Derived meta-status combining session, job, and stream states into a
     * single user-facing progression.
     */
    get status() {
        return this._status;
    }
    /**
     * When status is 'failed', the reason for the failure (e.g.
     * 'queue_wait_timeout', 'media_pipeline_failure'). Null otherwise.
     */
    get failureReason() {
        return this._failureReason;
    }
    /**
     * Timestamp (Date.now()) of the last user input sent to the streamer.
     * Returns 0 if no input has been sent yet. Consumers can poll this to
     * implement their own idle timeouts.
     */
    get lastUserInteraction() {
        return this._lastUserInteraction;
    }
    /**
     * Timestamp (Date.now()) of when the stream began.
     * Returns 0 if not currently streaming.
     */
    get streamStartedAt() {
        return this._streamStartedAt;
    }
    // ===== IDLE TIMER PROPERTIES =====
    get idleTimer() { return this._idleTimer; }
    set idleTimer(value) {
        this._idleTimer = value;
        this.toggleAttribute('idle-timer', value);
    }
    get idleAudibleWarning() { return this._idleAudibleWarning; }
    set idleAudibleWarning(value) {
        this._idleAudibleWarning = value;
        this.toggleAttribute('idle-audible-warning', value);
    }
    get idleWatchDuration() { return this._idleWatchDuration; }
    set idleWatchDuration(value) {
        this._idleWatchDuration = Math.max(0, Number(value) || 300000);
        this.setAttribute('idle-watch-duration', String(this._idleWatchDuration));
    }
    get idleWarningDuration() { return this._idleWarningDuration; }
    set idleWarningDuration(value) {
        this._idleWarningDuration = Math.max(0, Number(value) || 5000);
        this.setAttribute('idle-warning-duration', String(this._idleWarningDuration));
    }
    get idleCountdownDuration() { return this._idleCountdownDuration; }
    set idleCountdownDuration(value) {
        this._idleCountdownDuration = Math.max(1, Math.round(Number(value) || 30));
        this.setAttribute('idle-countdown-duration', String(this._idleCountdownDuration));
    }
    get idleState() { return this._idleState; }
    get idleCountdownRemaining() { return this._idleCountdownRemaining; }
    /**
     * Access token obtained from token exchange (read-only)
     */
    get accessToken() {
        return this.session.accessToken;
    }
    /**
     * Session ID obtained from token exchange (read-only)
     */
    get sessionId() {
        return this.session.sessionId;
    }
    /**
     * Agent ID obtained from token exchange (read-only)
     */
    get agentId() {
        return this.session.agentId;
    }
    /**
     * ICE servers obtained from token exchange (read-only)
     */
    get iceServers() {
        return this.session.iceServers;
    }
    /**
     * Get the current admission configuration
     */
    getAdmissionConfig() {
        if (!this._admissionToken) {
            return null;
        }
        return {
            admissionToken: this._admissionToken,
            appId: this._appId || undefined,
            appVersion: this._appVersion || undefined
        };
    }
    /**
     * Get the current session preferences
     */
    getSessionPreferences() {
        return {
            rendezvousPreference: this._rendezvousPreference || undefined,
            lingerPreference: this._lingerPreference || undefined,
            leftGracePeriod: this._leftGracePeriod || undefined
        };
    }
    /**
     * Check if the player is currently admitted to a session
     */
    get isAdmitted() {
        return this._isAdmitted;
    }
    /**
     * Build the private PixelStreamContext adapter.
     *
     * Arrow functions close over `this` (the PixelStream host) so delegate
     * fields (webrtc, session, metrics, …) are resolved at call-time, not
     * at construction-time.  This is safe because no delegate invokes a
     * context method inside its own constructor.
     */
    _createContext() {
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        const self = this;
        return {
            get host() { return self; },
            get config() { return self.config; },
            get performanceMetrics() { return self.performanceMetrics; },
            // --- DOM elements ---
            getVideoElement: () => self.video,
            getAudioElement: () => self.webrtc.getAudioElement(),
            getCoordTranslator: () => self.coordTranslator,
            // --- Data channel ---
            getDataChannel: () => self.webrtc.getDataChannel(),
            sendToStreamer: (type, data) => self.sendToStreamer(type, data ?? []),
            sendRawBinary: (buffer) => self.webrtc.sendRawBinary(buffer),
            // --- WebRTC ---
            getPeerConnection: () => self.webrtc.getPeerConnection(),
            // --- Session WebSocket ---
            getSessionWs: () => self.session.ws,
            sendSessionMessage: (msg) => self.session.sendMessage(msg),
            getSessionState: () => self._sessionState,
            // --- Session identity ---
            getAccessToken: () => self.session.accessToken,
            getSessionId: () => self.session.sessionId,
            getAgentId: () => self.session.agentId,
            getIceServers: () => self.session.iceServers,
            getPeerAgentId: () => self.session.peerAgentId,
            // --- Metrics ---
            logPerformanceSummary: () => self.metrics.logPerformanceSummary(),
            reportTiming: (milestone) => self.metrics.reportTiming(milestone),
            collectAndDisplaySessionStats: () => self.metrics.collectAndDisplaySessionStats(),
            getMetricsSessionStartTime: () => self.metrics.sessionStartTime,
            setMetricsSessionStartTime: (time) => { self.metrics.sessionStartTime = time; },
            // --- File transfer ---
            handleFileExtension: (data) => self.fileTransfer.handleExtension(data),
            handleFileMimeType: (data) => self.fileTransfer.handleMimeType(data),
            handleFileContents: (data) => self.fileTransfer.handleContents(data),
            // --- Session operations ---
            leaveSession: (reason) => self.stop(reason),
            getSubscribedStreamerId: () => self.session.subscribedStreamerId,
            subscribeToStreamer: (streamerId) => self.session.subscribeToStreamer(streamerId),
            // --- Host state ---
            setAutoplayBlocked: (blocked) => { self._autoplayBlocked = blocked; },
            setHasRemoteAudio: (value) => { self._setHasRemoteAudio(value); },
            setJobState: (state) => self._setJobState(state),
            detectXRStream: () => self._detectXRStream(),
            onWorkerLeft: () => self.webrtc.onWorkerLeft(),
            // --- Permissions ---
            checkPermission: (perm) => self.checkPermission(perm),
            watchPermission: (perm) => self.watchPermission(perm),
            // --- WebRTC signaling ---
            handleOffer: (offer) => self.webrtc.handleOffer(offer),
            handleAnswer: (answer) => self.webrtc.handleAnswer(answer),
            createOffer: () => self.webrtc.createOffer(),
            createDataChannel: (label) => self.webrtc.createDataChannel(label),
            addIceCandidate: (candidate) => self.webrtc.addIceCandidate(candidate),
            preCreatePeerConnection: (config) => self.webrtc.preCreatePeerConnection(config),
            getPcGeneration: () => self.webrtc.getPcGeneration(),
            // --- Latency ---
            handleLatencyTestResult: (payload) => self.metrics.handleLatencyTestResult(payload),
            getLatencyBreakdown: () => self.metrics.getLatencyBreakdown(),
            // --- Session state callbacks ---
            setStreamState: (state, detail) => self._setStreamState(state, detail),
            setSessionState: (state) => self._setSessionState(state),
            updateDebugIndicator: () => self._updateDebugIndicator(),
            cancelRendezvousTimer: () => self._cancelRendezvousTimer(),
            resetPerformanceMetrics: () => self.metrics.resetPerformanceMetrics(),
            // --- Meta-status (derived from session/job/stream) ---
            setRendezvoused: () => { self._rendezvoused = true; self._recomputeStatus(); },
            setNegotiatingStarted: () => { self._negotiatingStarted = true; self._recomputeStatus(); },
            setStatusFailed: (reason) => {
                self._setStatus('failed', reason);
                self.webrtc.closePeerConnection();
                self.webrtc.resetResilienceState();
            },
            setStatusEnded: () => {
                self._setJobState('unsubmitted');
                self._setStatus('ended');
                self.webrtc.closePeerConnection();
                self.webrtc.resetResilienceState();
            },
        };
    }
    constructor() {
        super();
        this._overlaySlot = null;
        this._hasSlottedOverlay = false;
        // Config backing fields
        this._nativeTouch = false;
        this._suppressBrowserKeys = false;
        this._pointerLocked = false;
        this._pointerLockRelease = false;
        this._noMouseCoalescing = false;
        this._noTouchCoalescing = false;
        // Resize observer for container size changes
        this.resizeObserver = null;
        // ICE candidate batching delay (config property)
        this._iceBatchDelay = 0; // ms to wait before sending batch (0 = no batching)
        // Performance metrics tracking
        this.performanceMetrics = {
            sessionStarted: null,
            tokenExchangeStart: null,
            tokenExchangeComplete: null,
            peerConnectionCreated: null,
            sessionWsConnectStart: null,
            sessionWsConnectComplete: null,
            sessionReady: null,
            jobRequested: null,
            subscribeStart: null,
            offerReceived: null,
            answerSent: null,
            iceConnected: null,
            firstTrack: null,
            firstFrame: null,
            connectionComplete: null
        };
        // Admission & session configuration
        this._admissionToken = null;
        this._appId = null;
        this._appVersion = null;
        this._noAutoConnect = false;
        this._userWantsStream = false;
        this._mute = false;
        this._useMicIntent = false;
        this._volume = 1;
        this._rendezvousPreference = null; // seconds to wait for peer streamer
        this._lingerPreference = null; // seconds to remain after others leave
        this._leftGracePeriod = null; // seconds grace before appearing as left
        this._queueWaitTolerance = null; // seconds to wait for worker after requesting job
        this._webrtcNegotiationTolerance = null; // seconds to wait for WebRTC after rendezvous
        // Session state
        this._isAdmitted = false;
        this._rendezvousTimer = null;
        this._lingerTimer = null;
        // ===== SESSION MANAGEMENT =====
        // API endpoint and session connection state
        this._apiEndpoint = 'api.interlucent.ai';
        // Session state (mirrored from SessionManager for host UI)
        this._sessionState = 'idle';
        // Stream state (WebRTC media pipeline lifecycle)
        this._streamState = 'idle';
        // Reconnection configuration (configurable via attributes)
        this._reconnectMode = 'none';
        this._reconnectAttempts = Infinity; // -1 or Infinity = unlimited
        this._reconnectStrategy = 'exponential-backoff';
        this._reconnectInterval = PixelStream.DEFAULT_RECONNECT_INTERVAL;
        this._disconnectGraceMs = 18000; // Default 18s grace — covers LTE↔Wi-Fi handoff (up to 15s observed on iOS) and TURN relay fallback
        this._streamerId = null; // From streamer-id attribute
        this._lat = null; // Geolocation override (latitude)
        this._lng = null; // Geolocation override (longitude)
        this._boundPageHideHandler = null; // pagehide listener ref for cleanup
        this._boundFullscreenChangeHandler = null; // fullscreenchange listener ref
        this._boundWebkitFullscreenChangeHandler = null; // webkitfullscreenchange listener ref
        this._boundDataChannelOpenHandler = null; // data-channel-open listener ref
        // Resize mode configuration
        // 'none': No resize messages sent
        // 'auto': Auto-detect best format based on UE version/response
        // 'pureweb': Pureweb plugin format (UIInteraction with action: '1')
        // 'pre-5.4': Legacy UE format (UIInteraction with Console command)
        // '5.4+': Modern UE format (Command message with Resolution.Width/Height)
        this._resizeMode = 'pureweb';
        // DPR cap for resize messages (1 = CSS pixels like Epic, Infinity = physical pixels like PureWeb)
        // Default is Infinity to match PureWeb behavior (send device pixels).
        this._dprCap = Infinity;
        this._dprCapExplicit = false; // true when user explicitly sets dpr-cap
        // Resolution clamp — caps device-pixel dimensions sent to the server (null = no clamping)
        this._resolutionClamp = { maxWidth: 1920, maxHeight: 1080 };
        // Auto-resize detection state
        this._detectedResizeMode = null;
        this._resizeDetectionInProgress = false;
        this._lastResizeDimensions = null;
        this._resizeVerifyTimer = null;
        this._initialResizeSent = false; // Track if we've sent the initial "shotgun" resize
        // XR stream detection
        this._isXRStream = false;
        // Debug mode - shows visual indicators for session connectivity
        this._debug = false;
        // Stream quality
        this._streamQuality = 'quality';
        this._videoBitrateMin = null;
        this._videoBitrateStart = null;
        this._videoBitrateMax = null;
        // Logging
        this._logLevel = 'warn';
        this._log = createLogger(this, 'pixel-stream', 'host', globalThreshold(() => this._logLevel));
        // Network
        this._forceRelay = false;
        // Job state tracking (for controls UI)
        this._jobState = 'unsubmitted';
        this._controls = true;
        this._swiftJobRequest = false;
        this._autoplayBlocked = false;
        this._lastUserInteraction = 0;
        this._streamStartedAt = 0;
        // Mute / unmute affordance state
        this._showUnmute = false;
        this._unmuteControlDismissed = false;
        this._hasRemoteAudioTrack = false;
        this._unmuteBtn = null;
        this._autoUnmuteHandler = null;
        // Idle timer state machine
        this._idleTimer = false;
        this._idleAudibleWarning = false;
        this._idleWatchDuration = 300000;
        this._idleWarningDuration = 5000;
        this._idleCountdownDuration = 30;
        this._idleState = 'off';
        this._idleCountdownRemaining = 0;
        this._idleScheduleHandle = null;
        this._idleWarningHandle = null;
        this._idleTickHandle = null;
        this._idleOverlaySlot = null;
        this._hasSlottedIdleOverlay = false;
        this._idleCountdownEl = null;
        this._idleBarEl = null;
        // Meta-status: single derived attribute combining session/job/stream
        this._status = 'idle';
        this._failureReason = null;
        this._rendezvoused = false;
        this._negotiatingStarted = false;
        // Quiet-Disc overlay state. No escalating text ladder — the disc pulses,
        // and a single stable message appears only after a cognitively significant
        // wait (STATUS_SILENCE_MS). Unhappy paths bypass the silence threshold.
        this._pendingPhaseStartedAt = null;
        this._pendingSilenceTimer = null;
        this._lastAnnouncedStatus = null;
        // Consumer-opt-in guidance escape hatches (all additive, defaults preserve behavior).
        this._preflightDeploymentCheck = false;
        this._showRequeue = false;
        /**
         * Overlay talkativeness. `minimal` is the new default — a single stable
         * message per status, updated immediately on state change. `none`
         * preserves the pure Quiet Disc (silent). `verbose` adds a one-line
         * hint under the primary.
         */
        this._guidanceLevel = 'minimal';
        this._guidanceStrings = null;
        /**
         * Event handler for initial-settings event.
         */
        this._onInitialSettings = (event) => {
            this._handleInitialSettingsForResize(event.detail);
        };
        const shadow = this.attachShadow({ mode: 'open' });
        // Build the context adapter — delegates use this, not `this`
        this._ctx = this._createContext();
        // Initialize delegates
        this.keyboardInput = new KeyboardInputHandler(this._ctx, this._log);
        this.mouseInput = new MouseInputHandler(this._ctx, this._log, {
            onPointerUnlocked: () => this.keyboardInput.releaseAllKeys(),
        });
        this.touchInput = new TouchInputHandler(this._ctx, this._log);
        this.gamepadInput = new GamepadInputHandler(this._ctx, this._log);
        this.xrInput = new XRInputHandler(this._ctx, this._log);
        this.fileTransfer = new FileTransferHandler(this._ctx, this._log);
        this.metrics = new MetricsManager(this._ctx, createLogger(this, 'pixel-stream', 'metrics', globalThreshold(() => this._logLevel)));
        this.webrtc = new WebRTCManager(this._ctx, createLogger(this, 'pixel-stream', 'webrtc', globalThreshold(() => this._logLevel)));
        this.session = new SessionManager(this._ctx, createLogger(this, 'pixel-stream', 'session', globalThreshold(() => this._logLevel)));
        shadow.innerHTML = `
            <style>
                :host {
                    display: block;
                    width: 100%;
                    height: 100%;
                    position: relative;
                    transition: box-shadow 0.3s ease;

                    /* ═══ Overlay theme tokens (dark default) ═══ */
                    --ps-overlay-bg: rgba(10, 10, 10, 0.88);
                    --ps-overlay-text: #e8e4df;
                    --ps-overlay-text-dim: rgba(255, 255, 255, 0.45);
                    --ps-overlay-btn-bg: rgba(255, 255, 255, 0.1);
                    --ps-overlay-btn-border: rgba(255, 255, 255, 0.15);
                    --ps-overlay-btn-hover: rgba(255, 255, 255, 0.18);
                    --ps-overlay-spinner-track: rgba(255, 255, 255, 0.1);
                    --ps-overlay-spinner-fill: rgba(255, 255, 255, 0.7);
                    --ps-overlay-pill-border: rgba(255, 255, 255, 0.15);
                    --ps-overlay-pill-text: rgba(255, 255, 255, 0.6);
                    --ps-overlay-pill-hover: rgba(255, 255, 255, 0.06);
                }

                @media (prefers-color-scheme: light) {
                    :host {
                        --ps-overlay-bg: rgba(245, 245, 245, 0.88);
                        --ps-overlay-text: #1a1a1a;
                        --ps-overlay-text-dim: rgba(0, 0, 0, 0.45);
                        --ps-overlay-btn-bg: rgba(0, 0, 0, 0.08);
                        --ps-overlay-btn-border: rgba(0, 0, 0, 0.15);
                        --ps-overlay-btn-hover: rgba(0, 0, 0, 0.14);
                        --ps-overlay-spinner-track: rgba(0, 0, 0, 0.1);
                        --ps-overlay-spinner-fill: rgba(0, 0, 0, 0.6);
                        --ps-overlay-pill-border: rgba(0, 0, 0, 0.15);
                        --ps-overlay-pill-text: rgba(0, 0, 0, 0.5);
                        --ps-overlay-pill-hover: rgba(0, 0, 0, 0.05);
                    }
                }

                /* Debug mode - session connectivity indicators */
                :host([debug]) {
                    box-shadow:
                        inset 0 0 0 4px rgba(128, 128, 128, 0.8),
                        0 0 0 4px rgba(128, 128, 128, 0.8);
                }

                :host([debug].session-connecting) {
                    box-shadow:
                        inset 0 0 0 4px rgba(255, 180, 0, 1),
                        0 0 0 4px rgba(255, 180, 0, 1),
                        0 0 30px 10px rgba(255, 180, 0, 0.6);
                }

                :host([debug].session-connected) {
                    box-shadow:
                        inset 0 0 0 4px rgba(0, 255, 80, 1),
                        0 0 0 4px rgba(0, 255, 80, 1),
                        0 0 30px 10px rgba(0, 255, 80, 0.6);
                }

                :host([debug].session-error) {
                    box-shadow:
                        inset 0 0 0 4px rgba(255, 50, 50, 1),
                        0 0 0 4px rgba(255, 50, 50, 1),
                        0 0 30px 10px rgba(255, 50, 50, 0.6);
                }

                video {
                    width: 100%;
                    height: 100%;
                    display: block;
                    object-fit: contain;
                    pointer-events: all;
                    /* Pre-allocate a GPU compositing layer so the first
                       real frame can be texture-uploaded without waiting
                       for layer promotion. */
                    will-change: transform;
                }

                /* ═══ OVERLAY ═══ */
                #controls-overlay {
                    position: absolute;
                    inset: 0;
                    display: none;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    background: var(--ps-overlay-bg);
                    backdrop-filter: blur(12px);
                    -webkit-backdrop-filter: blur(12px);
                    font-family: system-ui, -apple-system, sans-serif;
                    color: var(--ps-overlay-text);
                    transition: opacity 280ms cubic-bezier(0.16, 1, 0.3, 1);
                    text-align: center;
                    gap: 0.75rem;
                    z-index: 1;
                }

                #controls-overlay.visible {
                    display: flex;
                }

                :host([status="streaming"]) #controls-overlay {
                    opacity: 0;
                    pointer-events: none;
                }

                #controls-overlay.slotted {
                    background: transparent;
                    backdrop-filter: none;
                    -webkit-backdrop-filter: none;
                    pointer-events: none;
                }

                #controls-overlay.slotted ::slotted(*) {
                    pointer-events: auto;
                }

                ::slotted([slot="overlay"]) {
                    width: 100%;
                    height: 100%;
                }

                /* ═══ STATE VISIBILITY ═══ */
                .state { display: none; flex-direction: column; align-items: center; gap: 0.75rem; }

                :host([status="idle"])            .state-idle,
                :host([status="connected"])       .state-idle,
                :host([status="connecting"])      .state-pending,
                :host([status="authenticating"])  .state-pending,
                :host([status="queued"])          .state-pending,
                :host([status="matched"])         .state-pending,
                :host([status="rendezvoused"])    .state-pending,
                :host([status="negotiating"])     .state-pending,
                :host([status="interrupted"])     .state-pending,
                :host([status="recovering"])      .state-pending,
                :host([status="ready"])           .state-ready,
                :host([status="failed"])          .state-error,
                :host([status="ended"])           .state-ended {
                    display: flex;
                }

                /* Cancel vs Leave button — exactly one is visible inside the
                   pending overlay. Cancel only applies while a job request is
                   unfulfilled (status=queued); every other pending status
                   (connecting, matched, rendezvoused, negotiating,
                   interrupted, recovering, …) shows Leave instead. The outer
                   .state-pending already controls overall visibility — these
                   rules just pick which of the two pills renders. */
                .state-pending [data-action="cancel"],
                .state-pending [data-action="leave"] { display: none; }
                :host([status="queued"]) .state-pending [data-action="cancel"] { display: inline-block; }
                :host(:not([status="queued"])) .state-pending [data-action="leave"] { display: inline-block; }

                /* ═══ PLAY BUTTON ═══ */
                .play-btn {
                    width: 52px;
                    height: 52px;
                    border: 1px solid var(--ps-overlay-btn-border);
                    border-radius: 50%;
                    background: var(--ps-overlay-btn-bg);
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    transition: transform 0.2s, background 0.2s;
                }
                .play-btn:hover  { background: var(--ps-overlay-btn-hover); transform: scale(1.06); }
                .play-btn:active { transform: scale(0.95); }
                .play-btn svg { width: 22px; height: 22px; fill: var(--ps-overlay-text); margin-left: 2px; }

                /* ═══ STATUS DISC (Quiet Disc) ═══ */
                .status-disc {
                    position: relative;
                    width: 48px;
                    height: 48px;
                    border-radius: 50%;
                    background: var(--ps-overlay-spinner-fill, rgba(255, 255, 255, 0.75));
                    opacity: 0.55;
                    animation: disc-pulse 12s ease-in-out infinite;
                    transition: opacity 360ms ease, transform 360ms ease;
                }
                .status-disc::after {
                    /* Amber/red ring accent surfaces only on unhappy paths via data-ring. */
                    content: '';
                    position: absolute;
                    inset: -6px;
                    border-radius: 50%;
                    border: 2px solid transparent;
                    transition: border-color 240ms ease;
                }
                .status-disc[data-ring="amber"]::after { border-color: rgba(217, 162, 56, 0.9); }
                .status-disc[data-ring="red"]::after   { border-color: rgba(209, 74, 68, 0.95); }

                /* Preparing: brisker tempo and broader dynamic range — the disc
                   leans forward while the streamer warms up. A second, much
                   slower animation drifts a faint Bauhaus halo (blue → yellow
                   → red) so the prep moment carries a subtle chromatic pulse
                   without ever announcing itself. */
                :host([status="connecting"])     .status-disc,
                :host([status="authenticating"]) .status-disc,
                :host([status="queued"])         .status-disc,
                :host([status="matched"])        .status-disc,
                :host([status="rendezvoused"])   .status-disc,
                :host([status="negotiating"])    .status-disc {
                    animation:
                        disc-pulse-prep 3.2s ease-in-out infinite,
                        disc-prep-hue 19.2s ease-in-out infinite;
                }

                /* Interrupted / recovering: dim only (no alarm cascade). */
                :host([status="interrupted"]) .status-disc,
                :host([status="recovering"])  .status-disc {
                    opacity: 0.3;
                    animation-duration: 18s;
                }

                /* Terminal: disc becomes static; ring color (if any) carries meaning. */
                :host([status="failed"]) .status-disc,
                :host([status="ended"])  .status-disc {
                    animation: none;
                    opacity: 0.7;
                }

                @keyframes disc-pulse {
                    /* Breath wave: inhale ~4.5s → hold ~1s → exhale ~6.5s (12s total). */
                    0%, 100% { opacity: 0.45; transform: scale(0.96); }
                    37.5%    { opacity: 0.85; transform: scale(1.02); }
                    46%      { opacity: 0.85; transform: scale(1.02); }
                }

                @keyframes disc-pulse-prep {
                    /* Forward-leaning prep cadence — wider opacity/scale swing. */
                    0%, 100% { opacity: 0.30; transform: scale(0.90); }
                    50%      { opacity: 0.95; transform: scale(1.10); }
                }

                @keyframes disc-prep-hue {
                    /* Bauhaus triad halo: blue → yellow → red, very low alpha. */
                    0%, 100% { box-shadow: 0 0 14px 1px rgba(33, 64, 154, 0.16); }
                    33%      { box-shadow: 0 0 14px 1px rgba(245, 197, 24, 0.16); }
                    66%      { box-shadow: 0 0 14px 1px rgba(217, 74, 68, 0.14); }
                }

                @media (prefers-reduced-motion: reduce) {
                    .status-disc { animation: none; opacity: 0.7; }
                }

                /* ═══ STATUS MESSAGE ═══ */
                .status-msg {
                    font-size: 0.75rem;
                    font-weight: 500;
                    color: var(--ps-overlay-text-dim);
                    min-height: 1em; /* preserve layout while empty during silence window */
                    transition: opacity 200ms ease;
                }
                .status-msg[data-pending-msg]:empty { opacity: 0; }

                /* Screen-reader-only region for phase-entry announcements. */
                .status-aria-live {
                    position: absolute;
                    width: 1px;
                    height: 1px;
                    overflow: hidden;
                    clip: rect(0 0 0 0);
                    white-space: nowrap;
                }

                /* Consumer-provided guidance (slot). Appears under the disc when
                 * guidance-level !== 'none'. Hidden by default so existing
                 * consumers that don't opt in see no layout change. */
                .guidance-wrap {
                    display: none;
                    align-items: center;
                    justify-content: center;
                    gap: 0.5rem;
                    max-width: 320px;
                    text-align: center;
                    font-size: 0.7rem;
                    color: var(--ps-overlay-text-dim);
                }
                :host([guidance-level="minimal"]) .guidance-wrap,
                :host([guidance-level="verbose"]) .guidance-wrap {
                    display: flex;
                }
                :host([status="failed"]) .guidance-wrap,
                :host([status="ended"])  .guidance-wrap {
                    display: none;
                }

                /* ═══ PILL BUTTON ═══ */
                .pill-btn {
                    background: none;
                    border: 1px solid var(--ps-overlay-pill-border);
                    color: var(--ps-overlay-pill-text);
                    padding: 0.3rem 0.9rem;
                    border-radius: 16px;
                    cursor: pointer;
                    font-size: 0.7rem;
                    font-family: inherit;
                    transition: background 0.2s;
                }
                .pill-btn:hover { background: var(--ps-overlay-pill-hover); }

                /* ═══ ERROR MESSAGE ═══ */
                .error-message {
                    font-size: 0.7rem;
                    color: var(--ps-overlay-text-dim);
                    max-width: 280px;
                    text-align: center;
                    word-break: break-word;
                }
                .error-message:empty { display: none; }

                /* ═══ IDLE TIMER ═══ */
                :host([idle-state="grace"]),
                :host([idle-state="countdown"]) {
                    filter: grayscale(1);
                    transition: filter 0.6s ease;
                }

                #idle-overlay {
                    position: absolute;
                    inset: 0;
                    display: none;
                    align-items: center;
                    justify-content: center;
                    z-index: 2;
                    pointer-events: none;
                }

                :host([idle-state="countdown"]) #idle-overlay {
                    display: flex;
                }

                .idle-dialog {
                    pointer-events: auto;
                    background: rgba(12, 12, 14, 0.7);
                    backdrop-filter: blur(24px) saturate(1.4);
                    -webkit-backdrop-filter: blur(24px) saturate(1.4);
                    border: 1px solid rgba(255, 255, 255, 0.08);
                    border-radius: 16px;
                    padding: 1.5rem 2rem;
                    min-width: 220px;
                    font-family: system-ui, -apple-system, sans-serif;
                    color: rgba(255, 255, 255, 0.85);
                    text-align: center;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    gap: 0.75rem;
                    box-shadow:
                        0 8px 32px rgba(0, 0, 0, 0.4),
                        inset 0 1px 0 rgba(255, 255, 255, 0.06);
                    animation: idle-card-in 0.3s cubic-bezier(0.16, 1, 0.3, 1);
                }

                @media (prefers-color-scheme: light) {
                    .idle-dialog {
                        background: rgba(255, 255, 255, 0.7);
                        border-color: rgba(0, 0, 0, 0.08);
                        color: rgba(0, 0, 0, 0.8);
                        box-shadow:
                            0 8px 32px rgba(0, 0, 0, 0.12),
                            inset 0 1px 0 rgba(255, 255, 255, 0.5);
                    }
                }

                @keyframes idle-card-in {
                    from { opacity: 0; transform: scale(0.95) translateY(6px); }
                    to   { opacity: 1; transform: scale(1) translateY(0); }
                }

                .idle-dialog .status-msg {
                    color: rgba(255, 255, 255, 0.5);
                    letter-spacing: 0.02em;
                }
                @media (prefers-color-scheme: light) {
                    .idle-dialog .status-msg {
                        color: rgba(0, 0, 0, 0.45);
                    }
                }

                .idle-countdown-number {
                    font-size: 2.4rem;
                    font-weight: 600;
                    font-variant-numeric: tabular-nums;
                    line-height: 1;
                    color: rgba(255, 255, 255, 0.9);
                }
                .idle-countdown-number:empty { display: none; }
                @media (prefers-color-scheme: light) {
                    .idle-countdown-number {
                        color: rgba(0, 0, 0, 0.8);
                    }
                }

                .idle-bar {
                    width: 100%;
                    height: 3px;
                    background: rgba(255, 255, 255, 0.08);
                    border-radius: 2px;
                    overflow: hidden;
                }
                @media (prefers-color-scheme: light) {
                    .idle-bar { background: rgba(0, 0, 0, 0.08); }
                }

                .idle-bar-fill {
                    height: 100%;
                    width: 100%;
                    background: rgba(255, 255, 255, 0.35);
                    border-radius: 2px;
                    transition: width 1s linear;
                }
                @media (prefers-color-scheme: light) {
                    .idle-bar-fill { background: rgba(0, 0, 0, 0.25); }
                }

                .idle-dialog .pill-btn {
                    background: rgba(255, 255, 255, 0.1);
                    border-color: rgba(255, 255, 255, 0.12);
                    color: rgba(255, 255, 255, 0.8);
                    border-radius: 8px;
                    padding: 0.4rem 1.4rem;
                    font-weight: 500;
                }
                .idle-dialog .pill-btn:hover {
                    background: rgba(255, 255, 255, 0.16);
                    border-color: rgba(255, 255, 255, 0.2);
                }
                @media (prefers-color-scheme: light) {
                    .idle-dialog .pill-btn {
                        background: rgba(0, 0, 0, 0.06);
                        border-color: rgba(0, 0, 0, 0.1);
                        color: rgba(0, 0, 0, 0.7);
                    }
                    .idle-dialog .pill-btn:hover {
                        background: rgba(0, 0, 0, 0.1);
                        border-color: rgba(0, 0, 0, 0.15);
                    }
                }

                #idle-overlay.slotted {
                    pointer-events: none;
                }

                #idle-overlay.slotted ::slotted(*) {
                    pointer-events: auto;
                }

                ::slotted([slot="idle-overlay"]) {
                    width: 100%;
                    height: 100%;
                }

                /* ═══ UNMUTE BUTTON ═══
                   Sibling of #controls-overlay so it stays visible during
                   streaming (when the main controls overlay fades to 0).
                   Self-contained styling: this button has no backdrop of
                   its own, so it can't lean on the overlay theme tokens
                   the way state buttons do — they sit on a dark scrim. */
                #unmute-btn {
                    position: absolute;
                    top: 12px;
                    right: 12px;
                    display: none;
                    align-items: center;
                    justify-content: center;
                    width: 44px;
                    height: 44px;
                    padding: 0;
                    border: 1px solid rgba(255, 255, 255, 0.4);
                    border-radius: 50%;
                    background: rgba(0, 0, 0, 0.65);
                    color: #fff;
                    cursor: pointer;
                    z-index: 2;
                    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.5);
                    backdrop-filter: blur(8px);
                    -webkit-backdrop-filter: blur(8px);
                    transition: background 0.2s, transform 0.2s, border-color 0.2s;
                }
                #unmute-btn.visible { display: inline-flex; }
                #unmute-btn:hover {
                    background: rgba(0, 0, 0, 0.85);
                    border-color: rgba(255, 255, 255, 0.6);
                    transform: scale(1.06);
                }
                #unmute-btn:active { transform: scale(0.95); }
                #unmute-btn svg {
                    width: 22px;
                    height: 22px;
                    fill: currentColor;
                }
            </style>

            <video part="video" playsinline disablepictureinpicture></video>
            <div id="controls-overlay">
                <span class="status-aria-live" data-status-aria-live aria-live="polite" aria-atomic="true"></span>
                <slot name="overlay">
                    <div class="state state-idle">
                        <button class="play-btn" data-action="play" aria-label="Start session">
                            <svg viewBox="0 0 24 24"><polygon points="6,3 20,12 6,21"/></svg>
                        </button>
                        <span class="status-msg">Start</span>
                    </div>
                    <div class="state state-pending">
                        <div class="status-disc" part="status-disc" aria-hidden="true"></div>
                        <span class="status-msg" data-pending-msg></span>
                        <div class="guidance-wrap"><slot name="guidance"></slot></div>
                        <!-- Cancel vs Leave: visibility driven by host status in CSS above.
                             Cancel only sends cancel-job to the server, which only matters
                             while the job is unfulfilled (status=queued). For every other
                             pending status (matched/rendezvoused/negotiating/interrupted/
                             recovering) we show Leave instead, which calls stop(). -->
                        <button class="pill-btn" data-action="cancel">Cancel</button>
                        <button class="pill-btn" data-action="leave">Leave</button>
                    </div>
                    <div class="state state-ready">
                        <button class="play-btn" data-action="play" aria-label="Play stream">
                            <svg viewBox="0 0 24 24"><polygon points="6,3 20,12 6,21"/></svg>
                        </button>
                        <span class="status-msg">Tap to play</span>
                    </div>
                    <div class="state state-error">
                        <div class="status-disc" part="status-disc" aria-hidden="true"></div>
                        <span class="status-msg" data-failure-label></span>
                        <span class="error-message"></span>
                        <button class="pill-btn" data-action="retry">Retry</button>
                    </div>
                    <div class="state state-ended">
                        <span class="status-msg">Ended</span>
                        <button class="pill-btn" data-action="restart">Restart</button>
                    </div>
                </slot>
            </div>
            <div id="idle-overlay">
                <slot name="idle-overlay">
                    <div class="idle-dialog">
                        <span class="status-msg">Still watching?</span>
                        <span class="idle-countdown-number" data-idle-countdown></span>
                        <div class="idle-bar"><div class="idle-bar-fill" data-idle-bar></div></div>
                        <button class="pill-btn" data-idle-action="dismiss">Continue</button>
                    </div>
                </slot>
            </div>
            <button id="unmute-btn" type="button" aria-label="Unmute stream">
                <svg viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M3 10v4h4l5 5V5L7 10H3zm13.5 2a4.5 4.5 0 0 0-2.5-4.03v8.05A4.5 4.5 0 0 0 16.5 12zM14 3.23v2.06a7 7 0 0 1 0 13.42v2.06A9 9 0 0 0 14 3.23z"/>
                </svg>
            </button>
        `;
        this.video = shadow.querySelector('video');
        this.coordTranslator = new CoordTranslator(this.video);
        this.controlsOverlay = shadow.querySelector('#controls-overlay');
        // Detect slotted overlay content
        this._overlaySlot = shadow.querySelector('slot[name="overlay"]');
        this._overlaySlot?.addEventListener('slotchange', () => {
            this._hasSlottedOverlay =
                (this._overlaySlot?.assignedNodes({ flatten: false }).length ?? 0) > 0;
            this.controlsOverlay.classList.toggle('slotted', this._hasSlottedOverlay);
            this._updateControlsOverlay();
        });
        // Delegated click handler for overlay buttons (data-action dispatch)
        this.controlsOverlay.addEventListener('click', (e) => {
            const btn = e.target.closest('[data-action]');
            if (!btn)
                return;
            switch (btn.dataset.action) {
                case 'play':
                case 'retry':
                case 'restart':
                    this.play();
                    break;
                case 'cancel':
                    this.cancel();
                    break;
                case 'leave':
                    this.stop('user-leave');
                    break;
            }
        });
        // Idle overlay setup
        this._idleOverlay = shadow.querySelector('#idle-overlay');
        this._idleCountdownEl = shadow.querySelector('[data-idle-countdown]');
        this._idleBarEl = shadow.querySelector('[data-idle-bar]');
        this._idleOverlaySlot = shadow.querySelector('slot[name="idle-overlay"]');
        this._idleOverlaySlot?.addEventListener('slotchange', () => {
            this._hasSlottedIdleOverlay =
                (this._idleOverlaySlot?.assignedNodes({ flatten: false }).length ?? 0) > 0;
            this._idleOverlay.classList.toggle('slotted', this._hasSlottedIdleOverlay);
        });
        this._idleOverlay.addEventListener('click', (e) => {
            const btn = e.target.closest('[data-idle-action]');
            if (btn?.dataset.idleAction === 'dismiss') {
                this._enterIdleState('watching');
            }
        });
        // Built-in unmute button — sibling of #controls-overlay so it stays
        // visible once status=streaming fades the main overlay out.
        this._unmuteBtn = shadow.querySelector('#unmute-btn');
        this._unmuteBtn?.addEventListener('click', (e) => {
            // Don't let the click reach the input-forwarding pipeline —
            // this is a UI control, not a game input.
            e.stopPropagation();
            this._unmuteControlDismissed = true;
            this._userUnmute();
        });
    }
    /**
     * Unmute the stream from a user gesture (click, key press, etc.).
     *
     * Prefer this method over `el.mute = false` from inside DOM event
     * handlers — it re-kicks `video.play()` and `audioElement.play()` inside
     * the user activation window, which Safari (and some Chrome versions)
     * require to actually start the audio pipeline after a muted autoplay.
     *
     * Setting the `mute` property is still fine for non-gesture contexts,
     * but won't help if the browser is currently blocking unmuted playback.
     */
    unmute() {
        this._userUnmute();
    }
    /**
     * Whether the active remote stream currently carries at least one
     * live audio track. Useful for custom unmute UIs that should only
     * render when there is actually audio to unmute.
     *
     * Updated whenever audio tracks arrive or end. Reflected via the
     * `audio-available` event.
     */
    get hasRemoteAudio() {
        return this._hasRemoteAudioTrack;
    }
    /**
     * Unmute the stream in response to a user gesture or button click.
     * Toggling `video.muted = false` is necessary but not always sufficient:
     * Safari (and some Chrome versions) won't actually start the audio
     * pipeline unless `play()` is called again from inside the gesture's
     * activation context. We call `play()` defensively for both elements.
     */
    _userUnmute() {
        if (this._mute) {
            this.mute = false;
        }
        // Re-kick playback inside the user activation window so the browser
        // promotes the now-unmuted media to the "playing with sound" state.
        this.video?.play().catch(() => { });
        const audioEl = this.webrtc?.getAudioElement();
        audioEl?.play().catch(() => { });
        this._updateUnmuteControl();
    }
    attributeChangedCallback(name, oldValue, newValue) {
        // Skip if value hasn't changed
        if (oldValue === newValue)
            return;
        switch (name) {
            // Existing attributes
            case 'native-touch':
                this._nativeTouch = newValue !== null;
                break;
            case 'pointer-lock':
                this._pointerLocked = newValue !== null;
                break;
            case 'pointer-lock-release':
                this._pointerLockRelease = newValue !== null;
                break;
            case 'suppress-browser-keys':
                this._suppressBrowserKeys = newValue !== null;
                break;
            case 'no-mouse-coalescing':
                this._noMouseCoalescing = newValue !== null;
                break;
            case 'no-touch-coalescing':
                this._noTouchCoalescing = newValue !== null;
                break;
            case 'enable-gamepad':
                if (newValue !== null && oldValue === null) {
                    this.gamepadInput.setup();
                }
                else if (newValue === null && oldValue !== null) {
                    this.gamepadInput.teardown();
                }
                break;
            case 'enable-xr':
                if (newValue !== null && oldValue === null) {
                    this.xrInput.setup();
                }
                else if (newValue === null && oldValue !== null) {
                    this.xrInput.teardown();
                }
                break;
            // Admission & session attributes
            case 'admission-token':
                this._admissionToken = newValue;
                this.dispatchEvent(new CustomEvent('admission-token-change', {
                    detail: { oldValue, newValue }
                }));
                // Auto-start session when token is set (if component is in DOM)
                if (newValue && this.isConnected) {
                    this._autoStartSession();
                }
                break;
            case 'app-id':
                this._appId = newValue;
                this.dispatchEvent(new CustomEvent('app-id-change', {
                    detail: { oldValue, newValue }
                }));
                break;
            case 'app-version':
                this._appVersion = newValue;
                this.dispatchEvent(new CustomEvent('app-version-change', {
                    detail: { oldValue, newValue }
                }));
                break;
            case 'no-auto-connect':
                this._noAutoConnect = newValue !== null;
                this.dispatchEvent(new CustomEvent('no-auto-connect-change', {
                    detail: { oldValue: oldValue !== null, newValue: newValue !== null }
                }));
                break;
            case 'mute': {
                const prev = this._mute;
                this._mute = newValue !== null;
                if (this.video) {
                    this.video.muted = this._mute;
                }
                this.webrtc.getAudioElement().muted = this._mute;
                this.dispatchEvent(new CustomEvent('mute-change', {
                    detail: { oldValue: oldValue !== null, newValue: newValue !== null, muted: this._mute }
                }));
                if (prev !== this._mute) {
                    this._onMuteChanged();
                }
                break;
            }
            case 'show-unmute':
                this._showUnmute = newValue !== null;
                this._updateUnmuteControl();
                break;
            case 'use-mic':
                this._useMicIntent = newValue !== null;
                if (this._streamState === 'streaming') {
                    if (this._useMicIntent) {
                        this.requestMicrophone().then(success => {
                            if (!success) {
                                this._useMicIntent = false;
                                this.removeAttribute('use-mic');
                            }
                        });
                    }
                    else {
                        this.disableMicrophone();
                    }
                }
                this.dispatchEvent(new CustomEvent('use-mic-change', {
                    detail: { oldValue: oldValue !== null, newValue: newValue !== null, microphone: this._useMicIntent }
                }));
                break;
            case 'volume': {
                const vol = Math.max(0, Math.min(1, Number(newValue) || 0));
                this._volume = vol;
                if (this.video) {
                    this.video.volume = vol;
                }
                this.webrtc.getAudioElement().volume = vol;
                this.dispatchEvent(new CustomEvent('volume-change', {
                    detail: { oldValue: oldValue ? Number(oldValue) : 1, newValue: vol }
                }));
                break;
            }
            case 'rendezvous-preference':
                this._rendezvousPreference = newValue !== null ? parseInt(newValue, 10) : null;
                this.dispatchEvent(new CustomEvent('rendezvous-preference-change', {
                    detail: { oldValue: oldValue ? parseInt(oldValue, 10) : null, newValue: this._rendezvousPreference }
                }));
                break;
            case 'linger-preference':
                this._lingerPreference = newValue !== null ? parseInt(newValue, 10) : null;
                this.dispatchEvent(new CustomEvent('linger-preference-change', {
                    detail: { oldValue: oldValue ? parseInt(oldValue, 10) : null, newValue: this._lingerPreference }
                }));
                break;
            case 'left-grace-period':
                this._leftGracePeriod = newValue !== null ? parseInt(newValue, 10) : null;
                this.dispatchEvent(new CustomEvent('left-grace-period-change', {
                    detail: { oldValue: oldValue ? parseInt(oldValue, 10) : null, newValue: this._leftGracePeriod }
                }));
                break;
            case 'force-relay':
                this._forceRelay = newValue !== null;
                break;
            case 'queue-wait-tolerance':
                this._queueWaitTolerance = newValue !== null ? parseInt(newValue, 10) : null;
                break;
            case 'webrtc-negotiation-tolerance':
                this._webrtcNegotiationTolerance = newValue !== null ? parseInt(newValue, 10) : null;
                break;
            // Session management
            case 'api-endpoint':
                this._apiEndpoint = newValue || 'api.interlucent.ai';
                this.dispatchEvent(new CustomEvent('api-endpoint-change', {
                    detail: { oldValue, newValue: this._apiEndpoint }
                }));
                break;
            // Reconnection configuration
            case 'reconnect-mode':
                if (newValue === 'none' || newValue === 'recover' || newValue === 'always') {
                    this._reconnectMode = newValue;
                }
                else if (newValue === null) {
                    this._reconnectMode = 'none';
                }
                else {
                    this._log.warn(`Invalid reconnect-mode: ${newValue}, using "none"`);
                    this._reconnectMode = 'none';
                }
                this._log.debug(`Reconnect mode set to: ${this._reconnectMode}`);
                break;
            case 'reconnect-attempts':
                if (newValue === null) {
                    this._reconnectAttempts = Infinity;
                }
                else {
                    const attempts = parseInt(newValue, 10);
                    if (isNaN(attempts) || attempts < -1) {
                        this._log.warn(`Invalid reconnect-attempts: ${newValue}, using Infinity`);
                        this._reconnectAttempts = Infinity;
                    }
                    else if (attempts === -1 || attempts === 0) {
                        this._reconnectAttempts = Infinity;
                    }
                    else {
                        this._reconnectAttempts = attempts;
                    }
                }
                this._log.debug(`Reconnect attempts set to: ${this._reconnectAttempts === Infinity ? 'Infinity' : this._reconnectAttempts}`);
                break;
            case 'reconnect-strategy':
                if (newValue === 'periodic' || newValue === 'exponential-backoff') {
                    this._reconnectStrategy = newValue;
                }
                else if (newValue === null) {
                    this._reconnectStrategy = 'exponential-backoff';
                }
                else {
                    this._log.warn(`Invalid reconnect-strategy: ${newValue}, using "exponential-backoff"`);
                    this._reconnectStrategy = 'exponential-backoff';
                }
                this._log.debug(`Reconnect strategy set to: ${this._reconnectStrategy}`);
                break;
            case 'reconnect-interval':
                if (newValue === null) {
                    this._reconnectInterval = PixelStream.DEFAULT_RECONNECT_INTERVAL;
                }
                else {
                    const interval = parseInt(newValue, 10);
                    if (isNaN(interval) || interval < 100) {
                        this._log.warn(`Invalid reconnect-interval: ${newValue}, using ${PixelStream.DEFAULT_RECONNECT_INTERVAL}ms`);
                        this._reconnectInterval = PixelStream.DEFAULT_RECONNECT_INTERVAL;
                    }
                    else {
                        this._reconnectInterval = interval;
                    }
                }
                this._log.debug(`Reconnect interval set to: ${this._reconnectInterval}ms`);
                break;
            case 'disconnect-grace-ms':
                if (newValue === null) {
                    this._disconnectGraceMs = 18000;
                }
                else {
                    const graceMs = parseInt(newValue, 10);
                    if (isNaN(graceMs) || graceMs < 0) {
                        this._log.warn(`Invalid disconnect-grace-ms: ${newValue}, using 18000ms`);
                        this._disconnectGraceMs = 18000;
                    }
                    else {
                        this._disconnectGraceMs = graceMs;
                    }
                }
                this._log.debug(`Disconnect grace period set to: ${this._disconnectGraceMs}ms`);
                break;
            case 'resize-mode':
                if (newValue === 'none' || newValue === 'auto' || newValue === 'pureweb' || newValue === 'pre-5.4' || newValue === '5.4+') {
                    this._resizeMode = newValue;
                }
                else if (newValue === null) {
                    this._resizeMode = 'pureweb';
                }
                else {
                    this._log.warn(`Invalid resize-mode: ${newValue}, using "pureweb"`);
                    this._resizeMode = 'pureweb';
                }
                // Reset detection state when mode changes
                this._detectedResizeMode = null;
                this._resizeDetectionInProgress = false;
                this._initialResizeSent = false;
                this._log.debug(`Resize mode set to: ${this._resizeMode}`);
                break;
            case 'dpr-cap':
                if (newValue === null) {
                    this._dprCapExplicit = false;
                    // Revert to implicit default: Infinity if clamp is active, else 1
                    this._dprCap = this._resolutionClamp ? Infinity : 1;
                }
                else {
                    this._dprCapExplicit = true;
                    const cap = parseFloat(newValue);
                    if (isNaN(cap) || cap <= 0) {
                        this._log.warn(`Invalid dpr-cap: ${newValue}, using 1`);
                        this._dprCap = 1;
                    }
                    else {
                        this._dprCap = cap;
                    }
                }
                this._log.debug(`DPR cap set to: ${this._dprCap}`);
                break;
            case 'resolution-clamp':
                if (newValue === null) {
                    this._resolutionClamp = null;
                    // Revert implicit dpr-cap to default (1) when clamp is removed
                    if (!this._dprCapExplicit) {
                        this._dprCap = 1;
                        this._log.debug('DPR cap reverted to 1 (resolution clamp removed)');
                    }
                }
                else {
                    const key = newValue.toLowerCase().trim();
                    let resolved = RESOLUTION_CLAMP_MAP[key] ?? null;
                    if (!resolved) {
                        const dims = key.match(/^(\d+)x(\d+)$/);
                        if (dims) {
                            const w = parseInt(dims[1], 10);
                            const h = parseInt(dims[2], 10);
                            if (w > 0 && h > 0) {
                                resolved = { maxWidth: w, maxHeight: h };
                            }
                        }
                    }
                    if (resolved) {
                        this._resolutionClamp = resolved;
                        // Auto-promote dpr-cap to Infinity so the clamp actually activates
                        // on high-DPI devices (without this, CSS pixels are often already
                        // below the clamp, making it a no-op).
                        if (!this._dprCapExplicit) {
                            this._dprCap = Infinity;
                            this._log.debug('DPR cap auto-promoted to Infinity (resolution clamp set)');
                        }
                    }
                    else {
                        this._log.warn(`Invalid resolution-clamp: "${newValue}". Use a named preset (${Object.keys(RESOLUTION_CLAMP_MAP).join(', ')}) or "WIDTHxHEIGHT" (e.g. "1080x512").`);
                        this._resolutionClamp = null;
                    }
                }
                this._log.debug(`Resolution clamp set to: ${this._resolutionClamp ? `${this._resolutionClamp.maxWidth}x${this._resolutionClamp.maxHeight}` : 'none'}`);
                break;
            case 'debug':
                this._debug = newValue !== null;
                // Update visual indicator based on current session state
                this._updateDebugIndicator();
                break;
            case 'controls':
                // Default-true attribute: absent or "false" → false, any other value → true
                this._controls = newValue !== null && newValue !== 'false';
                this._updateControlsOverlay();
                break;
            case 'swift-job-request':
                this._swiftJobRequest = newValue !== null;
                break;
            case 'streamer-id':
                this._streamerId = newValue;
                this._evaluateSubscription();
                break;
            case 'lat':
                this._lat = newValue !== null ? parseFloat(newValue) : null;
                if (this._lat !== null && isNaN(this._lat))
                    this._lat = null;
                break;
            case 'lng':
                this._lng = newValue !== null ? parseFloat(newValue) : null;
                if (this._lng !== null && isNaN(this._lng))
                    this._lng = null;
                break;
            // Stream quality
            case 'stream-quality':
                if (newValue === 'low-latency' || newValue === 'balanced' || newValue === 'quality') {
                    this._streamQuality = newValue;
                }
                else if (newValue === null) {
                    this._streamQuality = 'quality';
                }
                else {
                    this._log.warn(`Invalid stream-quality: ${newValue}, using "quality"`);
                    this._streamQuality = 'quality';
                }
                break;
            case 'video-bitrate-min': {
                this._videoBitrateMin = newValue !== null ? parseInt(newValue, 10) || null : null;
                break;
            }
            case 'video-bitrate-start': {
                this._videoBitrateStart = newValue !== null ? parseInt(newValue, 10) || null : null;
                break;
            }
            case 'video-bitrate-max': {
                this._videoBitrateMax = newValue !== null ? parseInt(newValue, 10) || null : null;
                break;
            }
            case 'log-level':
                if (newValue === 'error' || newValue === 'warn' || newValue === 'info' || newValue === 'debug') {
                    this._logLevel = newValue;
                }
                else if (newValue === null) {
                    this._logLevel = 'warn';
                }
                else {
                    this._log.warn(`Invalid log-level: ${newValue}, using "warn"`);
                    this._logLevel = 'warn';
                }
                break;
            // Idle timer
            case 'idle-timer':
                this._idleTimer = newValue !== null;
                if (this._idleTimer && this._status === 'streaming') {
                    this._enterIdleState('watching');
                }
                else if (!this._idleTimer) {
                    this._enterIdleState('off');
                }
                break;
            case 'idle-audible-warning':
                this._idleAudibleWarning = newValue !== null;
                break;
            case 'idle-watch-duration':
                this._idleWatchDuration = newValue !== null ? Math.max(0, Number(newValue) || 300000) : 300000;
                break;
            case 'idle-warning-duration':
                this._idleWarningDuration = newValue !== null ? Math.max(0, Number(newValue) || 5000) : 5000;
                break;
            case 'idle-countdown-duration':
                this._idleCountdownDuration = newValue !== null ? Math.max(1, Math.round(Number(newValue) || 30)) : 30;
                break;
            case 'preflight-deployment-check':
                this._preflightDeploymentCheck = newValue !== null;
                break;
            case 'show-requeue':
                this._showRequeue = newValue !== null;
                break;
            case 'guidance-level':
                if (newValue === 'none' || newValue === 'minimal' || newValue === 'verbose') {
                    this._guidanceLevel = newValue;
                }
                else if (newValue === null) {
                    this._guidanceLevel = 'none';
                }
                else {
                    this._log.warn(`Invalid guidance-level: ${newValue}, using "none"`);
                    this._guidanceLevel = 'none';
                }
                this._updatePendingLabel();
                break;
            case 'guidance-strings':
                if (newValue === null) {
                    this._guidanceStrings = null;
                }
                else {
                    try {
                        const parsed = JSON.parse(newValue);
                        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
                            this._guidanceStrings = parsed;
                        }
                        else {
                            this._log.warn('guidance-strings must be a JSON object');
                            this._guidanceStrings = null;
                        }
                    }
                    catch (err) {
                        this._log.warn('guidance-strings is not valid JSON', err);
                        this._guidanceStrings = null;
                    }
                }
                this._updatePendingLabel();
                break;
        }
    }
    connectedCallback() {
        this._log.info(`pixel-stream v${PixelStream.VERSION} mounted`);
        // Make host focusable so keyboard events target it properly
        if (!this.hasAttribute('tabindex')) {
            this.setAttribute('tabindex', '0');
        }
        this.setAttribute('job-state', this._jobState);
        this.setAttribute('session-state', this._sessionState);
        this.setAttribute('stream-state', this._streamState);
        this.setAttribute('status', this._status);
        this.mouseInput.setup();
        this.touchInput.setup();
        this.keyboardInput.setup();
        if (this.hasAttribute('enable-gamepad'))
            this.gamepadInput.setup();
        if (this.hasAttribute('enable-xr'))
            this.xrInput.setup();
        // Initialize admission & session attributes from HTML
        if (this.hasAttribute('admission-token')) {
            this._admissionToken = this.getAttribute('admission-token');
        }
        if (this.hasAttribute('app-id')) {
            this._appId = this.getAttribute('app-id');
        }
        if (this.hasAttribute('app-version')) {
            this._appVersion = this.getAttribute('app-version');
        }
        if (this.hasAttribute('no-auto-connect')) {
            this._noAutoConnect = true;
        }
        if (this.hasAttribute('mute')) {
            this._mute = true;
            this.video.muted = true;
            this.webrtc.getAudioElement().muted = true;
            // Arm the auto-unmute gesture listener immediately so the first
            // user interaction after the stream loads will restore audio.
            this._installAutoUnmuteListener();
        }
        if (this.hasAttribute('show-unmute')) {
            this._showUnmute = true;
        }
        if (this.hasAttribute('use-mic')) {
            this._useMicIntent = true;
        }
        if (this.hasAttribute('volume')) {
            const vol = Math.max(0, Math.min(1, Number(this.getAttribute('volume')) || 0));
            this._volume = vol;
            this.video.volume = vol;
            this.webrtc.getAudioElement().volume = vol;
        }
        if (this.hasAttribute('rendezvous-preference')) {
            this._rendezvousPreference = parseInt(this.getAttribute('rendezvous-preference'), 10);
        }
        if (this.hasAttribute('linger-preference')) {
            this._lingerPreference = parseInt(this.getAttribute('linger-preference'), 10);
        }
        if (this.hasAttribute('left-grace-period')) {
            this._leftGracePeriod = parseInt(this.getAttribute('left-grace-period'), 10);
        }
        if (this.hasAttribute('queue-wait-tolerance')) {
            this._queueWaitTolerance = parseInt(this.getAttribute('queue-wait-tolerance'), 10);
        }
        if (this.hasAttribute('webrtc-negotiation-tolerance')) {
            this._webrtcNegotiationTolerance = parseInt(this.getAttribute('webrtc-negotiation-tolerance'), 10);
        }
        if (this.hasAttribute('api-endpoint')) {
            this._apiEndpoint = this.getAttribute('api-endpoint') || 'api.interlucent.ai';
        }
        // controls defaults to true; only disable if explicitly set to "false"
        if (this.hasAttribute('controls')) {
            this._controls = this.getAttribute('controls') !== 'false';
        }
        this._updateControlsOverlay();
        if (this.hasAttribute('swift-job-request')) {
            this._swiftJobRequest = true;
        }
        if (this.hasAttribute('force-relay')) {
            this._forceRelay = true;
        }
        if (this.hasAttribute('streamer-id')) {
            this._streamerId = this.getAttribute('streamer-id');
        }
        // Idle timer attribute initialization
        if (this.hasAttribute('idle-watch-duration')) {
            this._idleWatchDuration = Math.max(0, Number(this.getAttribute('idle-watch-duration')) || 300000);
        }
        if (this.hasAttribute('idle-warning-duration')) {
            this._idleWarningDuration = Math.max(0, Number(this.getAttribute('idle-warning-duration')) || 5000);
        }
        if (this.hasAttribute('idle-countdown-duration')) {
            this._idleCountdownDuration = Math.max(1, Math.round(Number(this.getAttribute('idle-countdown-duration')) || 30));
        }
        if (this.hasAttribute('idle-timer')) {
            this._idleTimer = true;
        }
        if (this.hasAttribute('idle-audible-warning')) {
            this._idleAudibleWarning = true;
        }
        this.setAttribute('idle-state', this._idleState);
        // Notify consumers when the stream's intrinsic resolution changes
        // (e.g. after UE responds to a resize command).
        this.video.addEventListener('resize', () => {
            this.dispatchEvent(new CustomEvent('stream-resolution-change', {
                detail: {
                    width: this.video.videoWidth,
                    height: this.video.videoHeight,
                    ...(this._resolutionClamp ? {
                        clamp: {
                            maxWidth: this._resolutionClamp.maxWidth,
                            maxHeight: this._resolutionClamp.maxHeight,
                        },
                    } : {}),
                },
            }));
        });
        // Use PixelStreamResizeObserverForVideo to detect video element size changes.
        // This handles CSS changes, flexbox/grid, container resize, fullscreen, etc.
        // `settleMs: 0` matches PureWeb's reference SDK behavior of emitting on
        // every observer tick — UE's pixel streaming plugin has a narrow window
        // during session startup where it accepts resize commands, and debouncing
        // causes us to miss it on fast connections.
        this.resizeObserver = PixelStreamResizeObserverForVideo({
            el: this.video,
            dprCap: () => this._dprCap,
            settleMs: 0,
            onUpdate: (u) => this.updateCoordTranslator(u),
        });
        // Fire a resize as early as possible at every media-pipeline milestone.
        // PureWeb's reference SDK catches at least one of these states by virtue
        // of firing the initial resize synchronously on `srcObject` assign; we
        // cover the same window explicitly by hooking every lifecycle event.
        // Each call is cheap (same code path as the resize observer), and the
        // updateCoordTranslator gate on `dataChannel.readyState === 'open'`
        // means pre-channel calls no-op silently.
        const fireResize = () => {
            this.updateCoordTranslator({
                final: true,
                cssW: this.video.clientWidth,
                cssH: this.video.clientHeight,
                ...this._computeDeviceSize(this.video.clientWidth, this.video.clientHeight),
            });
        };
        this.video.addEventListener('loadstart', fireResize);
        this.video.addEventListener('loadedmetadata', fireResize);
        this.video.addEventListener('canplay', fireResize);
        this.video.addEventListener('playing', fireResize);
        // Also listen for fullscreen changes to handle fullscreen transitions
        this._boundFullscreenChangeHandler = () => {
            this.updateCoordTranslator({
                final: true,
                cssW: this.video.clientWidth,
                cssH: this.video.clientHeight,
                ...this._computeDeviceSize(this.video.clientWidth, this.video.clientHeight),
            });
        };
        document.addEventListener('fullscreenchange', this._boundFullscreenChangeHandler);
        this._boundWebkitFullscreenChangeHandler = () => {
            this.updateCoordTranslator({
                final: true,
                cssW: this.video.clientWidth,
                cssH: this.video.clientHeight,
                ...this._computeDeviceSize(this.video.clientWidth, this.video.clientHeight),
            });
        };
        document.addEventListener('webkitfullscreenchange', this._boundWebkitFullscreenChangeHandler);
        // Install pagehide listener so we send a clean leave-session on
        // tab close, navigation, or page reload.
        this._installPageHideListener();
        // Listen for initial-settings to extract UE version hints for auto resize detection
        this.addEventListener('initial-settings', this._onInitialSettings);
        // Send initial resize after UE responds with InitialSettings —
        // the ResizeObserver will have already fired (and been ignored
        // because the channel wasn't ready), and UE may ignore resizes
        // received before InitialSettings is processed.
        this._boundDataChannelOpenHandler = () => {
            this.updateCoordTranslator({
                final: true,
                cssW: this.video.clientWidth,
                cssH: this.video.clientHeight,
                ...this._computeDeviceSize(this.video.clientWidth, this.video.clientHeight),
            });
        };
        this.addEventListener('initial-settings', this._boundDataChannelOpenHandler);
        // Dispatch ready event with current configuration
        this.dispatchEvent(new CustomEvent('ready', {
            detail: {
                admissionConfig: this.getAdmissionConfig(),
                sessionPreferences: this.getSessionPreferences(),
                noAutoConnect: this._noAutoConnect,
                mute: this._mute
            }
        }));
        // Auto-connect session when admission token is present (unless no-auto-connect)
        if (this._admissionToken) {
            this._autoStartSession();
        }
    }
    disconnectedCallback() {
        // Clear idle timer
        this._enterIdleState('off');
        // Clear the Quiet Disc silence timer to prevent leaks
        if (this._pendingSilenceTimer) {
            clearTimeout(this._pendingSilenceTimer);
            this._pendingSilenceTimer = null;
        }
        // Remove page-hide listener (registered in connectedCallback)
        this._removePageHideListener();
        // Remove fullscreenchange listeners
        if (this._boundFullscreenChangeHandler) {
            document.removeEventListener('fullscreenchange', this._boundFullscreenChangeHandler);
            this._boundFullscreenChangeHandler = null;
        }
        if (this._boundWebkitFullscreenChangeHandler) {
            document.removeEventListener('webkitfullscreenchange', this._boundWebkitFullscreenChangeHandler);
            this._boundWebkitFullscreenChangeHandler = null;
        }
        // Clean up initial-resize listener
        if (this._boundDataChannelOpenHandler) {
            this.removeEventListener('initial-settings', this._boundDataChannelOpenHandler);
            this._boundDataChannelOpenHandler = null;
        }
        // Clean up resize observer
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
            this.resizeObserver = null;
        }
        // Clean up resize detection
        this._cancelResizeDetection();
        this.removeEventListener('initial-settings', this._onInitialSettings);
        this.mouseInput.teardown();
        this.gamepadInput.teardown();
        this.xrInput.teardown();
        this.cleanupEventListeners();
        this.webrtc.closePeerConnection();
        this.webrtc.disableMicrophone();
        // Terminate session immediately — close WS without sending messages.
        // During unmount the element is being torn down; message sends may not
        // complete and the close handshake can leave ghost connections server-side.
        this.session.terminateSession('component-unmounted');
    }
    _installPageHideListener() {
        if (this._boundPageHideHandler)
            return; // already installed
        this._boundPageHideHandler = () => {
            // Use terminateSession directly — during pagehide the browser is unloading
            // and ws.send()/ws.close() handshakes won't complete, leaving ghost connections.
            this.session.terminateSession('page-hide');
        };
        window.addEventListener('pagehide', this._boundPageHideHandler);
    }
    _removePageHideListener() {
        if (this._boundPageHideHandler) {
            window.removeEventListener('pagehide', this._boundPageHideHandler);
            this._boundPageHideHandler = null;
        }
    }
    cleanupEventListeners() {
        this.keyboardInput.teardown();
        this.mouseInput.teardown();
        this.touchInput.teardown();
    }
    // ===== XR PUBLIC API (delegated) =====
    async startXRSession() { return this.xrInput.startSession(); }
    endXRSession() { this.xrInput.endSession(); }
    static async isXRSupported() { return XRInputHandler.isSupported(); }
    // ===== METRICS PUBLIC API (delegated) =====
    resetPerformanceMetrics() { this.metrics.resetPerformanceMetrics(); }
    getPerformanceMetrics() { return this.metrics.getPerformanceMetrics(); }
    async collectAndDisplaySessionStats() { return this.metrics.collectAndDisplaySessionStats(); }
    // ===== UTILITY METHODS =====
    _computeDeviceSize(cssW, cssH) {
        const dpr = Math.min(window.devicePixelRatio || 1, this._dprCap);
        let devW = Math.round(cssW * dpr);
        let devH = Math.round(cssH * dpr);
        devW = devW & -2;
        devH = devH & -2;
        devW = Math.max(2, devW);
        devH = Math.max(2, devH);
        return { dpr, devW, devH };
    }
    /**
     * Clamp device-pixel dimensions to the configured resolution cap,
     * preserving aspect ratio (PureWeb-compatible algorithm).
     * Returns dimensions unchanged when no clamp is set.
     */
    _applyResolutionClamp(devW, devH) {
        const clamp = this._resolutionClamp;
        if (!clamp)
            return { devW, devH };
        // Swap max bounds when viewport is portrait
        const isLandscape = devW >= devH;
        const maxW = isLandscape ? clamp.maxWidth : clamp.maxHeight;
        const maxH = isLandscape ? clamp.maxHeight : clamp.maxWidth;
        // Already within bounds
        if (devW <= maxW && devH <= maxH)
            return { devW, devH };
        // Aspect-ratio-preserving clamp
        const targetRatio = devW / devH;
        const maxRatio = maxW / maxH;
        let clampedW;
        let clampedH;
        if (targetRatio > maxRatio) {
            // Target is wider than max: clamp width, derive height
            clampedW = Math.min(devW, maxW);
            clampedH = Math.round(clampedW / targetRatio);
        }
        else {
            // Target is taller: clamp height, derive width
            clampedH = Math.min(devH, maxH);
            clampedW = Math.round(clampedH * targetRatio);
        }
        // Re-enforce even pixel alignment
        clampedW = clampedW & -2;
        clampedH = clampedH & -2;
        clampedW = Math.max(2, clampedW);
        clampedH = Math.max(2, clampedH);
        return { devW: clampedW, devH: clampedH };
    }
    updateCoordTranslator({ final, devW, devH }) {
        // Only send resize commands on final (settled) updates
        if (!final)
            return;
        // Apply resolution clamp (only affects what's sent to server, not input coord mapping)
        const clamped = this._applyResolutionClamp(devW, devH);
        const sendW = clamped.devW;
        const sendH = clamped.devH;
        const wasClamped = sendW !== devW || sendH !== devH;
        // Dispatch resolution change event with clamp details
        this.dispatchEvent(new CustomEvent('stream-resolution-change', {
            detail: {
                width: sendW,
                height: sendH,
                viewport: { width: devW, height: devH },
                ...(this._resolutionClamp ? {
                    clamp: {
                        maxWidth: this._resolutionClamp.maxWidth,
                        maxHeight: this._resolutionClamp.maxHeight,
                        applied: wasClamped,
                    },
                } : {}),
            },
        }));
        if (wasClamped) {
            this._log.debug(`Resolution clamped: ${devW}x${devH} → ${sendW}x${sendH} (max ${this._resolutionClamp.maxWidth}x${this._resolutionClamp.maxHeight})`);
        }
        // Send resize message based on resize mode
        if (this._resizeMode !== 'none' && this.webrtc.getDataChannel()?.readyState === 'open') {
            if (this._resizeMode === 'auto') {
                if (!this._initialResizeSent) {
                    // First resize in auto mode: shotgun all formats for guaranteed initial size
                    this._log.info(`Sending initial resize shotgun: ${sendW}x${sendH} (all formats)`);
                    this._sendResizeWithMode('5.4+', sendW, sendH);
                    this._sendResizeWithMode('pre-5.4', sendW, sendH);
                    this._sendResizeWithMode('pureweb', sendW, sendH);
                    this._initialResizeSent = true;
                    // Start detection for subsequent resizes
                    if (!this._detectedResizeMode && !this._resizeDetectionInProgress) {
                        this._startResizeDetection(sendW, sendH);
                    }
                }
                else if (this._detectedResizeMode) {
                    // Subsequent resize with detected mode
                    this._sendResizeWithMode(this._detectedResizeMode, sendW, sendH);
                }
                else {
                    // Subsequent resize but detection not complete - use 5.4+ as default
                    this._sendResizeWithMode('5.4+', sendW, sendH);
                }
            }
            else {
                // Explicit mode - just send that format
                this._sendResizeWithMode(this._resizeMode, sendW, sendH);
            }
        }
    }
    /**
     * Send resize command using the specified mode.
     */
    _sendResizeWithMode(mode, devW, devH) {
        if (mode === 'pureweb') {
            // Pureweb plugin mode: UIInteraction with action '1' (WindowResize).
            // Wire format is byte-for-byte identical to PureWeb's reference SDK
            // (platform-sdk-js InputEmitter.ts EmitResize): the body must contain
            // `type: 50` (MessageType.UIInteraction) alongside width/height/action.
            this.sendUIInteraction({
                type: 50,
                width: devW,
                height: devH,
                action: '1'
            });
            this._log.debug(`Sent Pureweb resize: ${devW}x${devH}`);
        }
        else if (mode === 'pre-5.4') {
            // Legacy mode: UIInteraction with Console command
            this.sendUIInteraction({
                Console: `r.setres ${devW}x${devH}w`
            });
            this._log.debug(`Sent legacy resize command: r.setres ${devW}x${devH}w`);
        }
        else if (mode === '5.4+') {
            // Modern mode: Command with Resolution.Width/Height
            this.sendCommand({
                'Resolution.Width': devW,
                'Resolution.Height': devH
            });
            this._log.debug(`Sent modern resize command: ${devW}x${devH}`);
        }
    }
    /**
     * Start auto-detection of resize mode by monitoring video dimensions.
     * Tries modes in order: ['5.4+', 'pre-5.4', 'pureweb']
     */
    _startResizeDetection(devW, devH) {
        if (this._resizeDetectionInProgress)
            return;
        this._resizeDetectionInProgress = true;
        this._lastResizeDimensions = { width: devW, height: devH };
        const modesToTry = ['5.4+', 'pre-5.4', 'pureweb'];
        let currentModeIndex = 0;
        // Record initial video dimensions
        const initialWidth = this.video.videoWidth;
        const initialHeight = this.video.videoHeight;
        this._log.info(`Starting resize auto-detection. Target: ${devW}x${devH}, Initial video: ${initialWidth}x${initialHeight}`);
        const tryNextMode = () => {
            if (currentModeIndex >= modesToTry.length) {
                // Exhausted all modes - fall back to 5.4+ (most common)
                this._log.warn('Resize auto-detection: no mode verified, defaulting to 5.4+');
                this._detectedResizeMode = '5.4+';
                this._resizeDetectionInProgress = false;
                this._dispatchResizeModeDetected('5.4+', false);
                return;
            }
            const mode = modesToTry[currentModeIndex];
            this._log.debug(`Resize auto-detection: trying ${mode} (attempt ${currentModeIndex + 1}/${modesToTry.length})`);
            // Send resize with this mode
            this._sendResizeWithMode(mode, devW, devH);
            // Wait and check if video dimensions changed toward target
            this._resizeVerifyTimer = setTimeout(() => {
                const newWidth = this.video.videoWidth;
                const newHeight = this.video.videoHeight;
                // Check if dimensions changed toward our target
                // Allow some tolerance (within 10% or exact match)
                const widthOk = Math.abs(newWidth - devW) < devW * 0.1 || newWidth !== initialWidth;
                const heightOk = Math.abs(newHeight - devH) < devH * 0.1 || newHeight !== initialHeight;
                const dimensionsChanged = newWidth !== initialWidth || newHeight !== initialHeight;
                if (dimensionsChanged && widthOk && heightOk) {
                    // Success! This mode works
                    this._log.info(`Resize auto-detection: ${mode} verified (video: ${newWidth}x${newHeight})`);
                    this._detectedResizeMode = mode;
                    this._resizeDetectionInProgress = false;
                    this._dispatchResizeModeDetected(mode, true);
                }
                else {
                    // Try next mode
                    this._log.debug(`Resize auto-detection: ${mode} not verified (video: ${newWidth}x${newHeight})`);
                    currentModeIndex++;
                    tryNextMode();
                }
            }, 500); // 500ms timeout per mode attempt
        };
        // Start trying modes
        tryNextMode();
    }
    /**
     * Dispatch event when resize mode is detected.
     */
    _dispatchResizeModeDetected(mode, verified) {
        this.dispatchEvent(new CustomEvent('resize-mode-detected', {
            detail: { mode, verified }
        }));
    }
    /**
     * Cancel any pending resize detection and reset state.
     */
    _cancelResizeDetection() {
        if (this._resizeVerifyTimer) {
            clearTimeout(this._resizeVerifyTimer);
            this._resizeVerifyTimer = null;
        }
        this._resizeDetectionInProgress = false;
        this._initialResizeSent = false;
        this._detectedResizeMode = null;
    }
    /**
     * Handle InitialSettings message to extract UE version hints for resize mode selection.
     * Called from webrtc.ts when InitialSettings is received.
     */
    _handleInitialSettingsForResize(settings) {
        if (this._resizeMode !== 'auto' || this._detectedResizeMode)
            return;
        // Try to extract UE version info from InitialSettings
        // Common fields: PixelStreamingSettings.WebRTCDisableResolutionChange, etc.
        const version = settings?.PixelStreamingSettings?.EngineVersion
            || settings?.EngineVersion
            || settings?.UEVersion;
        if (version) {
            this._log.debug(`InitialSettings contains version: ${version}`);
            // Parse version to determine best resize mode
            const detectedMode = this._inferResizeModeFromVersion(version);
            if (detectedMode) {
                this._log.info(`Inferred resize mode from UE version ${version}: ${detectedMode}`);
                this._detectedResizeMode = detectedMode;
                this._dispatchResizeModeDetected(detectedMode, false);
            }
        }
        // Check for explicit resize capability flags
        const webrtcDisableResize = settings?.PixelStreamingSettings?.WebRTCDisableResolutionChange;
        if (webrtcDisableResize === false) {
            // UE has resize enabled via WebRTC - likely 5.4+
            this._log.debug('InitialSettings indicates WebRTC resize is enabled');
            if (!this._detectedResizeMode) {
                this._detectedResizeMode = '5.4+';
                this._dispatchResizeModeDetected('5.4+', false);
            }
        }
    }
    /**
     * Infer resize mode from UE version string.
     */
    _inferResizeModeFromVersion(version) {
        // Parse version like "5.4.0", "5.3.2", "4.27.2", etc.
        const match = version.match(/(\d+)\.(\d+)/);
        if (!match)
            return null;
        const major = parseInt(match[1], 10);
        const minor = parseInt(match[2], 10);
        if (major >= 5 && minor >= 4) {
            return '5.4+';
        }
        else if (major >= 4) {
            return 'pre-5.4';
        }
        return null;
    }
    /**
     * Detect if the current stream is an XR/stereo stream based on track heuristics.
     * Updates _isXRStream and dispatches 'xr-stream-detected' event if detected.
     */
    _detectXRStream() {
        const tracks = Array.from(this.webrtc.getVideoTracks().entries());
        const wasXRStream = this._isXRStream;
        // Signal 1: Track labels contain XR-related keywords
        const hasXRLabels = tracks.some(([_, { track }]) => /left|right|stereo|xr|hmd|vr/i.test(track.label));
        // Signal 2: Exactly 2 video tracks (stereo pair)
        const hasStereoTracks = tracks.length === 2;
        // Signal 3: Side-by-side stereo aspect ratio (>3.5:1)
        // Wait for video metadata to check aspect ratio
        const checkAspectRatio = () => {
            if (this.video.videoWidth === 0 || this.video.videoHeight === 0) {
                // Metadata not loaded yet, try again on loadedmetadata
                this.video.addEventListener('loadedmetadata', checkAspectRatio, { once: true });
                return;
            }
            const aspectRatio = this.video.videoWidth / this.video.videoHeight;
            const hasStereoAspect = aspectRatio > 3.5;
            // Detect XR if we have strong signals
            const isXR = hasXRLabels || (hasStereoTracks && hasStereoAspect);
            if (isXR && !wasXRStream) {
                this._isXRStream = true;
                this._log.debug('Stream detected as XR/stereo', {
                    tracks: tracks.length,
                    aspectRatio: aspectRatio.toFixed(2),
                    resolution: `${this.video.videoWidth}x${this.video.videoHeight}`,
                    hasXRLabels,
                    hasStereoTracks,
                    hasStereoAspect
                });
                // Dispatch XR detection event
                this.dispatchEvent(new CustomEvent('xr-stream-detected', {
                    detail: {
                        trackCount: this.webrtc.getVideoTracks().size,
                        resolution: `${this.video.videoWidth}x${this.video.videoHeight}`,
                        aspectRatio,
                        hasXRLabels,
                        hasStereoTracks,
                        hasStereoAspect
                    }
                }));
            }
            else if (!isXR && wasXRStream) {
                // Stream changed from XR to non-XR
                this._isXRStream = false;
                this._log.debug('Stream no longer detected as XR');
            }
        };
        checkAspectRatio();
    }
    // ===== WEBRTC PUBLIC API (delegated to WebRTCManager) =====
    /** Send message to UE via WebRTC data channel */
    sendToStreamer(type, data = []) {
        // MouseEnter/MouseLeave are boundary signals, not user activity
        if (type !== 'MouseEnter' && type !== 'MouseLeave') {
            const now = Date.now();
            if (now - this._lastUserInteraction > 1000) {
                this.dispatchEvent(new CustomEvent('interlucent:user-interaction', { detail: { type } }));
            }
            this._lastUserInteraction = now;
            // Reset idle timer on genuine user activity
            if (this._idleState === 'grace' || this._idleState === 'countdown') {
                this._enterIdleState('watching');
            }
        }
        this.webrtc.sendToStreamer(type, data);
    }
    /** Send raw binary data over the data channel */
    sendRawBinary(buffer) { this.webrtc.sendRawBinary(buffer); }
    // Data channel commands
    sendCommand(command) { this.webrtc.sendCommand(command); }
    sendUIInteraction(descriptor) { this.webrtc.sendUIInteraction(descriptor); }
    requestIFrame() { this.webrtc.requestIFrame(); }
    requestKeyFrame() { this.webrtc.requestKeyFrame(); }
    requestQualityControl() { this.sendToStreamer('RequestQualityControl', []); }
    requestFps(fps) { this.sendToStreamer('FpsRequest', [fps]); }
    requestBitrate() { this.sendToStreamer('AverageBitrateRequest', []); }
    startStreaming() { this.sendToStreamer('StartStreaming', []); }
    stopStreaming() { this.sendToStreamer('StopStreaming', []); }
    sendTestEcho(message) { this.sendToStreamer('TestEcho', [message]); }
    // Permissions (host-side — used by context interface for WebRTCManager)
    async checkPermission(permission) {
        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
            return 'unavailable';
        }
        if (navigator.permissions && navigator.permissions.query) {
            try {
                const result = await navigator.permissions.query({ name: permission });
                return result.state;
            }
            catch {
                // Permissions API not supported for this permission
            }
        }
        return 'prompt';
    }
    async checkPermissions(permissions) {
        const results = {};
        for (const p of permissions) {
            results[p] = await this.checkPermission(p);
        }
        return results;
    }
    async watchPermission(permission) {
        if (!navigator.permissions || !navigator.permissions.query)
            return;
        try {
            const status = await navigator.permissions.query({ name: permission });
            status.addEventListener('change', () => {
                this.dispatchEvent(new CustomEvent('permission-change', {
                    detail: { permission, state: status.state }
                }));
            });
        }
        catch {
            // Not supported
        }
    }
    // Microphone (delegated)
    async requestMicrophone() { return this.webrtc.requestMicrophone(); }
    async enableMicrophone() { return this.webrtc.enableMicrophone(); }
    disableMicrophone() { this.webrtc.disableMicrophone(); }
    setMicrophoneMuted(muted) { this.webrtc.setMicrophoneMuted(muted); }
    get isMicrophoneEnabled() { return this.webrtc.isMicrophoneEnabled; }
    get isMicrophoneMuted() { return this.webrtc.isMicrophoneMuted; }
    // Multiple camera support (delegated to WebRTCManager)
    getVideoTracks() {
        const activeId = this.webrtc.getActiveVideoTrackId();
        return Array.from(this.webrtc.getVideoTracks().entries()).map(([trackId, { track }]) => ({
            trackId,
            label: track.label || `Camera ${trackId}`,
            isActive: trackId === activeId,
        }));
    }
    switchVideoTrack(trackId) { this.webrtc.switchVideoTrack(trackId); }
    getActiveVideoTrack() {
        const activeId = this.webrtc.getActiveVideoTrackId();
        if (!activeId)
            return null;
        const trackInfo = this.webrtc.getVideoTracks().get(activeId);
        if (!trackInfo)
            return null;
        return { trackId: activeId, label: trackInfo.track.label || `Camera ${activeId}` };
    }
    // ===== SESSION MANAGEMENT =====
    /**
     * Mark this client as admitted to a session.
     * Call this after successful authentication/admission.
     */
    _setAdmitted(admitted) {
        const wasAdmitted = this._isAdmitted;
        this._isAdmitted = admitted;
        if (admitted && !wasAdmitted) {
            this.dispatchEvent(new CustomEvent('admitted', {
                detail: {
                    admissionConfig: this.getAdmissionConfig(),
                    sessionPreferences: this.getSessionPreferences()
                }
            }));
            // Start rendezvous timer if configured
            if (this._rendezvousPreference && this._rendezvousPreference > 0) {
                this._startRendezvousTimer();
            }
        }
        else if (!admitted && wasAdmitted) {
            this.dispatchEvent(new CustomEvent('admission-revoked', {
                detail: { reason: 'revoked' }
            }));
            this._cancelRendezvousTimer();
            this._cancelLingerTimer();
        }
    }
    /**
     * Start the rendezvous timer. If no peer connects within the preference time,
     * 'rendezvous-timeout' event is fired.
     */
    _startRendezvousTimer() {
        this._cancelRendezvousTimer();
        if (!this._rendezvousPreference || this._rendezvousPreference <= 0) {
            return;
        }
        this._log.info(`Starting rendezvous timer: ${this._rendezvousPreference}s`);
        this.dispatchEvent(new CustomEvent('rendezvous-started', {
            detail: { timeoutSeconds: this._rendezvousPreference }
        }));
        this._rendezvousTimer = setTimeout(() => {
            this._log.info('Rendezvous timeout - no peer connected');
            this.dispatchEvent(new CustomEvent('rendezvous-timeout', {
                detail: {
                    timeoutSeconds: this._rendezvousPreference,
                    admissionConfig: this.getAdmissionConfig()
                }
            }));
        }, this._rendezvousPreference * 1000);
    }
    /**
     * Cancel the rendezvous timer (e.g., when a peer connects)
     */
    _cancelRendezvousTimer() {
        if (this._rendezvousTimer) {
            clearTimeout(this._rendezvousTimer);
            this._rendezvousTimer = null;
            this.dispatchEvent(new CustomEvent('rendezvous-cancelled'));
        }
    }
    /**
     * Start the linger timer. Called when all peers have left.
     * If no peer reconnects within the preference time, 'linger-timeout' is fired.
     */
    _startLingerTimer() {
        this._cancelLingerTimer();
        if (!this._lingerPreference || this._lingerPreference <= 0) {
            // No linger preference, disconnect immediately
            this.dispatchEvent(new CustomEvent('linger-timeout', {
                detail: { timeout: 0, reason: 'no-linger-preference' }
            }));
            return;
        }
        this._log.info(`Starting linger timer: ${this._lingerPreference}s`);
        this.dispatchEvent(new CustomEvent('linger-started', {
            detail: { timeoutSeconds: this._lingerPreference }
        }));
        this._lingerTimer = setTimeout(() => {
            this._log.info('Linger timeout - disconnecting');
            this.dispatchEvent(new CustomEvent('linger-timeout', {
                detail: {
                    timeoutSeconds: this._lingerPreference,
                    reason: 'timeout'
                }
            }));
        }, this._lingerPreference * 1000);
    }
    /**
     * Cancel the linger timer (e.g., when a peer reconnects)
     */
    _cancelLingerTimer() {
        if (this._lingerTimer) {
            clearTimeout(this._lingerTimer);
            this._lingerTimer = null;
            this.dispatchEvent(new CustomEvent('linger-cancelled'));
        }
    }
    /**
     * Notify that a peer streamer has connected.
     * This cancels the rendezvous timer and any linger timer.
     */
    _notifyPeerConnected(peerId) {
        this._cancelRendezvousTimer();
        this._cancelLingerTimer();
        this.dispatchEvent(new CustomEvent('peer-connected', {
            detail: { peerId }
        }));
    }
    /**
     * Notify that a peer streamer has disconnected.
     * If this was the last peer, starts the linger timer.
     * @param peerId The peer that disconnected
     * @param isLastPeer Whether this was the last peer in the session
     */
    _notifyPeerDisconnected(peerId, isLastPeer = true) {
        this.dispatchEvent(new CustomEvent('peer-disconnected', {
            detail: { peerId, isLastPeer }
        }));
        if (isLastPeer) {
            this._startLingerTimer();
        }
    }
    /**
     * Validate that the admission token is present (required attribute).
     * Throws an error if not set.
     */
    _validateAdmission() {
        if (!this._admissionToken) {
            const error = new Error('admission-token is required but not set');
            this.dispatchEvent(new CustomEvent('admission-error', {
                detail: { error: error.message }
            }));
            throw error;
        }
        return this.getAdmissionConfig();
    }
    /**
     * Get the last received file from UE (if available)
     * Returns null if no file has been received or file is incomplete
     */
    getLastReceivedFile() {
        return this.fileTransfer.getLastReceivedFile();
    }
    /**
     * Wait for the next file to be received from UE
     * Returns a promise that resolves when a complete file is received
     *
     * @example
     * const file = await player.waitForFile();
     * const url = URL.createObjectURL(file.blob);
     * const a = document.createElement('a');
     * a.href = url;
     * a.download = `file.${file.extension}`;
     * a.click();
     */
    async waitForFile() {
        return this.fileTransfer.waitForFile();
    }
    /**
     * Set the video source (for testing or manual stream setup)
     */
    setVideoSource(srcObject) {
        this.video.srcObject = srcObject;
    }
    // ===== SESSION CONNECTION (delegated to SessionManager) =====
    /**
     * Start a session by exchanging the admission token for an access token,
     * then connecting the WebSocket for signalling.
     */
    async startSession() {
        await this.session.startSession();
    }
    /**
     * Auto-start session if conditions are met.
     * Called when admission token is set or component is connected to DOM.
     */
    _autoStartSession() {
        if (this._noAutoConnect)
            return;
        if (this._sessionState !== 'idle' && this._sessionState !== 'error')
            return;
        this.startSession().catch(err => this._log.error('Auto session start failed', err));
    }
    /**
     * Send a message through the session WebSocket.
     */
    sendSessionMessage(message) {
        this.session.sendMessage(message);
    }
    // ===== JOB CONTROLS =====
    /**
     * Update internal job state and dispatch state change events.
     */
    _setJobState(state) {
        if (this._jobState === state)
            return;
        const oldState = this._jobState;
        this._jobState = state;
        this._log.debug(`Job state: ${oldState} -> ${state}`);
        this.setAttribute('job-state', state);
        this.dispatchEvent(new CustomEvent('job-state-change', {
            detail: { oldState, newState: state }
        }));
        this._updateControlsOverlay();
        this._recomputeStatus();
    }
    /**
     * Update the controls overlay visibility and dynamic text.
     * CSS handles state container visibility via :host([status="..."]) selectors.
     */
    _updateControlsOverlay() {
        if (this._hasSlottedOverlay) {
            // Slotted: always show the container. The overlay provider
            // decides its own visibility via [status], [job-state], etc.
            this.controlsOverlay.classList.add('visible');
            return;
        }
        // Error, ended, ready, and resilience states always show regardless of controls attribute
        const alwaysShow = this._status === 'failed' || this._status === 'ended' || this._jobState === 'ready'
            || this._status === 'interrupted' || this._status === 'recovering';
        if (!this._controls && !alwaysShow) {
            this.controlsOverlay.classList.remove('visible');
            return;
        }
        this.controlsOverlay.classList.add('visible');
        // Update dynamic text content
        this._updatePendingLabel();
        this._updateErrorMessage();
    }
    // ── Mute / unmute affordance ────────────────────────────────────
    /**
     * Called by webrtc when an audio track arrives (or ends). Drives the
     * visibility of the built-in unmute button, which only shows when there
     * is actually audio to unmute.
     */
    _setHasRemoteAudio(value) {
        if (this._hasRemoteAudioTrack === value)
            return;
        this._hasRemoteAudioTrack = value;
        this.dispatchEvent(new CustomEvent('audio-available', {
            detail: { available: value }
        }));
        this._updateUnmuteControl();
    }
    /**
     * Show the corner unmute button when all of these are true:
     *   - `show-unmute` attribute is set
     *   - host is currently muted
     *   - status is `streaming`
     *   - the remote stream carries at least one audio track
     *   - the control hasn't already been dismissed in this session
     */
    _updateUnmuteControl() {
        if (!this._unmuteBtn)
            return;
        const show = this._showUnmute &&
            this._mute &&
            this._status === 'streaming' &&
            this._hasRemoteAudioTrack &&
            !this._unmuteControlDismissed;
        this._unmuteBtn.classList.toggle('visible', show);
    }
    /**
     * Install a one-shot capture-phase gesture listener that auto-unmutes
     * the stream on the user's next interaction. Listens for pointerdown
     * and keydown. Non-blocking: does not preventDefault or stopPropagation,
     * so the same gesture still reaches the UE input pipeline.
     */
    _installAutoUnmuteListener() {
        if (this._autoUnmuteHandler)
            return;
        const handler = (_e) => {
            this._removeAutoUnmuteListener();
            this._userUnmute();
        };
        this._autoUnmuteHandler = handler;
        this.addEventListener('pointerdown', handler, { capture: true });
        this.addEventListener('keydown', handler, { capture: true });
    }
    _removeAutoUnmuteListener() {
        if (!this._autoUnmuteHandler)
            return;
        this.removeEventListener('pointerdown', this._autoUnmuteHandler, { capture: true });
        this.removeEventListener('keydown', this._autoUnmuteHandler, { capture: true });
        this._autoUnmuteHandler = null;
    }
    /** Short user-facing title (amber/red ring partners this). */
    _failureReasonTitle(reason) {
        const override = this._guidanceStrings?.[`failed-${reason ?? ''}`];
        if (typeof override === 'string')
            return override;
        switch (reason) {
            case 'no_deployment': return "This app isn't deployed at this time.";
            case 'token_expired': return 'Session expired';
            case 'unauthorized': return 'Access denied';
            case 'server_error': return 'Server error';
            case 'request_failed': return 'Could not reach server';
            case 'connection_failed': return 'Connection failed';
            case 'disconnected': return 'Connection lost';
            case 'reconnect_exhausted': return "Couldn't recover the stream";
            case 'rendezvous_exhausted': return "Couldn't find a working streamer";
            case 'no_access_token': return 'Session recovery unavailable';
            case 'not_connected': return 'Session not connected';
            case 'media_pipeline_failure': return 'Media pipeline failed';
            case 'rendezvous_timeout': return 'No streamer available right now';
            case 'queue_wait_timeout': return 'Still waiting for a streamer';
            case 'webrtc_sdp_timeout': return "Couldn't agree on a media connection";
            case 'webrtc_negotiation_timeout': return "Couldn't establish a media connection";
            case 'worker_left': return 'Streamer disconnected';
            default: return reason ? 'Something went wrong' : 'Session ended';
        }
    }
    /** Detail string — historically `.error-message`. Kept for consumer read-back. */
    _failureReasonLabel(reason) {
        switch (reason) {
            case 'no_deployment': return 'Contact your administrator.';
            case 'token_expired': return 'Please reload and sign in again.';
            case 'unauthorized': return 'Invalid admission token.';
            case 'server_error': return 'Try again in a moment.';
            case 'request_failed': return 'The server was unreachable.';
            case 'connection_failed': return 'The media connection failed to establish.';
            case 'disconnected': return 'Your connection dropped.';
            case 'reconnect_exhausted': return 'Couldn’t restore the connection after multiple attempts.';
            case 'rendezvous_exhausted': return 'Every available streamer failed to connect. Try again.';
            case 'no_access_token': return 'Start a new session to continue.';
            case 'not_connected': return 'Session was not connected.';
            case 'media_pipeline_failure': return 'The stream encountered an error.';
            case 'rendezvous_timeout': return 'Try again or switch pools.';
            case 'queue_wait_timeout': return 'Try again in a moment.';
            case 'webrtc_sdp_timeout': return 'Signaling exchange timed out.';
            case 'webrtc_negotiation_timeout': return "Your network may be blocking media traffic.";
            case 'worker_left': return 'Your streamer dropped the session.';
            default: return reason ?? '';
        }
    }
    /**
     * Pre-stream phases. The label stays empty during the silence window,
     * then shows `_PENDING_PRIMARY[status]` (plus `_PENDING_VERBOSE` when
     * guidance-level === 'verbose'). Unhappy paths (`failed`, `ended`) are
     * handled by {@link _updateErrorMessage}.
     */
    _updatePendingLabel() {
        if (!this.controlsOverlay)
            return; // Attribute may land before shadow DOM is built.
        const pendingLabel = this.controlsOverlay.querySelector('[data-pending-msg]');
        const disc = this.controlsOverlay.querySelector('.state-pending .status-disc');
        const errorDisc = this.controlsOverlay.querySelector('.state-error .status-disc');
        if (this._pendingSilenceTimer) {
            clearTimeout(this._pendingSilenceTimer);
            this._pendingSilenceTimer = null;
        }
        // Clear the disc ring by default — only unhappy paths opt in.
        disc?.removeAttribute('data-ring');
        errorDisc?.setAttribute('data-ring', this._failureRingColor(this._failureReason));
        const isPrestream = this._status in PixelStream._PENDING_PRIMARY;
        if (pendingLabel && isPrestream) {
            const statusChanged = this._lastAnnouncedStatus !== this._status;
            if (statusChanged) {
                this._pendingPhaseStartedAt = Date.now();
            }
            // Forward-progress states render immediately so each real
            // transition becomes visible. Silence-group states (interrupted,
            // recovering) still wait STATUS_SILENCE_MS to avoid churn on
            // sub-threshold network blips that self-resolve.
            if (PixelStream.SILENCE_STATUSES.has(this._status)) {
                const cameFromSilenceGroup = PixelStream.SILENCE_STATUSES.has(this._lastAnnouncedStatus ?? '');
                if (statusChanged && !cameFromSilenceGroup) {
                    // Entered the silence group from outside (e.g. streaming → interrupted).
                    // Suppress the label until the silence window elapses.
                    pendingLabel.textContent = '';
                    this._pendingSilenceTimer = setTimeout(() => {
                        this._renderPendingText(pendingLabel);
                        this._pendingSilenceTimer = null;
                    }, PixelStream.STATUS_SILENCE_MS);
                }
                else if (statusChanged && cameFromSilenceGroup) {
                    // Moved within the silence group (e.g. interrupted → recovering).
                    // The label has already been displayed; keep it stable rather
                    // than blinking it off for another silence window. Re-render
                    // in case the per-status text differs.
                    this._renderPendingText(pendingLabel);
                }
                else {
                    const elapsed = Date.now() - (this._pendingPhaseStartedAt ?? Date.now());
                    if (elapsed >= PixelStream.STATUS_SILENCE_MS) {
                        this._renderPendingText(pendingLabel);
                    }
                }
            }
            else {
                this._renderPendingText(pendingLabel);
            }
        }
        else if (pendingLabel) {
            pendingLabel.textContent = '';
            this._pendingPhaseStartedAt = null;
        }
        this._announceStatus();
    }
    _renderPendingText(label) {
        const primary = this._resolveGuidanceString(this._status, PixelStream._PENDING_PRIMARY[this._status] ?? '');
        if (this._guidanceLevel === 'none') {
            // Silent by default — words are reserved for opt-in consumers.
            label.textContent = '';
            return;
        }
        if (this._guidanceLevel === 'verbose') {
            const hint = this._resolveGuidanceString(`${this._status}-verbose`, PixelStream._PENDING_VERBOSE[this._status] ?? '');
            label.textContent = hint ? `${primary} — ${hint}` : primary;
            return;
        }
        label.textContent = primary;
    }
    _resolveGuidanceString(key, fallback) {
        const override = this._guidanceStrings?.[key];
        return typeof override === 'string' ? override : fallback;
    }
    _failureRingColor(reason) {
        // Amber = user-actionable (policy/mapping/token/rendezvous wait).
        // Red   = terminal media / recovery exhaustion.
        switch (reason) {
            case 'no_deployment':
            case 'token_expired':
            case 'unauthorized':
            case 'rendezvous_timeout':
            case 'queue_wait_timeout':
            case 'webrtc_sdp_timeout':
            case 'webrtc_negotiation_timeout':
            case 'worker_left':
                return 'amber';
            case 'reconnect_exhausted':
            case 'rendezvous_exhausted':
            case 'media_pipeline_failure':
            case 'connection_failed':
            case 'disconnected':
            case 'server_error':
                return 'red';
            default:
                return reason ? 'red' : '';
        }
    }
    /**
     * Announce the current status through the aria-live region once per
     * status transition. Debounced by re-rendering only when `_status`
     * actually changed — no ladder-driven announcements.
     */
    _announceStatus() {
        if (this._lastAnnouncedStatus === this._status)
            return;
        const liveRegion = this.controlsOverlay.querySelector('[data-status-aria-live]');
        if (!liveRegion)
            return;
        const text = this._resolveGuidanceString(`aria-${this._status}`, PixelStream._ARIA_PRIMARY[this._status] ?? '');
        if (text) {
            // Small debounce so rapid status flips don't read multiple times.
            setTimeout(() => {
                if (this._status === this._lastAnnouncedStatus) {
                    liveRegion.textContent = text;
                }
            }, 200);
        }
        this._lastAnnouncedStatus = this._status;
    }
    /** Update the error-state message text. */
    _updateErrorMessage() {
        const titleEl = this.controlsOverlay.querySelector('[data-failure-label]');
        const msgEl = this.controlsOverlay.querySelector('.error-message');
        if (titleEl) {
            const showTitle = this._failureReason !== null || this._status === 'ended';
            titleEl.textContent = showTitle ? this._failureReasonTitle(this._failureReason) : '';
        }
        if (msgEl) {
            msgEl.textContent = this._failureReason ? this._failureReasonLabel(this._failureReason) : '';
        }
    }
    /**
     * Request a marketplace job for this session.
     * @deprecated Use {@link play} instead — it handles all states including autoplay.
     */
    async requestJob() {
        // Collaborators join an existing session and cannot submit jobs
        if (this.session.isCollaborator) {
            this._log.warn('session-collaborator cannot request jobs');
            return;
        }
        // Guard: prevent duplicate in-flight job requests
        if (this._jobState === 'pending') {
            this._log.debug('Job request already in flight — ignoring');
            return;
        }
        // Detect error recovery before clearing state (ended is a clean stop, not an error)
        const isRecovery = this._status === 'failed';
        // Auto-clear error/terminal state so the overlay resets immediately
        if (isRecovery) {
            this._setJobState('unsubmitted');
            this._setSessionState('idle');
        }
        // Happy path: session is connected, send immediately
        if (this.session.ws?.readyState === WebSocket.OPEN) {
            this._log.info('Requesting job...');
            this._setJobState('pending');
            this.performanceMetrics.jobRequested = performance.now();
            this.sendSessionMessage({
                type: 'request-job',
                ...(this._preflightDeploymentCheck && { preflight: true }),
            });
            return;
        }
        // Session is mid-connect — the connect hook will fire requestJob()
        // once the session reaches 'connected' (via _userWantsStream flag).
        if (this._sessionState === 'connecting' || this._sessionState === 'authenticating') {
            this._log.info('Session connecting — job will be requested on connect');
            return;
        }
        // Session is down — need to (re)start it.
        if (this.config.admissionToken) {
            // Error recovery: force sjr for latency optimization
            if (isRecovery) {
                this._log.info('Error recovery — restarting session with swift job request...');
                const wasSwift = this._swiftJobRequest;
                this._swiftJobRequest = true;
                try {
                    await this.session.startSession();
                    return;
                }
                catch (err) {
                    this._log.error('Failed to restart session', err);
                    return; // startSession() already dispatched session-error
                }
                finally {
                    this._swiftJobRequest = wasSwift;
                }
            }
            // Normal start (no-auto-connect, or session hasn't started yet).
            // The connect hook will send request-job when session reaches 'connected'.
            this._log.info('Starting session — job will be requested on connect');
            this.startSession().catch(err => this._log.error('Failed to start session', err));
            return;
        }
        this._log.warn('Cannot request job — no active session and no admission token');
        this.dispatchEvent(new CustomEvent('session-error', {
            detail: { error: 'No active session', phase: 'job-request' }
        }));
    }
    /**
     * Start or resume streaming — the primary user-facing action.
     *
     * Handles all states:
     * - Autoplay blocked → resumes video playback
     * - Session connected, job unsubmitted → sends `request-job`
     * - Session connecting → waits for connect hook
     * - Session idle → starts session, connect hook sends `request-job`
     * - Error/ended → clears error state and restarts
     *
     * Must be triggered by a user gesture for browser autoplay policy.
     */
    async play() {
        // Unlock audio on iOS Safari — must happen within a user gesture.
        // On iOS, the Audio element is blocked until load()/play() is called
        // from a gesture handler. This primes it so that when the WebRTC audio
        // track arrives later, srcObject assignment produces sound.
        this.webrtc.unlockAudio();
        // Autoplay blocked: just resume video
        if (this._autoplayBlocked) {
            try {
                await this.video.play();
                this._autoplayBlocked = false;
                this._setJobState('fulfilled');
            }
            catch (e) {
                this._log.warn('Play still blocked', e);
                throw e;
            }
            return;
        }
        // Set intent and request a job
        this._userWantsStream = true;
        return this.requestJob();
    }
    /**
     * Cancel a pending job request. Tears down any local timers that were
     * armed for this attempt so nothing late-fires against a cancelled
     * session (rendezvous timeout, negotiation escalation).
     */
    cancel() {
        if (!this.session.ws || this.session.ws.readyState !== WebSocket.OPEN) {
            this._log.warn('Cannot cancel job - WebSocket not connected');
            this.dispatchEvent(new CustomEvent('session-error', {
                detail: { error: 'WebSocket not connected', phase: 'job-cancel' }
            }));
            return;
        }
        this._log.info('Cancelling job...');
        // Clear the attribute-driven rendezvous timer so it cannot fire a
        // stale `rendezvous-timeout` event after the user cancelled.
        this._cancelRendezvousTimer();
        // Reset derived-status flags so _recomputeStatus returns to a
        // pre-match state. The next request-job starts fresh.
        this._rendezvoused = false;
        this._negotiatingStarted = false;
        this.sendSessionMessage({ type: 'cancel-job' });
    }
    /**
     * Request a session invite token.
     *
     * Sends a `request-invite` message to the session. The server responds with
     * an `invite-token` event containing a short-lived JWT that can be passed as
     * the `admission-token` to a second `<pixel-stream>` element, allowing it to
     * join the same session as a `session-collaborator`.
     *
     * Only the session owner (browser-agent) may call this. Requires an active
     * WebSocket connection — call after the `session-connected` event.
     */
    requestInvite() {
        this.session.requestInvite();
    }
    /**
     * Stop the session gracefully.
     */
    stop(reason) {
        this._setJobState('unsubmitted');
        this._setStatus('ended');
        this.webrtc.closePeerConnection();
        this.session.leaveSession(reason);
        this.webrtc.resetResilienceState();
    }
    // ── Deprecated aliases ──
    /** @deprecated Use {@link play}. */
    async resumePlayback() { return this.play(); }
    /** @deprecated Use {@link cancel}. */
    cancelJob() { this.cancel(); }
    /** @deprecated Use {@link stop}. */
    leaveSession(reason) { this.stop(reason); }
    // ===== STREAMER SUBSCRIPTION (delegated to SessionManager) =====
    subscribeToStreamer(streamerId) {
        this.session.subscribeToStreamer(streamerId);
    }
    get availableStreamers() {
        return this.session.availableStreamers;
    }
    _evaluateSubscription() {
        this.session.evaluateSubscription();
    }
    /**
     * Update the debug visual indicator based on current session state.
     * Only applies when the 'debug' attribute is set.
     */
    _updateDebugIndicator() {
        // Remove all state classes first
        this.classList.remove('session-connecting', 'session-connected', 'session-error');
        if (!this._debug)
            return;
        // Add appropriate class based on session state
        switch (this._sessionState) {
            case 'connecting':
            case 'authenticating':
            case 'reconnecting':
                this.classList.add('session-connecting');
                break;
            case 'connected':
                this.classList.add('session-connected');
                break;
            case 'error':
                this.classList.add('session-error');
                break;
            // 'idle' - no special indicator (default gray border from :host([debug]))
        }
    }
    /**
     * Update stream state and emit event with optional diagnostic detail.
     */
    _setStreamState(state, detail) {
        if (this._streamState === state)
            return;
        const oldState = this._streamState;
        this._streamState = state;
        // Seed interaction timestamp and record stream start time
        if (state === 'streaming' && oldState !== 'streaming') {
            this._lastUserInteraction = Date.now();
            this._streamStartedAt = Date.now();
            this._userWantsStream = false; // intent fulfilled
            // Microphone is now acquired pre-offer in createPeerConnection when
            // useMic is set. If the mic was denied at that point, _useMicIntent
            // stays true but isMicrophoneEnabled is false — sync the attribute.
            if (this._useMicIntent && !this.webrtc.isMicrophoneEnabled) {
                this._useMicIntent = false;
                this.removeAttribute('use-mic');
            }
        }
        this._log.debug(`Stream state: ${oldState} -> ${state}`);
        this.setAttribute('stream-state', state);
        this.dispatchEvent(new CustomEvent('stream-state-change', {
            detail: { oldState, newState: state, ...detail }
        }));
        this._updateDebugIndicator();
        this._recomputeStatus();
    }
    /**
     * Update session state and emit event.
     */
    _setSessionState(state) {
        const oldState = this._sessionState;
        this._sessionState = state;
        if (oldState !== state) {
            // Reset meta-status tracking flags at the start of a new session.
            // Also clear terminal status so _recomputeStatus can derive again.
            if (state === 'connecting') {
                this._rendezvoused = false;
                this._negotiatingStarted = false;
                this._status = 'idle';
                this._failureReason = null;
                this._streamStartedAt = 0;
            }
            // Track admission state
            if (state === 'connected' && !this._isAdmitted) {
                this._setAdmitted(true);
            }
            else if ((state === 'error' || state === 'idle') && this._isAdmitted) {
                this._setAdmitted(false);
            }
            // Clear intent-to-stream when session ends or errors out
            if (state === 'error' || (state === 'idle' && oldState !== 'idle')) {
                this._userWantsStream = false;
            }
            this._log.debug(`Session state: ${oldState} -> ${state}`);
            this.setAttribute('session-state', state);
            this.dispatchEvent(new CustomEvent('session-state-change', {
                detail: { oldState, newState: state }
            }));
            // Update debug indicator when state changes
            this._updateDebugIndicator();
            this._updateControlsOverlay();
            this._recomputeStatus();
            // Session connect hook: auto-send request-job if the user
            // clicked play before the session was connected.
            if (state === 'connected' && this._userWantsStream && this._jobState === 'unsubmitted') {
                this.requestJob();
            }
        }
    }
    // ===== IDLE TIMER STATE MACHINE =====
    _enterIdleState(next) {
        const old = this._idleState;
        // Allow watching → watching for rescheduling
        if (old === next && next !== 'watching')
            return;
        // Tear down all timers
        if (this._idleScheduleHandle !== null) {
            clearTimeout(this._idleScheduleHandle);
            this._idleScheduleHandle = null;
        }
        if (this._idleWarningHandle !== null) {
            clearTimeout(this._idleWarningHandle);
            this._idleWarningHandle = null;
        }
        if (this._idleTickHandle !== null) {
            clearInterval(this._idleTickHandle);
            this._idleTickHandle = null;
        }
        this._idleState = next;
        if (old !== next) {
            this.setAttribute('idle-state', next);
            this._log.debug(`Idle: ${old} -> ${next}`);
            this.dispatchEvent(new CustomEvent('idle-state-change', {
                detail: { oldState: old, newState: next }
            }));
        }
        switch (next) {
            case 'off':
            case 'watching':
                this._idleCountdownRemaining = 0;
                if (this._idleBarEl)
                    this._idleBarEl.style.width = '100%';
                // Seed interaction timestamp when entering watching from grace/countdown
                // so the full grace period restarts
                if (old !== 'off' && old !== 'watching') {
                    this._lastUserInteraction = Date.now();
                }
                if (next === 'watching') {
                    this._scheduleIdleCheck();
                }
                break;
            case 'grace':
                if (this._idleAudibleWarning)
                    this._playIdleBeep();
                this._idleWarningHandle = setTimeout(() => {
                    this._idleWarningHandle = null;
                    this._enterIdleState('countdown');
                }, this._idleWarningDuration);
                break;
            case 'countdown':
                this._idleCountdownRemaining = this._idleCountdownDuration;
                this._updateIdleCountdownDisplay();
                this._dispatchIdleCountdownTick();
                this._idleTickHandle = setInterval(() => {
                    this._idleCountdownRemaining--;
                    this._updateIdleCountdownDisplay();
                    this._dispatchIdleCountdownTick();
                    if (this._idleCountdownRemaining <= 0) {
                        this._enterIdleState('off');
                        this.stop('Idle timer expiry');
                    }
                }, 1000);
                break;
        }
    }
    _scheduleIdleCheck() {
        const last = this._lastUserInteraction || Date.now();
        const wait = Math.max(0, this._idleWatchDuration - (Date.now() - last));
        this._idleScheduleHandle = setTimeout(() => {
            this._idleScheduleHandle = null;
            const idle = Date.now() - (this._lastUserInteraction || Date.now());
            if (idle >= this._idleWatchDuration) {
                this._enterIdleState('grace');
            }
            else {
                this._enterIdleState('watching'); // reschedule
            }
        }, wait + 500);
    }
    _updateIdleCountdownDisplay() {
        if (this._idleCountdownEl) {
            this._idleCountdownEl.textContent = String(this._idleCountdownRemaining);
        }
        if (this._idleBarEl) {
            this._idleBarEl.style.width = `${(this._idleCountdownRemaining / this._idleCountdownDuration) * 100}%`;
        }
    }
    _dispatchIdleCountdownTick() {
        this.dispatchEvent(new CustomEvent('idle-countdown-tick', {
            detail: { remaining: this._idleCountdownRemaining }
        }));
    }
    _playIdleBeep() {
        try {
            const ctx = new AudioContext();
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();
            osc.connect(gain);
            gain.connect(ctx.destination);
            osc.frequency.value = 880;
            gain.gain.value = 0.15;
            gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.15);
            osc.start();
            osc.stop(ctx.currentTime + 0.15);
            osc.onended = () => ctx.close();
        }
        catch {
            // AudioContext may be blocked before user gesture
        }
    }
    /**
     * Set the derived meta-status and emit a status-change event.
     */
    _setStatus(status, failureReason) {
        if (this._status === status)
            return;
        const oldStatus = this._status;
        this._status = status;
        this._failureReason = failureReason ?? null;
        this._log.debug(`Status: ${oldStatus} -> ${status}${failureReason ? ` (${failureReason})` : ''}`);
        this.setAttribute('status', status);
        if (failureReason) {
            this.setAttribute('failure-reason', failureReason);
        }
        else {
            this.removeAttribute('failure-reason');
        }
        this.dispatchEvent(new CustomEvent('status-change', {
            detail: { oldStatus, newStatus: status, failureReason: this._failureReason }
        }));
        // Structured stream-error event for terminal failures. Lets consumers
        // switch on a stable `code` rather than parsing failure-reason strings.
        // Idempotent: only emits on the transition INTO `failed` (the if-same
        // guard at function entry covers re-emission). Code vocabulary tracked
        // in IL-149 and documented in the streaming guide (IL-148).
        if (status === 'failed' && failureReason) {
            this.dispatchEvent(new CustomEvent('stream-error', {
                detail: {
                    code: failureReason,
                    sessionId: this.session?.sessionId ?? null,
                    agentId: this.session?.agentId ?? null,
                    timestamp: Date.now(),
                },
            }));
        }
        // Mirror client-side status transitions into the SessionDO transcript
        // so operators can reconstruct what the user actually saw. Best-effort
        // — only fires when the session WS is open and skips echoes of
        // pre-session state flips.
        if (this.session?.ws?.readyState === WebSocket.OPEN) {
            try {
                this.session.sendMessage({
                    type: 'report-event',
                    event_type: 'client.status_change',
                    data: {
                        oldStatus,
                        newStatus: status,
                        ...(this._failureReason && { failureReason: this._failureReason }),
                    },
                });
            }
            catch {
                // Swallow — transcript observability is non-critical.
            }
        }
        this._updateControlsOverlay();
        this._updateUnmuteControl();
        // Reset mute/audio session-scoped flags when we return to idle so a
        // subsequent session can show the unmute button again.
        if (status === 'idle') {
            this._unmuteControlDismissed = false;
            this._hasRemoteAudioTrack = false;
        }
        // Start/stop idle timer based on streaming status
        if (status === 'streaming' && this._idleTimer && this._idleState === 'off') {
            this._enterIdleState('watching');
        }
        else if (status !== 'streaming' && this._idleState !== 'off') {
            this._enterIdleState('off');
        }
    }
    /**
     * Recompute the derived meta-status from internal session/job/stream states
     * and signal flags. Called at the end of every state setter.
     *
     * Priority order (highest first):
     *   stream failed → recovering → interrupted →
     *   job ready (autoplay blocked) → streaming →
     *   negotiating → rendezvoused → queued →
     *   connected (no job) → connecting
     *
     * idle, failed, and ended are set explicitly — never derived here.
     */
    _recomputeStatus() {
        // Terminal states are set explicitly and must not be overridden
        // by subsequent state-setter cascades during teardown.
        if (this._status === 'failed' || this._status === 'ended')
            return;
        if (this._streamState === 'failed') {
            this._setStatus('failed', 'media_pipeline_failure');
        }
        else if (this._streamState === 'recovering') {
            this._setStatus('recovering');
        }
        else if (this._streamState === 'interrupted') {
            this._setStatus('interrupted');
        }
        else if (this._jobState === 'ready') {
            // Autoplay-blocked must take priority over streaming — the
            // WebRTC connection can report 'connected' even when the browser
            // blocks video.play().  Without this, the overlay hides and the
            // user sees a black screen with no RESUME button.
            this._setStatus('ready');
        }
        else if (this._streamState === 'streaming') {
            this._setStatus('streaming');
        }
        else if (this._negotiatingStarted) {
            this._setStatus('negotiating');
        }
        else if (this._rendezvoused) {
            this._setStatus('rendezvoused');
        }
        else if (this._jobState === 'fulfilled' && this._sessionState === 'connected') {
            // A worker-agent joined the session (participant-joined) but has
            // not yet announced a contribution. Sits between `queued` and
            // `rendezvoused` — matchmaking is done, the streamer is spinning
            // up.
            this._setStatus('matched');
        }
        else if (this._jobState === 'pending' && this._sessionState === 'connected') {
            this._setStatus('queued');
        }
        else if (this._sessionState === 'connected' && this._jobState === 'unsubmitted') {
            this._setStatus('connected');
        }
        else if (this._sessionState === 'reconnecting') {
            this._setStatus('connecting');
        }
        else if (this._sessionState === 'authenticating') {
            this._setStatus('authenticating');
        }
        else if (this._sessionState === 'connecting') {
            this._setStatus('connecting');
        }
        else if (this._sessionState === 'idle') {
            this._setStatus('idle');
        }
        // failed and ended are set explicitly — not derived
    }
    /**
     * Force-close the session without notifying the server.
     * Prefer {@link stop} for graceful departure.
     */
    terminateSession() {
        this.session.terminateSession();
        this.webrtc.resetResilienceState();
    }
}
/** Package version, injected at build time. */
PixelStream.VERSION = "0.0.101";
// Reconnection defaults
PixelStream.DEFAULT_RECONNECT_INTERVAL = 30000; // 30 seconds
/**
 * Silence window for `interrupted` / `recovering` only — suppresses
 * alarm text during sub-threshold network blips that self-resolve.
 * Forward-progress pre-stream states (`connecting`, `authenticating`,
 * `queued`, `rendezvoused`, `negotiating`) render immediately so the
 * user sees the session advancing.
 */
PixelStream.STATUS_SILENCE_MS = 3000;
PixelStream.SILENCE_STATUSES = new Set(['interrupted', 'recovering']);
// ── Quiet Disc progress messaging ───────────────────────────────
//
// Replaces the previous escalating-text ladder (7 phases × 3-4 stages ×
// ~25 strings) with a single centered disc that pulses silently for the
// first 3s, then reveals one stable message. Unhappy paths bypass the
// silence threshold — failures are immediate and actionable.
//
// Keys used for guidance-strings override (primary + optional verbose):
//   queued                queued-verbose
//   rendezvoused          rendezvoused-verbose
//   negotiating           negotiating-verbose
//   connecting            connecting-verbose
//   authenticating        authenticating-verbose
//   interrupted           interrupted-verbose
//   recovering            recovering-verbose
//   failed-no_deployment  failed-rendezvous_exhausted  failed-<reason>
//   aria-<status>
PixelStream._PENDING_PRIMARY = {
    connecting: 'Connecting',
    authenticating: 'Exchanging credentials',
    queued: 'Finding a session',
    matched: 'Preparing streamer',
    rendezvoused: 'Streamer found',
    negotiating: 'Starting stream',
    // Media-layer vocabulary is intentionally distinct from the WS-level
    // "Connecting/Reconnecting" terms so transitions are unambiguous.
    // WS reconnect maps to status='connecting' via _recomputeStatus.
    interrupted: 'Network interruption detected',
    recovering: 'Recovering',
};
PixelStream._PENDING_VERBOSE = {
    connecting: 'Opening a secure channel',
    authenticating: 'Verifying your admission token',
    queued: 'Waiting for an available streamer',
    matched: 'A streamer claimed your session',
    rendezvoused: 'Your streamer is warming up',
    negotiating: 'Probing the best network route',
    interrupted: 'Checking the network',
    recovering: 'Rebuilding the media stream',
};
PixelStream._ARIA_PRIMARY = {
    connecting: 'Connecting',
    authenticating: 'Authenticating',
    queued: 'Finding a session',
    matched: 'Streamer matched',
    rendezvoused: 'Streamer assigned',
    negotiating: 'Starting stream',
    interrupted: 'Reconnecting',
    recovering: 'Reconnecting',
    streaming: 'Connected',
    failed: 'Session failed',
    ended: 'Session ended',
};
// ===== DNS PREFETCH SETUP =====
/**
 * Auto-inject DNS prefetch links based on meta tags for earliest possible DNS resolution.
 * Runs once when module loads, before component instantiation.
 *
 * Configure via meta tags:
 * <meta name="ps-signaling-server" content="wss://stream.example.com">
 * <meta name="ps-ice-servers" content="stun:stun.cloudflare.com:3478,turn:turn.cloudflare.com:3478">
 *
 * If no meta tags are found, defaults to Google STUN server.
 */
if (typeof document !== 'undefined') {
    const prefetchUrls = [];
    // Read from <meta name="ps-signaling-server" content="wss://...">
    const signalingMeta = document.querySelector('meta[name="ps-signaling-server"]');
    if (signalingMeta) {
        const url = signalingMeta.getAttribute('content');
        if (url)
            prefetchUrls.push(url);
    }
    // Read from <meta name="ps-ice-servers" content="stun:...,turn:...">
    const iceServersMeta = document.querySelector('meta[name="ps-ice-servers"]');
    if (iceServersMeta) {
        const servers = iceServersMeta.getAttribute('content');
        if (servers) {
            prefetchUrls.push(...servers.split(',').map(s => s.trim()).filter(s => s));
        }
    }
    // Default: Always prefetch Cloudflare STUN if nothing specified
    if (prefetchUrls.length === 0) {
        prefetchUrls.push('stun:stun.cloudflare.com:3478');
    }
    // Inject prefetch links
    prefetchUrls.forEach(url => {
        try {
            // Convert STUN/TURN URLs to HTTP(S) for origin extraction
            let httpUrl = url;
            const isStunOrTurn = url.startsWith('stun:') || url.startsWith('turn:') || url.startsWith('turns:');
            if (url.startsWith('stun:')) {
                httpUrl = url.replace('stun:', 'https://');
            }
            else if (url.startsWith('turn:')) {
                httpUrl = url.replace('turn:', 'https://');
            }
            else if (url.startsWith('turns:')) {
                httpUrl = url.replace('turns:', 'https://');
            }
            const urlObj = new URL(httpUrl);
            const origin = urlObj.origin;
            // Skip if already exists
            if (document.querySelector(`link[rel="dns-prefetch"][href="${origin}"]`)) {
                return;
            }
            // DNS prefetch (fastest, just DNS lookup)
            const dnsPrefetch = document.createElement('link');
            dnsPrefetch.rel = 'dns-prefetch';
            dnsPrefetch.href = origin;
            document.head.appendChild(dnsPrefetch);
            // Only preconnect for non-STUN/TURN URLs (e.g. signaling servers).
            // STUN/TURN servers don't serve HTTPS so preconnect will always fail.
            if (!isStunOrTurn) {
                const preconnect = document.createElement('link');
                preconnect.rel = 'preconnect';
                preconnect.href = origin;
                document.head.appendChild(preconnect);
            }
        }
        catch (e) {
            // Silently skip invalid URLs
        }
    });
}
// Expose DevTools namespace for standalone users: interlucent.setLogLevel('debug'), interlucent.versions, etc.
exposeDevTools();
registerVersion('pixel-stream', "0.0.101");
// Register the custom element (skip in non-browser environments like SSR)
if (typeof customElements !== 'undefined') {
    customElements.define('pixel-stream', PixelStream);
}
function PixelStreamResizeObserverForVideo(opts) {
    const { el, onUpdate, settleMs = 200, dprCap = Infinity, even = true, cssJitterPx = 0, } = opts;
    let rafId = null;
    let settleTimeout = null;
    // Last seen (delta filter)
    let lastCssW = -1;
    let lastCssH = -1;
    // Last applied (avoid redundant work)
    let liveAppliedW = -1;
    let liveAppliedH = -1;
    let finalAppliedW = -1;
    let finalAppliedH = -1;
    const approxEq = (a, b, eps) => Math.abs(a - b) <= eps;
    function readContentCssSize() {
        // clientWidth/Height:
        //  - include padding (rare on <video>)
        //  - exclude border (critical if your <video> has border: 4px etc.)
        //  - stable for "perfect fit the element's inside"
        const cssW = el.clientWidth;
        const cssH = el.clientHeight;
        return { cssW, cssH };
    }
    function computeDeviceSize(cssW, cssH) {
        const cap = typeof dprCap === 'function' ? dprCap() : dprCap;
        const dpr = Math.min(window.devicePixelRatio || 1, cap);
        let devW = Math.round(cssW * dpr);
        let devH = Math.round(cssH * dpr);
        if (even) {
            devW = devW & -2;
            devH = devH & -2;
        }
        // avoid zeros after rounding/evening
        devW = Math.max(even ? 2 : 1, devW);
        devH = Math.max(even ? 2 : 1, devH);
        return { dpr, devW, devH };
    }
    function emit(final) {
        const { cssW, cssH } = readContentCssSize();
        if (cssW <= 0 || cssH <= 0)
            return;
        const { dpr, devW, devH } = computeDeviceSize(cssW, cssH);
        onUpdate({ final, cssW, cssH, dpr, devW, devH });
    }
    const ro = new ResizeObserver(() => {
        const { cssW, cssH } = readContentCssSize();
        // 1) Delta filter (with optional jitter tolerance)
        const sameAsLast = (cssJitterPx > 0
            ? approxEq(cssW, lastCssW, cssJitterPx) && approxEq(cssH, lastCssH, cssJitterPx)
            : cssW === lastCssW && cssH === lastCssH);
        if (sameAsLast)
            return;
        lastCssW = cssW;
        lastCssH = cssH;
        // 2) Live update (rAF-coalesced: at most once per frame)
        if (rafId === null) {
            rafId = requestAnimationFrame(() => {
                rafId = null;
                const { cssW: w, cssH: h } = readContentCssSize();
                const sameAsLive = (cssJitterPx > 0
                    ? approxEq(w, liveAppliedW, cssJitterPx) && approxEq(h, liveAppliedH, cssJitterPx)
                    : w === liveAppliedW && h === liveAppliedH);
                if (sameAsLive)
                    return;
                liveAppliedW = w;
                liveAppliedH = h;
                emit(false);
            });
        }
        // 3) Final update
        // With settleMs === 0, fire synchronously from the observer callback
        // so every distinct size lands as a final (matches PureWeb's reference
        // SDK behavior). Any positive value keeps the debounced commit.
        if (settleMs === 0) {
            const sameAsFinal = (cssJitterPx > 0
                ? approxEq(cssW, finalAppliedW, cssJitterPx) && approxEq(cssH, finalAppliedH, cssJitterPx)
                : cssW === finalAppliedW && cssH === finalAppliedH);
            if (!sameAsFinal) {
                finalAppliedW = cssW;
                finalAppliedH = cssH;
                emit(true);
            }
        }
        else {
            if (settleTimeout !== null)
                clearTimeout(settleTimeout);
            settleTimeout = window.setTimeout(() => {
                settleTimeout = null;
                const { cssW: w, cssH: h } = readContentCssSize();
                const sameAsFinal = (cssJitterPx > 0
                    ? approxEq(w, finalAppliedW, cssJitterPx) && approxEq(h, finalAppliedH, cssJitterPx)
                    : w === finalAppliedW && h === finalAppliedH);
                if (sameAsFinal)
                    return;
                finalAppliedW = w;
                finalAppliedH = h;
                emit(true);
            }, settleMs);
        }
    });
    // Observe the <video> element itself
    ro.observe(el);
    // Optional: attach a dispose helper
    ro.dispose = () => {
        ro.disconnect();
        if (rafId !== null)
            cancelAnimationFrame(rafId);
        rafId = null;
        if (settleTimeout !== null)
            clearTimeout(settleTimeout);
        settleTimeout = null;
    };
    return ro;
}

export { PixelStream, PixelStreamResizeObserverForVideo };
//# sourceMappingURL=pixel-stream.esm.js.map
